import { VideoEffectsPanel } from '@/components/editor/VideoEffectsPanel';
import { DEFAULT_VIDEO_EFFECTS, normalizeVideoEffects, type VideoEffects } from '@/lib/video-effects/types';
import { useAutoSaveVideoEffects } from '@/hooks/use-auto-save-video-effects';
import { EffectSectionControls } from '@/components/video-effects/EffectSectionControls';
import { canExportWithEffectSections } from '@/lib/video-effects/canExportWithEffectSections';
import { applyEffectSectionPatch } from '@/lib/video-effects/applyEffectSectionPatch';
import { effectControlUndoCategory, effectNudgeUndoCategory } from '@/lib/video-effects/effectSectionUndoCategory';
import { INITIAL_UNDO_COALESCE_STATE, resolveUndoPush, type UndoCoalesceState } from '@/lib/content/resolveUndoPush';
import { createSectionAtPlayhead, moveSectionWindow } from '@/lib/video-effects/sectionTimeline';
import { MAX_EFFECT_SECTIONS, type EffectId, type EffectSection } from '@/lib/video-effects/sections';
import { FRAME_DURATION } from '@/lib/timeline/exportFrameTiming';
import { isDialogOpen, isKeyOwningTarget } from '@/hooks/use-slide-keyboard-navigation';
import { useFillerPreviewSources } from '@/hooks/use-filler-preview-sources';
import { useSourceSubtitlesEnabled } from '@/hooks/use-source-subtitles-enabled';
import { resolveDefaultSubtitlesEnabled } from '@/lib/subtitles/resolveDefaultSubtitlesEnabled';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  RotateCw,
  Lock,
  Unlock,
  Palette,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Monitor,
  Smartphone,
  CheckSquare,
  Check,
  Plus,
  FileText,
  Crop,
  Loader2,
  Undo2,
  Redo2,
  History,
  Trash2,
  Scissors,
  Wand2,
  Zap,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { useMemo, useState, useRef, useEffect, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useMyDataQuery, useMyFontsQuery, useFillerVideosQuery } from "@/generated/graphql";
import { deriveQuotaPressure } from "@/lib/quota/deriveQuotaPressure";
import type { MyProjectQuery } from "@/generated/graphql";
import {
  CREATE_CLIP,
  ACTIVATE_CLIP,
  GET_MY_DATA,
  GET_MY_BRAND_KITS,
  GET_MY_PROJECT,
  GET_MY_CLIP,
  UPDATE_PROJECT,
  RATE_CLIP,
  UPDATE_CLIP_DATA,
} from "@/queries/queries";
import {
  getFillerWordSet,
  isFillerWordSupported,
  normalizeForFillerMatch,
} from "@/constants/filler-words";
import { effectiveContentLanguage } from "@/lib/projects/effectiveContentLanguage";
import { useVideoFrameExtractor } from "@/hooks/use-video-frame-extractor";
import VideoFrameAtTime from "@/components/VideoFrameAtTime";
import ProjectPreview, { ProjectPreviewRef } from "@/components/ProjectPreview";
import Timeline from "@/components/Timeline";
import { EditorTemplatesCarousel } from "@/components/editor/EditorTemplatesCarousel";
import {
  defaultSubtitleEditorData,
  defaultSubtitleEditorLogoData,
  type SubtitleEditorData,
  type SubtitleEditorLogoData,
} from "@/components/SubtitleEditor";
import { StyleSidePanel } from "@/components/editor/StyleSidePanel";
import { CoverImageControl } from "@/components/cover/CoverImageControl";
import { useEditorFrameCapture } from "@/components/assistant/useEditorFrameCapture";
import { AiStudioLayout } from "@/components/assistant/AiStudioLayout";
import { AiStudioBar } from "@/components/assistant/AiStudioBar";
import { AiStudioTransport } from "@/components/assistant/AiStudioTransport";
import { useAssistantPanel } from "@/contexts/AssistantContext";
import { resolveStudioUndo } from "@/lib/ai-assistant/studioUndo";
import { usePlayheadInit } from "@/hooks/use-playhead-init";
import { useAiAssistantAccess } from "@/hooks/use-ai-assistant-access";
import { aiStudioPathFor } from "@/lib/ai-assistant/aiStudioPaths";
import { useUploadFile } from "@/hooks/use-upload-file";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Navigation, Mousewheel, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHasFeature } from "@/lib/useHasFeature";
import { POST_LAYOUTS } from "@/lib/layouts/registry";
import { CIRCULO_SUBTITLE_SEED, shouldSeedCirculoSubtitle } from "@/lib/layouts/circuloSubtitleSeed";
import { buildPostLayoutSelection } from "@/lib/layouts/buildPostLayoutSelection";
import { resolveExportFormat } from "@/lib/export/resolveExportFormat";
import { buildClipRenderInput } from "@/lib/export/buildClipRenderInput";
import { buildSubtitleLogoRenderConfig } from "@/lib/export/buildSubtitleLogoRenderConfig";
import { useEditorClipSelection } from "@/hooks/use-editor-clip-selection";
import { useAutoSavePostLayout } from "@/hooks/use-auto-save-post-layout";
import { resolveFields } from "@/lib/layouts/resolveFields";
import {
  sanitizeLayoutFieldBinds,
  resolveClipTitleForBind,
  applyLayoutBinds,
  withMatchingClipTitleBinds,
  defaultLayoutBinds,
  clipTitleKeysForKind,
  type LayoutFieldBinds,
} from "@/lib/layouts/layoutFieldBinds";
import { materializeFillerPlaylist } from "@/lib/layouts/fillerPlaylist";
import { fillerCatalogUrls } from "@/lib/layouts/filterFillerVideos";
import type { PostLayoutConfig, BrandKitLayoutSource, PostLayoutFieldValue } from "@/lib/layouts/types";
import {
  buildPersistedPostLayout,
  parsePersistedPostLayout,
  type PersistedPostLayoutBlob,
} from "@/lib/layouts/persistPostLayout";
import { resolveLayoutChannelSource } from "@/lib/layouts/resolveLayoutChannelSource";
import MassExportPreviewModal from "@/components/MassExportPreviewModal";
import { useUserRole } from "@/hooks/use-user-role";
import { getAvailableExportFormats } from "@/lib/video-aspect-ratio";
import { useToast } from "@/hooks/use-toast";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { useDebouncedIntervals } from "@/hooks/use-debounced-intervals";
import { useTranscription } from "@/hooks/use-transcription";
import { useUploadJson } from "@/hooks/use-upload-json";
import { useSceneDetection } from "@/hooks/use-scene-detection";
import { generateSRT } from "@/lib/format";
import { saveAs } from "file-saver";
import { useAutoSaveClipData } from "@/hooks/use-auto-save-clip-data";
import {
  useAutoSaveCropKeyframes,
  type CropCenterKeyframe,
} from "@/hooks/use-auto-save-crop-keyframes";
import ReframeModal from "@/components/ReframeModal";
import { SceneZoomControl } from "@/components/crop/SceneZoomControl";
import { MAX_CLIP_DURATION_SECONDS } from "@/lib/video-limits";
import { buildClipTranscriptionSegments } from "@/lib/preview/buildClipTranscriptionSegments";
import { resolvePersistedClipPreviewForClip } from "@/lib/preview/resolvePersistedClipPreview";
import { groupTranscriptSegments } from "@/lib/content/groupTranscriptSegments";
import { resolveRevertRanges } from "@/lib/content/resolveRevertRanges";
import { useEffectivePostLayout, useLibraryEffectivePostLayout } from "@/hooks/use-effective-post-layout";
import { buildEnforcedInitialConfig, enforcedLockedKeys } from "@/lib/layouts/applyEnforcedLayout";
import { resolveEditorPreviewPostLayout } from "@/lib/layouts/resolveEditorPreviewPostLayout";
import { applyPostLayoutField } from "@/lib/layouts/applyPostLayoutField";
import { useMediaLayers } from "@/hooks/use-media-layers";
import { useEditorAssistantBridge } from "@/components/assistant/useEditorAssistantBridge";
import { useAssistantMediaSearch } from "@/components/assistant/useAssistantMediaSearch";
import { clipTimeToVideoTime } from "@/lib/layers/clipTimeToVideoTime";
import LayerDropzone from "@/components/layers/LayerDropzone";
import LayerCanvasEditor from "@/components/layers/LayerCanvasEditor";
import CompositionFitOverlay from "@/components/layers/CompositionFitOverlay";
import { PreviewChromeEditors } from "@/components/layouts/PreviewChromeEditors";
import { buildLayersRenderInput } from "@/lib/layers/buildLayersRenderInput";
import { canExportWithLayers } from "@/lib/layers/canExportWithLayers";
import LayersProExportDialog from "@/components/layers/LayersProExportDialog";
import {
  sanitizeTransitions,
  isDirectionalKind,
  DEFAULT_TRANSITION_DURATION_SEC,
  clampTransitionDuration,
  type SceneTransition,
  type KeyframeTransition,
  type TransitionKind,
} from "@/lib/transitions/types";
import { reconcileTransitions } from "@/lib/transitions/reconcileTransitions";
import { assignRandomTransitions } from "@/lib/transitions/assignRandomTransitions";
import {
  assignSignalTransitions,
  parseVideoSignals,
  resolveContentTypeProfile,
} from "@/lib/transitions/assignSignalTransitions";
import { canExportWithTransitions } from "@/lib/transitions/canExportWithTransitions";
import { buildSceneLayout } from "@/lib/transitions/buildSceneLayout";
import { sourceTimeToOutputTime, outputFrameToSourceFrame } from "@/lib/transitions/mapTimes";
import { splitNaturalScenes, splitSceneCropKeyframes } from "@/lib/timeline/naturalScenes";
import {
  keyframeBoundaries,
  sanitizeKeyframeTransitions,
  round3,
  KEYFRAME_TRANSITION_EPS,
} from "@/lib/transitions/keyframeCuts";
import { useVideoSignals } from "@/hooks/use-video-signals";
import { useAutoSaveTransitions } from "@/hooks/use-auto-save-transitions";
import { useAutoSaveCoverImage } from "@/hooks/use-auto-save-cover-image";
import { resolveClipCoverImage, type ClipCoverImage } from "@/lib/cover/coverImage";
import { resolveCropFramingAtTime } from "@/lib/cover/resolveCropFramingAtTime";
import { useAutoSaveKeyframeTransitions } from "@/hooks/use-auto-save-keyframe-transitions";
import {
  autoCameraMoveTransitions,
  deriveDetectionCropKeyframes,
  toCropCenterKeyframes,
} from "@/lib/crop/deriveDetectionCropKeyframes";
import ProFeatureDialog from "@/components/ProFeatureDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EditorTopBar from "@/components/editor/EditorTopBar";
import { OriginalVideoDownloadButton } from "@/components/editor/OriginalVideoDownloadButton";
import { resolveOriginalVideoDownloadUrl } from "@/lib/downloads/resolveOriginalVideoDownloadUrl";
import { resolvePreviewVideoUrl, type PreviewQuality } from "@/lib/preview/resolvePreviewVideoUrl";
import { buildSubtitlePreviewConfig } from "@/lib/preview/buildSubtitlePreviewConfig";
import { canApplyBrandKit, resolveRestoredBrandKitId } from "@/lib/brand-kit/brandKitAccess";
import { resolveInitialBrandKitId } from "@/lib/brand-kit/resolveInitialBrandKitId";
import {
  gateForAppliedKit,
  gateForSavedSettings,
  OPEN_KIT_PREFILL_GATE,
  settleSelectedKit,
  type KitPrefillGate,
} from "@/lib/brand-kit/kitPrefillGate";
import { harvestBrandKitLayoutSource } from "@/lib/brand-kit/harvestBrandKitLayoutSource";
import { hydrateBrandKitWorkspace } from "@/lib/brand-kit/hydrateBrandKitWorkspace";
import { applyTemplateToEditor } from "@/lib/brand-kit/applyTemplateToEditor";
import { overlayTemplateOnCurrentLayout } from "@/lib/brand-kit/overlayTemplateOnCurrentLayout";
import { PaletteProvider } from "@/components/palette/PaletteContext";
import { useAutoSaveSubtitleConfig } from "@/hooks/use-auto-save-subtitle-config";
import { useShowWatermark } from "@/lib/useShowWatermark";
import { useWatermarkVisibility } from "@/hooks/use-watermark-visibility";
import { useWatermarkPosition } from "@/hooks/use-watermark-position";
import { WATERMARK_DELAY_SECONDS } from "@/lib/watermark/resolveWatermarkStart";
import SaveBrandKitDialog from "@/components/SaveBrandKitDialog";
import { useRegisterUserMedia } from "@/hooks/use-register-user-media";
import type { MediaLayer, LayerFrame, LayerRect } from "@/lib/layers/types";
import { computeLayerWindow } from "@/lib/layers/computeLayerWindow";
import { videoTimeToClipTime } from "@/lib/layers/videoTimeToClipTime";
import { normalizeLayer } from "@/lib/layers/normalizeLayer";
import { probeMediaMetadata } from "@/lib/layers/probeMediaMetadata";
import { probeImageSize } from "@/lib/layers/probeImageSize";
import { fitRectToAspect } from "@/lib/layers/fitRectToAspect";
import {
  layerTypeConfig,
  makeImageLayer,
  makeVideoLayer,
  makeAudioLayer,
  makeTextLayer,
  makeClipMirrorLayer,
  TEXT_LAYER_DEFAULT_DURATION,
} from "@/lib/layers/layerTypeConfig";
import type { AddableLayerType } from "@/lib/layers/layerTypeConfig";
import { removeInterval } from "@/lib/layers/removeInterval";
import { resolveDeleteTarget } from "@/lib/layers/resolveDeleteTarget";
import { isLayerUiTarget } from "@/lib/layers/isLayerUiTarget";
import { isEditableTarget } from "@/lib/dom/isEditableTarget";
import AddLayerContextMenu from "@/components/layers/AddLayerContextMenu";
import LayerExpansionRow from "@/components/layers/LayerExpansionRow";
import {
  resolveLayoutExportFormats,
  resolveEffectivePreviewAspect,
  shouldShowAutoCropPendingNotice,
} from "@/lib/preview/resolveEffectivePreviewAspect";
import { resolveLandscapeLayoutSwitch } from "@/lib/preview/resolveLandscapeLayoutSwitch";
import { SoundEffectsBrowseDialog } from "@/components/layers/SoundEffectsBrowseDialog";
import { createSoundEffectLayer } from "@/lib/layers/soundEffectLayer";
import type { SoundEffectRow } from "@/queries/soundEffects";
import { StockMediaBrowseDialog } from "@/components/layers/StockMediaBrowseDialog";
import { MyMediaPickerDialog } from "@/components/media/MyMediaPickerDialog";
import { stockItemToLayerInput } from "@/lib/stock-media/stockItemToLayerInput";
import { userMediaToStockItem } from "@/lib/stock-media/userMediaToStockItem";
import type { StockMediaItem } from "@/lib/stock-media/types";
import { clipTimeSegments } from "@/lib/clips/clipTimeSegments";

interface ProjectEditorProps {
  project: MyProjectQuery["myProject"];
  /** `studio`: AI Studio — the preview and the assistant chat only. */
  variant?: "editor" | "studio";
  onExport?: () => void;
  initialClipId?: string;
  /** When provided, export uses renderV2 pipeline instead of createClip/activateClip */
  renderV2?: (variables: { id: string; input: any }) => Promise<any>;
  /** Path to navigate after export (defaults to '/clips') */
  clipsPagePath?: string;
  /** Set when the `project` prop is actually a library_project adapted to the
   * project shape (see LibraryCopilotPage). Routes the campaign-watermark
   * lookup in ProjectPreview to the library variant. */
  isLibrary?: boolean;
  /** Persist the user's head-count choice on the parent project/library. */
  onSpeakerLayoutChange?: (layout: number) => void;
  /** Persist the user's post-frame layout choice on the parent project/library. */
  onPostLayoutChange?: (postLayout: PersistedPostLayoutBlob | null) => void;
}

const ProjectEditor = ({
  project,
  onExport = () => {},
  initialClipId,
  renderV2,
  clipsPagePath = "/clips",
  isLibrary = false,
  onSpeakerLayoutChange,
  onPostLayoutChange,
  variant = "editor",
}: ProjectEditorProps) => {
  const { t, i18n } = useTranslation();
  const { data: transcription } = useTranscription(
    project?.transcription_url,
    project?.transcription,
  );
  const { data: originalTranscription } = useTranscription(
    (project as any)?.original_transcription_url,
  );
  const { data: sceneDetection } = useSceneDetection(
    project?.scene_detection_url,
    project?.scene_detection,
  );
  // Signal-driven transitions (PR A/B): projects processed before the signals
  // stage have a null url, and the hook then no-ops (idle, null data, no
  // request) — the whole Auto feature stays inert for them.
  const {
    data: videoSignals,
    status: videoSignalsStatus,
  } = useVideoSignals(project?.video_signals_url);
  const { uploadJson, isLoading: isUploadingTranscription } = useUploadJson();
  const transcriptionDraftRef = useRef<any | null>(null);
  const originalWordsRef = useRef<any[]>([]);
  const firstTranscriptionRef = useRef<any | null>(null);
  type TimelineInterval = {
    id: number;
    startTime: number;
    endTime: number;
    title?: string;
  };
  type EditorSnapshot = {
    transcription: any | null;
    clipIntervals: TimelineInterval[];
    sceneSequence: boolean;
    editedCropKeyframes: CropCenterKeyframe[] | null;
    sceneTransitions: SceneTransition[];
    keyframeTransitions: KeyframeTransition[];
    coverImage: ClipCoverImage | null;
    videoEffects: VideoEffects;
  };
  const undoStackRef = useRef<EditorSnapshot[]>([]);
  const redoStackRef = useRef<EditorSnapshot[]>([]);
  const clipIntervalsRef = useRef<TimelineInterval[]>([]);
  const sceneSequenceRef = useRef(false);
  const editedCropKeyframesRef = useRef<CropCenterKeyframe[] | null>(null);
  const sceneTransitionsRef = useRef<SceneTransition[]>([]);
  const keyframeTransitionsRef = useRef<KeyframeTransition[]>([]);
  const coverImageRef = useRef<ClipCoverImage | null>(null);
  const videoEffectsRef = useRef<VideoEffects>(DEFAULT_VIDEO_EFFECTS);
  const videoEffectsDraftsRef = useRef(new Map<string, VideoEffects>());
  const videoEffectsLoadedIdRef = useRef<string | null>(null);
  const [, setUndoRedoVersion] = useState(0);
  const transcriptionDraftVersionRef = useRef(0);
  const [transcriptionDraftVersion, setTranscriptionDraftVersion] = useState(0);
  const [isTranscriptionDirty, setIsTranscriptionDirty] = useState(false);
  const isTranscriptionDirtyRef = useRef(false);
  const MAX_TRANSCRIPTION_HISTORY = 10;
  const [editingWordId, setEditingWordId] = useState<string | null>(null);
  const transcriptionMenuInsertionRef = useRef<{ direction: "before" | "after"; editId: string } | null>(null);
  const [hoveredWordId, setHoveredWordId] = useState<string | null>(null);
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(
    null,
  );
  const [selectionEndId, setSelectionEndId] = useState<string | null>(null);
  const [discreteSelectedIds, setDiscreteSelectedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [decoupPopoverOpen, setDecoupPopoverOpen] = useState(false);
  const [decoupSilenceGap, setDecoupSilenceGap] = useState("0.6");
  const [decoupPadding, setDecoupPadding] = useState("0.15");
  const [fillerPopoverOpen, setFillerPopoverOpen] = useState(false);
  const [fillerMatchCount, setFillerMatchCount] = useState(0);
  const selectedWordIds = useMemo(() => {
    const ids = new Set<string>(discreteSelectedIds);
    if (!selectionAnchorId || !selectionEndId) return ids;
    const dw =
      transcriptionDraftRef.current?.results?.channels?.[0]?.alternatives?.[0]
        ?.words || [];
    const anchorIdx = dw.findIndex((w: any) => w._editId === selectionAnchorId);
    const endIdx = dw.findIndex((w: any) => w._editId === selectionEndId);
    if (anchorIdx < 0 || endIdx < 0) return ids;
    const [from, to] =
      anchorIdx <= endIdx ? [anchorIdx, endIdx] : [endIdx, anchorIdx];
    for (let i = from; i <= to; i++) {
      ids.add(dw[i]._editId);
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectionAnchorId,
    selectionEndId,
    discreteSelectedIds,
    transcriptionDraftVersion,
  ]);
  const pendingWordEditRef = useRef<{ editId: string; value: string } | null>(
    null,
  );
  const nextFocusCursorRef = useRef<"start" | "end" | null>(null);
  const autoSaveTimerRef = useRef<number | null>(null);
  const [isSavingTranscription, setIsSavingTranscription] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [subtitlesEnabled, setSubtitlesEnabled] = useSourceSubtitlesEnabled(project);
  const [selectedBrandKit, setSelectedBrandKit] = useState<string | null>(null);
  const [subtitleData, setSubtitleData] = useState<SubtitleEditorData>({
    ...defaultSubtitleEditorData,
  });
  const [logoData, setLogoData] = useState<SubtitleEditorLogoData>({
    ...defaultSubtitleEditorLogoData,
  });
  const [selectedSubtitleFontId, setSelectedSubtitleFontId] = useState("");
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [selectedClips, setSelectedClips] = useState<number[]>([1]);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  // Which clip the CURRENT clipIntervals / editedCropKeyframes belong to.
  // During a clip switch there is a commit where currentSaveableClipId already
  // points at the new clip while this state still holds the old clip's data;
  // the auto-save hooks use this tag to ignore that stale window instead of
  // guessing (the guess used to swallow the first edit after a switch).
  const [clipDataLoadedFor, setClipDataLoadedFor] = useState<string | null>(null);
  // The selected clip whose intervals the interval state holds (unlike
  // clipDataLoadedFor, also set for clips with no saveable id yet).
  const [intervalsLoadedForClip, setIntervalsLoadedForClip] = useState<number | null>(null);
  const [clipIntervals, setClipIntervals] = useState<
    Array<{ id: number; startTime: number; endTime: number; title?: string }>
  >([]);
  const [sceneSequence, setSceneSequence] = useState(false);
  sceneSequenceRef.current = sceneSequence;
  const [editedCropKeyframes, setEditedCropKeyframes] = useState<
    CropCenterKeyframe[] | null
  >(null);
  const [sceneTransitions, setSceneTransitions] = useState<SceneTransition[]>([]);
  // Per-clip cover image (clip.data.coverImage): prepended by the worker as
  // the export's first frame. Loaded with the clip's other data (same
  // clipDataLoadedFor tag), undoable, auto-saved, flushed before export.
  const [coverImage, setCoverImage] = useState<ClipCoverImage | null>(null);
  const [videoEffects, setVideoEffects] = useState<VideoEffects>(DEFAULT_VIDEO_EFFECTS);
  const [effectsPreview, setEffectsPreview] = useState<VideoEffects | null>(null);
  videoEffectsRef.current = videoEffects;
  // RAW persisted keyframe-boundary transitions (working set). Sanitized
  // lazily against effectiveCropKeyframes at use sites (effectiveKeyframeTransitions),
  // so a keyframes-still-loading window never wipes them.
  const [keyframeTransitions, setKeyframeTransitions] = useState<KeyframeTransition[]>([]);
  const [reframeModalOpen, setReframeModalOpen] = useState(false);

  // Pause playback whenever the reframe modal opens — otherwise the
  // background video keeps scrubbing while the user drags the crop center.
  useEffect(() => {
    if (!reframeModalOpen) return;
    videoPlayerRef.current?.pause();
    setIsPlaying(false);
  }, [reframeModalOpen]);
  const [massExportModalOpen, setMassExportModalOpen] = useState(false);
  const [massExportFocusedClipId, setMassExportFocusedClipId] = useState<number | null>(null);
  const [massExportFormat, setMassExportFormat] = useState<"horizontal" | "vertical">("vertical");
  const [checkedClips, setCheckedClips] = useState<number[]>([]);
  const [isMassExporting, setIsMassExporting] = useState(false);
  // The padlock limits paused navigation. Playback always previews the edit.
  const [isVideoLocked, setIsVideoLocked] = useState(false);
  const isPreviewVideoLocked = isVideoLocked || isPlaying;
  const [isTimelineClipView, setIsTimelineClipView] = useState(false);
  const [availableFormats, setAvailableFormats] = useState<
    ("horizontal" | "vertical")[]
  >(["horizontal", "vertical"]);
  const [isExporting, setIsExporting] = useState(false);
  const [previewAspectRatio, setPreviewAspectRatio] = useState<
    "landscape" | "portrait"
  >((project as any).news_pdf_mirror_url ? "landscape" : "portrait");
  const [landscapeSingleConfirmOpen, setLandscapeSingleConfirmOpen] = useState(false);
  // null = no post-frame active. Declared with the other canvas state because
  // layout aspect requirements feed the shared preview/export aspect below.
  // Seeded from the saved layout so the first render already matches it: a
  // null first frame would read as "layout removed" to the autosave below.
  // The restore effect still owns enforced (campaign) layouts and refetches.
  const [postLayout, setPostLayout] = useState<PostLayoutConfig | null>(
    () => parsePersistedPostLayout((project as any)?.post_layout)?.postLayout ?? null,
  );
  const [layoutFieldBinds, setLayoutFieldBinds] = useState<LayoutFieldBinds>(
    () => parsePersistedPostLayout((project as any)?.post_layout)?.fieldBinds ?? {},
  );
  const postLayoutRef = useRef(postLayout);
  postLayoutRef.current = postLayout;
  // Preview-only: compressed (lighter, the historic default) vs the full
  // original. Exports always render from full quality on the worker.
  const [previewQuality, setPreviewQuality] = useState<PreviewQuality>("compressed");
  const [clipRatings, setClipRatings] = useState<Record<string, string>>({});
  const videoPlayerRef = useRef<ProjectPreviewRef>(null);
  // AI Studio: the player frame that goes fullscreen.
  const studioFullscreenRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const lastSeekTimeRef = useRef<number>(0);
  const isSeekingRef = useRef<boolean>(false);
  const wasPlayingBeforeSeekRef = useRef<boolean>(false);
  const transcriptionRef = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const hasProFeatures = useHasFeature("pro_features");
  const { permissions } = useUserRole();
  const { data: brandKitsData } =
    useQuery(GET_MY_BRAND_KITS);
  const { data: fontsData } = useMyFontsQuery();
  const { data: fillerVideosData } = useFillerVideosQuery();
  const fillerPreviewSources = useFillerPreviewSources();
  const { getUploadUrl, createBlobUrl } = useUploadFile();
  const registerUserMedia = useRegisterUserMedia();
  const layerFileInputRef = useRef<HTMLInputElement>(null);
  const layerVideoInputRef = useRef<HTMLInputElement>(null);
  const layerAudioInputRef = useRef<HTMLInputElement>(null);
  // Video adds open stock search. Image adds open My Media first and can
  // continue to stock search or the hidden upload input from there.
  const [stockDialogKind, setStockDialogKind] = useState<"video" | "image" | null>(null);
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [audioPickerClipKey, setAudioPickerClipKey] = useState<string | null>(null);

  const [createClip, { loading: createClipLoading }] = useMutation(CREATE_CLIP);
  const [activateClipMutation, { loading: activateClipLoading }] =
    useMutation(ACTIVATE_CLIP);
  const [updateProject, { loading: updateProjectLoading }] =
    useMutation(UPDATE_PROJECT);
  const [rateClipMutation] = useMutation(RATE_CLIP);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: myData } = useMyDataQuery();

  // Helper to update currentTime state and ref together
  const updateCurrentTime = useCallback((time: number) => {
    currentTimeRef.current = time;
    setCurrentTime(time);
  }, []);

  const cloneTranscriptionWithIds = useCallback((source: any): any => {
    const cloned = structuredClone(source);
    const dw = cloned?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
    dw.forEach((w: any) => {
      if (!w._editId) {
        w._editId = `w-${Math.random().toString(36).slice(2, 10)}`;
      }
    });
    return cloned;
  }, []);

  useEffect(() => {
    if (!transcription) {
      transcriptionDraftRef.current = null;
      originalWordsRef.current = [];
      setTranscriptionDraftVersion((v) => v + 1);
      return;
    }
    originalWordsRef.current = (
      transcription?.results?.channels?.[0]?.alternatives?.[0]?.words || []
    ).map((w: any) => ({ ...w }));
    // Fallback to the first-loaded transcription when the project has no
    // dedicated original_transcription_url (e.g. legacy projects before backfill).
    if (!firstTranscriptionRef.current) {
      firstTranscriptionRef.current = cloneTranscriptionWithIds(transcription);
    }
    if (isTranscriptionDirtyRef.current) return;
    transcriptionDraftRef.current = cloneTranscriptionWithIds(transcription);
    setTranscriptionDraftVersion((v) => v + 1);
  }, [transcription, cloneTranscriptionWithIds]);

  useEffect(() => {
    if (originalTranscription) {
      firstTranscriptionRef.current =
        cloneTranscriptionWithIds(originalTranscription);
    }
  }, [originalTranscription, cloneTranscriptionWithIds]);

  useEffect(() => {
    const el = topBarRef.current;
    if (!el) return;
    const update = () => setTopBarHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const captureSnapshot = useCallback((): EditorSnapshot => {
    return {
      transcription: transcriptionDraftRef.current
        ? structuredClone(transcriptionDraftRef.current)
        : null,
      clipIntervals: structuredClone(clipIntervalsRef.current),
      sceneSequence: sceneSequenceRef.current,
      editedCropKeyframes: editedCropKeyframesRef.current
        ? structuredClone(editedCropKeyframesRef.current)
        : null,
      sceneTransitions: structuredClone(sceneTransitionsRef.current),
      keyframeTransitions: structuredClone(keyframeTransitionsRef.current),
      // Immutable value object — replaced, never mutated — so no clone needed.
      coverImage: coverImageRef.current,
      videoEffects: videoEffectsRef.current,
    };
  }, []);

  // Coalesce rapid pushes within the same category (e.g. dragging) so a single
  // continuous edit only produces one undo entry. The rule lives in
  // resolveUndoPush; `sliding` restarts the window on every folded push, so a
  // held arrow key is one entry however long it is held.
  const undoCoalesceRef = useRef<UndoCoalesceState>(INITIAL_UNDO_COALESCE_STATE);
  const COALESCE_WINDOW_MS = 800;

  const pushUndoSnapshot = useCallback(
    (category?: string, options?: { sliding?: boolean }) => {
      const decision = resolveUndoPush(undoCoalesceRef.current, {
        category,
        now: Date.now(),
        windowMs: COALESCE_WINDOW_MS,
        sliding: options?.sliding,
      });
      undoCoalesceRef.current = decision.next;
      if (!decision.push) return;
      const snapshot = captureSnapshot();
      undoStackRef.current.push(snapshot);
      if (undoStackRef.current.length > MAX_TRANSCRIPTION_HISTORY) {
        undoStackRef.current.shift();
      }
      redoStackRef.current = [];
      setUndoRedoVersion((v) => v + 1);
    },
    [captureSnapshot],
  );

  const mutateTranscriptionDraft = useCallback(
    (mutator: (draft: any) => void) => {
      if (!transcriptionDraftRef.current && transcription) {
        transcriptionDraftRef.current =
          cloneTranscriptionWithIds(transcription);
      }
      if (!transcriptionDraftRef.current) return;
      pushUndoSnapshot();
      mutator(transcriptionDraftRef.current);
      if (!isTranscriptionDirtyRef.current) {
        isTranscriptionDirtyRef.current = true;
        setIsTranscriptionDirty(true);
      }
      transcriptionDraftVersionRef.current += 1;
      setTranscriptionDraftVersion(transcriptionDraftVersionRef.current);
    },
    [transcription, cloneTranscriptionWithIds, pushUndoSnapshot],
  );

  const commitOrRemoveWord = useCallback(
    (editIdToCommit: string, textOverride?: string) => {
      const dw =
        transcriptionDraftRef.current?.results?.channels?.[0]?.alternatives?.[0]
          ?.words || [];
      const index = dw.findIndex((w: any) => w._editId === editIdToCommit);
      if (index < 0) {
        pendingWordEditRef.current = null;
        return;
      }
      const pending = pendingWordEditRef.current;
      let finalText: string;
      if (textOverride !== undefined) {
        finalText = textOverride;
      } else if (pending && pending.editId === editIdToCommit) {
        finalText = pending.value;
      } else {
        finalText = dw[index].word ?? "";
      }
      pendingWordEditRef.current = null;

      const shouldRemove = finalText.trim() === "";
      const currentWord = dw[index].word ?? "";
      const currentPunctuated = dw[index].punctuated_word;
      const hasChange =
        shouldRemove ||
        finalText !== currentWord ||
        (currentPunctuated !== undefined && finalText !== currentPunctuated);
      if (!hasChange) return;

      mutateTranscriptionDraft((draft) => {
        const dw2 =
          draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
        const idx = dw2.findIndex((w: any) => w._editId === editIdToCommit);
        if (idx < 0) return;
        if (shouldRemove) {
          dw2.splice(idx, 1);
        } else {
          dw2[idx].word = finalText;
          if (dw2[idx].punctuated_word !== undefined) {
            dw2[idx].punctuated_word = finalText;
          }
        }
      });
    },
    [mutateTranscriptionDraft],
  );

  const handleStartEditWord = useCallback(
    (editId: string) => {
      const pending = pendingWordEditRef.current;
      if (pending && pending.editId !== editId) {
        commitOrRemoveWord(pending.editId);
      }
      setEditingWordId(editId);
    },
    [commitOrRemoveWord],
  );

  const handleCommitEditWord = useCallback(() => {
    const currentEditId = editingWordId;
    if (currentEditId) {
      commitOrRemoveWord(currentEditId);
    } else {
      pendingWordEditRef.current = null;
    }
    setEditingWordId(null);
  }, [editingWordId, commitOrRemoveWord]);

  const handleCancelEditWord = useCallback(() => {
    pendingWordEditRef.current = null;
    const currentEditId = editingWordId;
    if (currentEditId) {
      // Drop empty words on cancel (e.g. a freshly inserted word the user didn't type into)
      mutateTranscriptionDraft((draft) => {
        const dw =
          draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
        const index = dw.findIndex((w: any) => w._editId === currentEditId);
        if (index >= 0 && ((dw[index].word ?? "") as string).trim() === "") {
          dw.splice(index, 1);
        }
      });
    }
    setEditingWordId(null);
  }, [editingWordId, mutateTranscriptionDraft]);

  const handleNavigateToSibling = useCallback(
    (currentEditId: string, direction: 1 | -1) => {
      const draft = transcriptionDraftRef.current;
      const dw = draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
      const currentIndex = dw.findIndex(
        (w: any) => w._editId === currentEditId,
      );
      const targetWord =
        currentIndex >= 0 ? dw[currentIndex + direction] : undefined;
      const targetEditId: string | undefined = targetWord?._editId;

      commitOrRemoveWord(currentEditId);

      if (targetEditId) {
        nextFocusCursorRef.current = direction > 0 ? "start" : "end";
        setEditingWordId(targetEditId);
      } else {
        setEditingWordId(null);
      }
    },
    [commitOrRemoveWord],
  );

  const commitCurrentlyEditingWord = useCallback(() => {
    if (editingWordId) {
      commitOrRemoveWord(editingWordId);
    } else {
      pendingWordEditRef.current = null;
    }
  }, [editingWordId, commitOrRemoveWord]);

  const computeInsertedTiming = (prevEnd: number, nextStart: number) => {
    const gap = Math.max(0, nextStart - prevEnd);
    let duration: number;
    if (gap <= 0) {
      duration = 0.15;
    } else if (gap > 0.3) {
      duration = Math.min(0.25, gap * 0.4);
    } else {
      duration = Math.max(0.05, gap / 2);
    }
    const mid = (prevEnd + nextStart) / 2;
    const start = Math.max(prevEnd, mid - duration / 2);
    let end = mid + duration / 2;
    if (nextStart > prevEnd) {
      end = Math.min(nextStart, end);
    } else {
      end = start + duration;
    }
    if (end <= start) {
      end = start + 0.05;
    }
    return { start, end };
  };

  const handleInsertWordAfter = useCallback(
    (editId: string) => {
      commitCurrentlyEditingWord();
      const newEditId = `w-${Math.random().toString(36).slice(2, 10)}`;
      mutateTranscriptionDraft((draft) => {
        const dw =
          draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
        const index = dw.findIndex((w: any) => w._editId === editId);
        if (index < 0) return;
        const prev = dw[index];
        const next = dw[index + 1];
        const prevEnd = prev?.end ?? prev?.end_time ?? prev?.endTime ?? 0;
        const nextStart =
          next?.start ?? next?.start_time ?? next?.startTime ?? prevEnd + 0.4;
        const { start, end } = computeInsertedTiming(prevEnd, nextStart);
        const newWord: any = {
          word: "",
          start,
          end,
          confidence: 1,
          _editId: newEditId,
        };
        if (prev?.punctuated_word !== undefined) newWord.punctuated_word = "";
        if (prev?.start_time !== undefined) newWord.start_time = start;
        if (prev?.end_time !== undefined) newWord.end_time = end;
        if (prev?.speaker !== undefined) newWord.speaker = prev.speaker;
        dw.splice(index + 1, 0, newWord);
      });
      setEditingWordId(newEditId);
    },
    [commitCurrentlyEditingWord, mutateTranscriptionDraft],
  );

  const handleInsertWordBefore = useCallback(
    (editId: string) => {
      commitCurrentlyEditingWord();
      const newEditId = `w-${Math.random().toString(36).slice(2, 10)}`;
      mutateTranscriptionDraft((draft) => {
        const dw =
          draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
        const index = dw.findIndex((w: any) => w._editId === editId);
        if (index < 0) return;
        const curr = dw[index];
        const prev = dw[index - 1];
        const prevEnd = prev?.end ?? prev?.end_time ?? prev?.endTime ?? 0;
        const currStart =
          curr?.start ?? curr?.start_time ?? curr?.startTime ?? prevEnd + 0.4;
        const { start, end } = computeInsertedTiming(prevEnd, currStart);
        const newWord: any = {
          word: "",
          start,
          end,
          confidence: 1,
          _editId: newEditId,
        };
        if (curr?.punctuated_word !== undefined) newWord.punctuated_word = "";
        if (curr?.start_time !== undefined) newWord.start_time = start;
        if (curr?.end_time !== undefined) newWord.end_time = end;
        if (curr?.speaker !== undefined) newWord.speaker = curr.speaker;
        dw.splice(index, 0, newWord);
      });
      setEditingWordId(newEditId);
    },
    [commitCurrentlyEditingWord, mutateTranscriptionDraft],
  );

  const handleInsertAtWordSelection = (direction: "before" | "after") => {
    const draftWords: Array<{ _editId: string }> =
      transcriptionDraftRef.current?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
    const selected = draftWords.filter((word) => selectedWordIds.has(word._editId));
    const anchor = direction === "before" ? selected[0] : selected[selected.length - 1];
    if (!anchor) return;
    // Wait until the menu releases its focus scope. Inserting during onSelect
    // makes it pull focus back and immediately blur/remove the empty word.
    transcriptionMenuInsertionRef.current = { direction, editId: anchor._editId };
  };

  // Apply an intervals change AND keep per-cut transitions consistent, without
  // pushing an undo snapshot. Every path that rewrites clipIntervals must go
  // through this (or setClipIntervalsWithUndo) so stale transitions can't
  // dangle after an edit — a wholesale rebuild with fresh ids naturally drops
  // all transitions (no surviving id-pairs), which is the desired semantics.
  const applyIntervalsChange = useCallback((next: TimelineInterval[]) => {
    const remapped = reconcileTransitions(
      clipIntervalsRef.current,
      next,
      sceneTransitionsRef.current,
    );
    setClipIntervals(next);
    if (remapped !== sceneTransitionsRef.current) setSceneTransitions(remapped);
  }, []);

  const setCropKeyframesWithUndo = useCallback(
    (next: CropCenterKeyframe[]) => {
      pushUndoSnapshot("crop");
      setEditedCropKeyframes(next);
    },
    [pushUndoSnapshot],
  );

  const applyDraftSnapshot = useCallback((snapshot: EditorSnapshot) => {
    pendingWordEditRef.current = null;
    setEditingWordId(null);
    setSelectionAnchorId(null);
    setSelectionEndId(null);
    if (snapshot.transcription) {
      transcriptionDraftRef.current = snapshot.transcription;
      isTranscriptionDirtyRef.current = true;
      setIsTranscriptionDirty(true);
      transcriptionDraftVersionRef.current += 1;
      setTranscriptionDraftVersion(transcriptionDraftVersionRef.current);
    }
    setClipIntervals(snapshot.clipIntervals);
    setSceneSequence(snapshot.sceneSequence ?? false);
    setEditedCropKeyframes(snapshot.editedCropKeyframes);
    setSceneTransitions(snapshot.sceneTransitions ?? []);
    setKeyframeTransitions(snapshot.keyframeTransitions ?? []);
    setCoverImage(snapshot.coverImage ?? null);
    const effects = snapshot.videoEffects ?? DEFAULT_VIDEO_EFFECTS;
    if (videoEffectsLoadedIdRef.current) videoEffectsDraftsRef.current.set(videoEffectsLoadedIdRef.current, effects);
    setVideoEffects(effects);
    setEffectsPreview(null);
  }, []);

  // The one commit path for clip effects — the whole-clip panel AND timeline
  // effect sections: an undo entry, the per-clip draft (mass export), and the
  // debounced autosave. Without `undoCategory` every call is its own entry (the
  // panel's behaviour); with one, a run of same-category calls folds into one
  // entry on a sliding window (arrow-key nudges, slider keyboard steps).
  // Declared up here so the effect-section helpers beside selectLayer can use it.
  // Without the undo entry: the assistant's batches have their own undo, so
  // they must not also land in the editor history (Undo would replay them).
  const applyVideoEffects = useCallback((next: VideoEffects) => {
    if (videoEffectsLoadedIdRef.current) videoEffectsDraftsRef.current.set(videoEffectsLoadedIdRef.current, next);
    setVideoEffects(next);
    setEffectsPreview(null);
  }, []);
  const handleVideoEffectsChange = useCallback((next: VideoEffects, undoCategory?: string) => {
    if (undoCategory) pushUndoSnapshot(undoCategory, { sliding: true });
    else pushUndoSnapshot();
    applyVideoEffects(next);
  }, [pushUndoSnapshot, applyVideoEffects]);

  const clipsRef = useRef<any[]>([]);
  const selectedClipsRef = useRef<number[]>([]);

  const handleRevertToOriginal = useCallback(() => {
    if (!firstTranscriptionRef.current) return;
    if (!transcriptionDraftRef.current) return;

    const currentClips = clipsRef.current;
    const currentSelected = selectedClipsRef.current;

    const selectedClipData = currentClips.find(
      (c) => c.id === currentSelected[0],
    );
    const ranges = resolveRevertRanges(selectedClipData);

    const originalWords: any[] =
      firstTranscriptionRef.current?.results?.channels?.[0]?.alternatives?.[0]
        ?.words || [];

    mutateTranscriptionDraft((draft) => {
      const dw: any[] =
        draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];

      if (ranges.length === 0) {
        dw.length = 0;
        for (const w of originalWords) {
          dw.push({
            ...w,
            _editId: `w-${Math.random().toString(36).slice(2, 10)}`,
          });
        }
        return;
      }

      const sorted = [...ranges].sort((a, b) => a.start - b.start);
      const merged: Array<{ start: number; end: number }> = [];
      for (const r of sorted) {
        const last = merged[merged.length - 1];
        if (last && r.start <= last.end) {
          last.end = Math.max(last.end, r.end);
        } else {
          merged.push({ ...r });
        }
      }

      for (let i = merged.length - 1; i >= 0; i--) {
        const { start, end } = merged[i];

        const removeIndices: number[] = [];
        for (let j = 0; j < dw.length; j++) {
          const ws = dw[j].start ?? dw[j].start_time ?? 0;
          const we = dw[j].end ?? dw[j].end_time ?? ws;
          if (ws >= start && we <= end) {
            removeIndices.push(j);
          }
        }

        let insertIndex = removeIndices.length > 0 ? removeIndices[0] : -1;
        if (insertIndex < 0) {
          const fallback = dw.findIndex(
            (w: any) => (w.start ?? w.start_time ?? 0) > start,
          );
          insertIndex = fallback < 0 ? dw.length : fallback;
        }

        for (let j = removeIndices.length - 1; j >= 0; j--) {
          dw.splice(removeIndices[j], 1);
        }

        const toInsert: any[] = [];
        for (const w of originalWords) {
          const ws = w.start ?? w.start_time ?? 0;
          const we = w.end ?? w.end_time ?? ws;
          if (ws >= start && we <= end) {
            toInsert.push({
              ...w,
              _editId: `w-${Math.random().toString(36).slice(2, 10)}`,
            });
          }
        }

        dw.splice(insertIndex, 0, ...toInsert);
      }
    });

    if (ranges.length > 0) {
      setSceneSequence(false);
      const title =
        typeof selectedClipData?.title === "string"
          ? selectedClipData.title
          : "";
      applyIntervalsChange(
        ranges.map((r, idx) => ({
          id: Date.now() + idx,
          startTime: r.start,
          endTime: r.end,
          title,
        })),
      );
    }

    setSelectionAnchorId(null);
    setSelectionEndId(null);
    setEditingWordId(null);
  }, [mutateTranscriptionDraft, applyIntervalsChange]);

  const handleUndoTranscription = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const current = captureSnapshot();
    if (!current) return;
    redoStackRef.current.push(current);
    if (redoStackRef.current.length > MAX_TRANSCRIPTION_HISTORY) {
      redoStackRef.current.shift();
    }
    const previous = undoStackRef.current.pop()!;
    // The next edit must open its own entry, or an edit right after an undo
    // would fold into the undone one and could not be undone.
    undoCoalesceRef.current = INITIAL_UNDO_COALESCE_STATE;
    applyDraftSnapshot(previous);
    setUndoRedoVersion((v) => v + 1);
  }, [applyDraftSnapshot, captureSnapshot]);

  const handleRedoTranscription = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const current = captureSnapshot();
    if (!current) return;
    undoStackRef.current.push(current);
    if (undoStackRef.current.length > MAX_TRANSCRIPTION_HISTORY) {
      undoStackRef.current.shift();
    }
    const next = redoStackRef.current.pop()!;
    undoCoalesceRef.current = INITIAL_UNDO_COALESCE_STATE;
    applyDraftSnapshot(next);
    setUndoRedoVersion((v) => v + 1);
  }, [applyDraftSnapshot, captureSnapshot]);

  const canUndoTranscription = undoStackRef.current.length > 0;
  const canRedoTranscription = redoStackRef.current.length > 0;

  // AI Studio's Undo also undoes the assistant's last edit batch, which the
  // editor history above does not record.
  const { lastEdit: assistantLastEdit, undoLastEdit: undoAssistantEdit } = useAssistantPanel();
  const studioUndo = resolveStudioUndo({
    assistantEditPending: !!assistantLastEdit,
    editorCanUndo: canUndoTranscription,
  });
  const handleStudioUndo = studioUndo.source === "assistant"
    ? undoAssistantEdit
    : handleUndoTranscription;

  const isSavingTranscriptionRef = useRef(false);
  const transcriptionSavePromiseRef = useRef<Promise<void> | null>(null);
  const handleSaveTranscription = useCallback(async () => {
    // Serialize: if a save is already running, wait for it before deciding
    // whether to start another. Required so callers (e.g. export) can
    // `await handleSaveTranscription()` and be sure the latest edits hit S3.
    if (transcriptionSavePromiseRef.current) {
      try { await transcriptionSavePromiseRef.current; } catch { /* already logged */ }
    }

    commitCurrentlyEditingWord();
    if (!transcriptionDraftRef.current || !project?.id) return;
    if (!isTranscriptionDirtyRef.current) return;

    const task = (async () => {
      isSavingTranscriptionRef.current = true;
      setIsSavingTranscription(true);
      const savedVersion = transcriptionDraftVersionRef.current;
      try {
        const payload = structuredClone(transcriptionDraftRef.current);
        const pw =
          payload?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
        pw.forEach((w: any) => {
          delete w._editId;
        });

        const uploadedUrl = await uploadJson(payload);
        if (!uploadedUrl) {
          throw new Error("Upload failed");
        }

        // Only clear dirty if the draft wasn't mutated during the upload
        if (transcriptionDraftVersionRef.current === savedVersion) {
          isTranscriptionDirtyRef.current = false;
          setIsTranscriptionDirty(false);
        }

        await updateProject({
          variables: {
            id: project.id,
            input: { transcription_url: uploadedUrl },
          },
          refetchQueries: [
            { query: GET_MY_PROJECT, variables: { id: project.id } },
          ],
          awaitRefetchQueries: true,
        });

        toast({ title: t("projectEditor.clipSaved") });
      } catch (err) {
        console.error("Error saving transcription:", err);
        isTranscriptionDirtyRef.current = true;
        setIsTranscriptionDirty(true);
        toast({
          title: t("projectEditor.transcriptionSaveError"),
          variant: "destructive",
        });
      } finally {
        isSavingTranscriptionRef.current = false;
        setIsSavingTranscription(false);
      }
    })();

    transcriptionSavePromiseRef.current = task.finally(() => {
      if (transcriptionSavePromiseRef.current === task) {
        transcriptionSavePromiseRef.current = null;
      }
    });

    await task;
  }, [
    project?.id,
    uploadJson,
    updateProject,
    toast,
    t,
    commitCurrentlyEditingWord,
  ]);

  const handleSaveTranscriptionRef = useRef(handleSaveTranscription);
  useEffect(() => {
    handleSaveTranscriptionRef.current = handleSaveTranscription;
  }, [handleSaveTranscription]);

  // Unmount: transcript edits still inside the 1s autosave window flush
  // instead of dying with the timer. handleSaveTranscription is async
  // (S3 upload + mutation) and runs fine after unmount; fire-and-forget.
  useEffect(() => () => {
    if (isTranscriptionDirtyRef.current && !isSavingTranscriptionRef.current) {
      void handleSaveTranscriptionRef.current();
    }
  }, []);

  useEffect(() => {
    if (!isTranscriptionDirty) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveTimerRef.current = null;
      if (editingWordId) return; // defer if user is still actively editing a word
      if (isSavingTranscriptionRef.current) return; // another save already in flight
      handleSaveTranscriptionRef.current();
    }, 1000);
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [transcriptionDraftVersion, isTranscriptionDirty, editingWordId]);

  const clearAllSelections = useCallback(() => {
    setSelectionAnchorId(null);
    setSelectionEndId(null);
    setDiscreteSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const handleClearSelection = clearAllSelections;

  const handleWordShiftClick = useCallback(
    (editId: string) => {
      setDiscreteSelectedIds((prev) =>
        prev.size === 0 ? prev : new Set(),
      );
      if (!selectionAnchorId) {
        setSelectionAnchorId(editId);
        setSelectionEndId(editId);
      } else {
        setSelectionEndId(editId);
      }
    },
    [selectionAnchorId],
  );

  const getSelectedWordRanges = useCallback(
    (ids: Set<string>) => {
      const dw =
        transcriptionDraftRef.current?.results?.channels?.[0]?.alternatives?.[0]
          ?.words || [];
      const ranges: Array<{ start: number; end: number }> = [];
      for (const w of dw) {
        if (ids.has(w._editId)) {
          const s = w.start ?? w.start_time ?? 0;
          const e = w.end ?? w.end_time ?? s;
          if (e > s) ranges.push({ start: s, end: e });
        }
      }
      ranges.sort((a, b) => a.start - b.start);
      const merged: Array<{ start: number; end: number }> = [];
      for (const r of ranges) {
        const last = merged[merged.length - 1];
        if (last && r.start <= last.end) {
          last.end = Math.max(last.end, r.end);
        } else {
          merged.push({ ...r });
        }
      }
      return merged;
    },
    [],
  );

  const subtractRangeFromIntervals = useCallback(
    (
      intervals: TimelineInterval[],
      rangeStart: number,
      rangeEnd: number,
      startId: number,
    ): { next: TimelineInterval[]; nextId: number } => {
      if (rangeEnd <= rangeStart) return { next: intervals, nextId: startId };
      const next: TimelineInterval[] = [];
      let nextId = startId;
      for (const interval of intervals) {
        if (rangeEnd <= interval.startTime || rangeStart >= interval.endTime) {
          next.push(interval);
        } else if (
          rangeStart <= interval.startTime &&
          rangeEnd >= interval.endTime
        ) {
          // fully covered — drop
        } else if (
          rangeStart > interval.startTime &&
          rangeEnd < interval.endTime
        ) {
          next.push({ ...interval, endTime: rangeStart });
          next.push({ ...interval, id: nextId++, startTime: rangeEnd });
        } else if (rangeStart <= interval.startTime) {
          next.push({ ...interval, startTime: rangeEnd });
        } else {
          next.push({ ...interval, endTime: rangeStart });
        }
      }
      return { next, nextId };
    },
    [],
  );

  const handleDeleteSelection = useCallback(() => {
    if (selectedWordIds.size === 0) return;
    const idsToRemove = selectedWordIds;
    mutateTranscriptionDraft((draft) => {
      const dw: any[] =
        draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
      for (let i = dw.length - 1; i >= 0; i--) {
        if (idsToRemove.has(dw[i]._editId)) {
          dw.splice(i, 1);
        }
      }
    });
    clearAllSelections();
  }, [selectedWordIds, mutateTranscriptionDraft, clearAllSelections]);

  const handleDeleteSelectionFromVideo = useCallback(() => {
    if (selectedWordIds.size === 0) return;
    const idsToRemove = selectedWordIds;
    const ranges = getSelectedWordRanges(idsToRemove);

    // Snapshot the words that will survive the deletion so we can drop any
    // interval (or split fragment) that ends up containing none of them.
    const draftWords: any[] =
      transcriptionDraftRef.current?.results?.channels?.[0]?.alternatives?.[0]
        ?.words || [];
    const remainingWordRanges = draftWords
      .filter((w) => !idsToRemove.has(w._editId))
      .map((w) => {
        const s = w.start ?? w.start_time ?? w.startTime ?? 0;
        const e = w.end ?? w.end_time ?? w.endTime ?? s;
        return { start: s, end: e };
      });

    mutateTranscriptionDraft((draft) => {
      const dw: any[] =
        draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
      for (let i = dw.length - 1; i >= 0; i--) {
        if (idsToRemove.has(dw[i]._editId)) {
          dw.splice(i, 1);
        }
      }
    });

    if (ranges.length > 0) {
      // clipIntervalsRef is kept in sync with the clipIntervals state, so we
      // can compute the next intervals outside a functional updater and route
      // them through applyIntervalsChange (which also reconciles transitions).
      let current = clipIntervalsRef.current;
      let nextId =
        current.reduce((max, i) => Math.max(max, i.id), 0) + 1;
      for (const r of ranges) {
        const result = subtractRangeFromIntervals(
          current,
          r.start,
          r.end,
          nextId,
        );
        current = result.next;
        nextId = result.nextId;
      }
      const next = current.filter((interval) =>
        remainingWordRanges.some(
          (w) => w.start <= interval.endTime && w.end >= interval.startTime,
        ),
      );
      applyIntervalsChange(next);
    }

    clearAllSelections();
  }, [
    selectedWordIds,
    getSelectedWordRanges,
    subtractRangeFromIntervals,
    mutateTranscriptionDraft,
    clearAllSelections,
    applyIntervalsChange,
  ]);

  const isMouseSelectingRef = useRef(false);
  const mouseSelectAnchorRef = useRef<string | null>(null);
  const wasDraggingRef = useRef(false);

  const handleWordMouseDown = useCallback(
    (event: React.MouseEvent, editId: string) => {
      if (event.button !== 0 || event.shiftKey) return;
      isMouseSelectingRef.current = true;
      mouseSelectAnchorRef.current = editId;
    },
    [],
  );

  const handleWordMouseEnter = useCallback((editId: string) => {
    setHoveredWordId(editId);
    if (isMouseSelectingRef.current) {
      const anchor = mouseSelectAnchorRef.current;
      if (anchor && anchor !== editId) {
        wasDraggingRef.current = true;
        setDiscreteSelectedIds((prev) =>
          prev.size === 0 ? prev : new Set(),
        );
        setSelectionAnchorId(anchor);
        setSelectionEndId(editId);
      }
    }
  }, []);

  useEffect(() => {
    const onMouseUp = () => {
      isMouseSelectingRef.current = false;
      mouseSelectAnchorRef.current = null;
    };
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, []);

  const handleWordContextMenu = useCallback(
    (editId: string) => {
      if (discreteSelectedIds.has(editId)) return;
      if (selectionAnchorId && selectionEndId) {
        const dw =
          transcriptionDraftRef.current?.results?.channels?.[0]
            ?.alternatives?.[0]?.words || [];
        const anchorIdx = dw.findIndex(
          (w: any) => w._editId === selectionAnchorId,
        );
        const endIdx = dw.findIndex((w: any) => w._editId === selectionEndId);
        const clickedIdx = dw.findIndex((w: any) => w._editId === editId);
        const [from, to] =
          anchorIdx <= endIdx ? [anchorIdx, endIdx] : [endIdx, anchorIdx];
        if (clickedIdx >= from && clickedIdx <= to) return;
      }
      setDiscreteSelectedIds((prev) =>
        prev.size === 0 ? prev : new Set(),
      );
      setSelectionAnchorId(editId);
      setSelectionEndId(editId);
    },
    [selectionAnchorId, selectionEndId, discreteSelectedIds],
  );

  const handleRevertSegment = useCallback(
    (segmentWords: any[]) => {
      if (!segmentWords || segmentWords.length === 0) return;
      const starts = segmentWords.map((w: any) => w.start ?? w.start_time ?? 0);
      const ends = segmentWords.map(
        (w: any) => w.end ?? w.end_time ?? w.start ?? 0,
      );
      const segmentStart = Math.min(...starts);
      const segmentEnd = Math.max(...ends);

      mutateTranscriptionDraft((draft) => {
        const dw =
          draft?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
        const removeIndices: number[] = [];
        for (let i = 0; i < dw.length; i++) {
          const w = dw[i];
          const ws = w.start ?? w.start_time ?? 0;
          const we = w.end ?? w.end_time ?? ws;
          if (ws >= segmentStart && we <= segmentEnd) {
            removeIndices.push(i);
          }
        }

        let insertIndex = removeIndices.length > 0 ? removeIndices[0] : -1;
        if (insertIndex < 0) {
          const fallback = dw.findIndex(
            (w: any) => (w.start ?? w.start_time ?? 0) > segmentStart,
          );
          insertIndex = fallback < 0 ? dw.length : fallback;
        }

        for (let i = removeIndices.length - 1; i >= 0; i--) {
          dw.splice(removeIndices[i], 1);
        }

        const originalWords = originalWordsRef.current || [];
        const toInsert: any[] = [];
        for (const w of originalWords) {
          const ws = w.start ?? w.start_time ?? 0;
          const we = w.end ?? w.end_time ?? ws;
          if (ws >= segmentStart && we <= segmentEnd) {
            const clone = {
              ...w,
              _editId: `w-${Math.random().toString(36).slice(2, 10)}`,
            };
            toInsert.push(clone);
          }
        }

        dw.splice(insertIndex, 0, ...toInsert);
      });
      setSelectionAnchorId(null);
      setSelectionEndId(null);
      setEditingWordId(null);
    },
    [mutateTranscriptionDraft],
  );

  // After a failed translation the transcript is in the audio language.
  const contentLanguage = effectiveContentLanguage(project);

  const fillerWordSet = useMemo(
    () => getFillerWordSet(contentLanguage),
    [contentLanguage],
  );

  const fillerDetectionSupported = useMemo(
    () => isFillerWordSupported(contentLanguage),
    [contentLanguage],
  );

  const handleDetectFillerWords = useCallback((): number => {
    if (fillerWordSet.size === 0) {
      setFillerMatchCount(0);
      return 0;
    }
    const selectedClipData = clipsRef.current.find(
      (c) => c.id === selectedClipsRef.current[0],
    );
    const clipStart =
      typeof selectedClipData?.clip_start_time === "number"
        ? selectedClipData.clip_start_time
        : null;
    const clipEnd =
      typeof selectedClipData?.clip_end_time === "number"
        ? selectedClipData.clip_end_time
        : null;

    const dw =
      transcriptionDraftRef.current?.results?.channels?.[0]?.alternatives?.[0]
        ?.words || [];
    const matched = new Set<string>();
    for (const w of dw) {
      const ws = w.start ?? w.start_time ?? 0;
      const we = w.end ?? w.end_time ?? ws;
      if (clipStart !== null && we < clipStart) continue;
      if (clipEnd !== null && ws > clipEnd) continue;
      const normalized = normalizeForFillerMatch(w.word ?? "");
      if (!normalized) continue;
      if (fillerWordSet.has(normalized) && w._editId) {
        matched.add(w._editId);
      }
    }
    setSelectionAnchorId(null);
    setSelectionEndId(null);
    setDiscreteSelectedIds(matched);
    setFillerMatchCount(matched.size);
    return matched.size;
  }, [fillerWordSet]);

  const handleDecoupClip = useCallback(
    (silenceGapSeconds: number, paddingSeconds: number) => {
      if (!transcriptionDraftRef.current) return;
      const selectedClipData = clipsRef.current.find(
        (c) => c.id === selectedClipsRef.current[0],
      );
      const clipStart =
        typeof selectedClipData?.clip_start_time === "number"
          ? selectedClipData.clip_start_time
          : null;
      const clipEnd =
        typeof selectedClipData?.clip_end_time === "number"
          ? selectedClipData.clip_end_time
          : null;
      if (clipStart === null || clipEnd === null) return;

      const dw =
        transcriptionDraftRef.current?.results?.channels?.[0]?.alternatives?.[0]
          ?.words || [];

      const padded = groupTranscriptSegments({
        words: dw,
        clipStart,
        clipEnd,
        silenceGap: silenceGapSeconds,
        padding: paddingSeconds,
      });

      if (padded.length === 0) return;

      pushUndoSnapshot();

      const title =
        typeof selectedClipData?.title === "string"
          ? selectedClipData.title
          : "";
      applyIntervalsChange(
        padded.map((p, idx) => ({
          id: Date.now() + idx,
          startTime: p.start,
          endTime: p.end,
          title,
        })),
      );

      clearAllSelections();
      setEditingWordId(null);
    },
    [pushUndoSnapshot, clearAllSelections, applyIntervalsChange],
  );

  // Single source of truth for "can we actually render portrait right now."
  // Declared before detection-derived crop keyframes because facecam-safe
  // framing is only required for portrait output.
  const requiredPostLayoutAspect = postLayout
    ? POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS]?.requiredAspect
    : undefined;
  const effectivePreviewAspect = useMemo(
    () => resolveEffectivePreviewAspect({
      previewAspectRatio,
      availableFormats,
      requiredAspect: requiredPostLayoutAspect,
    }),
    [availableFormats, previewAspectRatio, requiredPostLayoutAspect],
  );
  const layoutExportFormats = useMemo(
    () => resolveLayoutExportFormats({
      availableFormats,
      requiredAspect: requiredPostLayoutAspect,
    }),
    [availableFormats, requiredPostLayoutAspect],
  );

  useEffect(() => {
    if (requiredPostLayoutAspect && previewAspectRatio !== requiredPostLayoutAspect) {
      setPreviewAspectRatio(requiredPostLayoutAspect);
    }
  }, [previewAspectRatio, requiredPostLayoutAspect]);

  // Extract scenes from scene detection data
  const detectionScenes = useMemo(() => {
    if (!sceneDetection) return [];
    const data = Array.isArray(sceneDetection)
      ? sceneDetection
      : [sceneDetection];
    return data.flatMap((d: any) => d.scenes || []);
  }, [sceneDetection]);

  // Auto-framing from detection: the same rule the export renders an
  // unedited clip with (shared test vectors with the backend), zoom included.
  // When a per-scene facecam PiP is active, the helper also keeps the base
  // viewport outside the original inset.
  const detectionCropKeyframes = useMemo(
    () => deriveDetectionCropKeyframes({
      detection: sceneDetection,
      isPortrait: effectivePreviewAspect === "portrait",
    }),
    [sceneDetection, effectivePreviewAspect],
  );
  const cropCenterKeyframes = useMemo(
    () => toCropCenterKeyframes(detectionCropKeyframes),
    [detectionCropKeyframes],
  );

  // Unique speakers (by face_id) for the multi-head selector. Mirrors
  // LibraryClips.tsx:468-495. Sorted by total screen time descending.
  const uniqueSpeakers = useMemo(() => {
    if (!detectionScenes.length) return [];
    const faceMap = new Map<
      string,
      {
        face_id: string;
        totalTime: number;
        scenes: Array<{ start: number; end: number; x: number; y: number; width: number; height: number }>;
      }
    >();
    for (const scene of detectionScenes) {
      if (typeof scene.start_time !== "number" || typeof scene.end_time !== "number") continue;
      for (const face of scene.faces || []) {
        if (!face.face_id) continue;
        if (!faceMap.has(face.face_id)) {
          faceMap.set(face.face_id, { face_id: face.face_id, totalTime: 0, scenes: [] });
        }
        const entry = faceMap.get(face.face_id)!;
        entry.totalTime += scene.end_time - scene.start_time;
        entry.scenes.push({
          start: scene.start_time,
          end: scene.end_time,
          x: face.x,
          y: face.y,
          width: face.width,
          height: face.height,
        });
      }
    }
    return Array.from(faceMap.values()).sort((a, b) => b.totalTime - a.totalTime);
  }, [detectionScenes]);

  // Scene timeline (which face_ids are present in each scene). Mirrors
  // LibraryClips.tsx:500-511.
  const sceneTimeline = useMemo(() => {
    return detectionScenes
      .filter((s: any) => typeof s.start_time === "number" && typeof s.end_time === "number")
      .map((s: any) => ({
        start: s.start_time,
        end: s.end_time,
        faceIds: ((s.faces || []) as any[]).map((f: any) => f.face_id).filter(Boolean) as string[],
      }));
  }, [detectionScenes]);

  // 1 = single head (default). User can choose 2/3/4 when enough faces are detected.
  // Initial value comes from the parent project/library. Synced via effect for
  // when the prop arrives asynchronously (initial render may have project={}).
  const [selectedSpeakerLayout, setSelectedSpeakerLayout] = useState<number>(
    (project as any)?.speaker_layout ?? 1,
  );

  useEffect(() => {
    const persisted = (project as any)?.speaker_layout;
    if (typeof persisted === "number" && persisted !== selectedSpeakerLayout) {
      setSelectedSpeakerLayout(persisted);
    }
    // intentionally not depending on selectedSpeakerLayout — we only want to
    // re-sync when the persisted value changes, not when the user picks locally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(project as any)?.speaker_layout]);

  // Debounce persistence so rapid clicks don't fire multiple mutations.
  const speakerLayoutDebounceRef = useRef<number | null>(null);
  const selectedSpeakerLayoutRef = useRef(selectedSpeakerLayout);
  selectedSpeakerLayoutRef.current = selectedSpeakerLayout;
  const onSpeakerLayoutChangeRef = useRef(onSpeakerLayoutChange);
  onSpeakerLayoutChangeRef.current = onSpeakerLayoutChange;
  const persistSpeakerLayout = useCallback(
    (next: number) => {
      setSelectedSpeakerLayout(next);
      if (!onSpeakerLayoutChange) return;
      if (speakerLayoutDebounceRef.current != null) {
        window.clearTimeout(speakerLayoutDebounceRef.current);
      }
      speakerLayoutDebounceRef.current = window.setTimeout(() => {
        onSpeakerLayoutChange(next);
        speakerLayoutDebounceRef.current = null;
      }, 400);
    },
    [onSpeakerLayoutChange],
  );

  const applyLandscapePreview = useCallback(() => {
    setPreviewAspectRatio("landscape");
    setMassExportFormat("horizontal");
  }, []);

  const switchToSingleLayout = useCallback(() => {
    setPostLayout(null);
    setLayoutFieldBinds({});
    persistSpeakerLayout(1);
  }, [persistSpeakerLayout]);

  const requestLandscapePreview = useCallback(() => {
    if (resolveLandscapeLayoutSwitch({ requiredAspect: requiredPostLayoutAspect }).kind === "confirm-single") {
      setLandscapeSingleConfirmOpen(true);
      return;
    }
    applyLandscapePreview();
  }, [applyLandscapePreview, requiredPostLayoutAspect]);

  const confirmLandscapeSingle = useCallback(() => {
    switchToSingleLayout();
    applyLandscapePreview();
    setLandscapeSingleConfirmOpen(false);
  }, [applyLandscapePreview, switchToSingleLayout]);

  // Unmount: flush a pending speaker-layout write instead of dropping it.
  useEffect(() => {
    return () => {
      if (speakerLayoutDebounceRef.current != null) {
        window.clearTimeout(speakerLayoutDebounceRef.current);
        speakerLayoutDebounceRef.current = null;
        onSpeakerLayoutChangeRef.current?.(selectedSpeakerLayoutRef.current);
      }
    };
  }, []);

  // Build the speakerLayout config the preview consumes. Mirrors
  // LibraryClips.tsx:514-529.
  const previewSpeakerLayout = useMemo(() => {
    if (selectedSpeakerLayout <= 1 || uniqueSpeakers.length <= 1) return undefined;
    const numSpeakers = Math.min(selectedSpeakerLayout, uniqueSpeakers.length);
    const speakers = uniqueSpeakers.slice(0, numSpeakers).map((entry) => ({
      face_id: entry.face_id,
      scenes: entry.scenes.map((s) => ({
        start: s.start,
        end: s.end,
        cropCenter: {
          x: (s.x + s.width / 2) * 100,
          y: (s.y + s.height / 2) * 100,
        },
      })),
    }));
    return { numSpeakers, speakers, sceneTimeline };
  }, [selectedSpeakerLayout, uniqueSpeakers, sceneTimeline]);

  // Quota check: block when monthly clips usage reached plan max
  const subscriptionData: any = myData?.myData?.subscription;
  const plan: any = subscriptionData?.plan;
  const planMaxClips: number | undefined =
    typeof plan?.max_clips === "number" ? plan.max_clips : undefined;
  const currentMonthClips: number = subscriptionData?.current_month_clips || 0;
  const hasClipQuota: boolean =
    planMaxClips === undefined ? true : currentMonthClips < planMaxClips;
  // Warn before the quota is gone, not only once it is (see deriveQuotaPressure).
  const quotaPressure = useMemo(
    () => deriveQuotaPressure(subscriptionData, myData?.myData?.onboarding_objective),
    [subscriptionData, myData?.myData?.onboarding_objective],
  );

  const {
    dialog: quotaUpsellDialog,
    openQuotaUpsell,
  } = useQuotaUpsellDialog();
  const showUpgradeToast = useCallback(() => {
    openQuotaUpsell('clip_exports');
  }, [openQuotaUpsell]);

  // CapCut-style: layers are free to add + preview, but exporting a clip
  // that contains any layer requires PRO. The gate is a DIALOG at click time,
  // not a disabled button — the moment of intent is when the explanation and
  // the upgrade CTA actually land.
  const [layersProDialogOpen, setLayersProDialogOpen] = useState(false);
  const showLayersProRequiredDialog = useCallback(() => {
    setLayersProDialogOpen(true);
  }, []);

  // Same click-time dialog pattern, for the scene-transitions PRO export gate.
  const [transitionsProDialogOpen, setTransitionsProDialogOpen] = useState(false);
  const [effectsProDialogOpen, setEffectsProDialogOpen] = useState(false);

  // Bulk export (select-all + export-N-at-once) is PRO-only; free users
  // export one clip at a time. Same click-time dialog pattern as layers.
  const [bulkProDialogOpen, setBulkProDialogOpen] = useState(false);

  // Initialize clip ratings from clips data
  useEffect(() => {
    const projectClips = (project as any).clips || [];
    const initialRatings: Record<string, string> = {};
    for (const clip of projectClips) {
      if (clip.id && clip.curation_rating) {
        initialRatings[clip.id] = clip.curation_rating;
      }
    }
    setClipRatings(initialRatings);
  }, [(project as any)?.clips]);

  // Handle per-clip rating
  const handleClipRating = useCallback(
    async (clipId: string, rating: "like" | "dislike") => {
      try {
        const currentRating = clipRatings[clipId];
        const newRating = currentRating === rating ? "neutral" : rating;

        await rateClipMutation({
          variables: { id: clipId, rating: newRating },
        });

        setClipRatings((prev) => ({ ...prev, [clipId]: newRating }));

        toast({
          title:
            newRating === "neutral"
              ? t("projectEditor.clipRatingRemoved")
              : t("projectEditor.clipRated"),
          variant: "default",
        });
      } catch (error) {
        console.error("Error rating clip:", error);
        toast({
          title: t("projectEditor.clipRatingError"),
          variant: "destructive",
        });
      }
    },
    [clipRatings, rateClipMutation, toast, t],
  );

  // Font loading helpers
  const loadFont = useCallback(
    (fontId: string) => {
      if (!fontsData?.myFonts || loadedFonts.has(fontId)) return;
      const font = fontsData.myFonts.find((f) => f.id === fontId);
      if (!font || !font.file_url) return;
      let styleElement = document.getElementById(
        `font-loader-${fontId}`,
      ) as HTMLStyleElement;
      if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = `font-loader-${fontId}`;
        styleElement.textContent = `@font-face { font-family: '${fontId}'; src: url('${font.file_url}') format('truetype'); font-display: swap; }`;
        document.head.appendChild(styleElement);
      }
      setLoadedFonts((prev) => new Set([...prev, fontId]));
    },
    [fontsData?.myFonts, loadedFonts],
  );

  const getFontFamilyName = useCallback(
    (fontId: string) => {
      if (!fontId || !loadedFonts.has(fontId)) return null;
      return fontId;
    },
    [loadedFonts],
  );

  // Hydrate editor state from the selected brand kit
  const loadBrandKitIntoEditor = useCallback(
    (kit: any) => {
      if (!kit?.data) return;
      const workspace = hydrateBrandKitWorkspace(kit.data);
      setSubtitleData(workspace.subtitle);
      setLogoData(workspace.logo);
      if (workspace.fontId) {
        setSelectedSubtitleFontId(workspace.fontId);
        loadFont(workspace.fontId);
      }
    },
    [loadFont],
  );

  // Restore the project's own subtitle/logo config. Runs before the kit
  // prefill below and keeps it from overwriting the restore until the user
  // applies a kit: the user's saved tweaks must win over the brand kit's
  // defaults on load, or every reload would reset the sliders to the kit.
  // The gate also guards by kit id: loadFont's identity churns when fonts
  // load, which re-runs the prefill effect.
  const kitPrefillGateRef = useRef<KitPrefillGate>(OPEN_KIT_PREFILL_GATE);
  const [saveBrandKitOpen, setSaveBrandKitOpen] = useState(false);
  const [kitApplyProDialogOpen, setKitApplyProDialogOpen] = useState(false);
  // A pro user editing options away from the applied kit gets a save
  // suggestion. Cheap heuristic: any subtitle edit after a kit prefill marks
  // drift; cleared when a kit is (re)applied.
  const [subtitleDriftsFromKit, setSubtitleDriftsFromKit] = useState(false);
  const restoredBrandKitAccessKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const saved = (project as any)?.subtitle_config;
    if (!saved) return;
    // Also guards a kit picked automatically: when the project has NO
    // persisted kit, the "force brand kit selection" effect auto-picks one,
    // and its prefill would stomp the config we just restored.
    kitPrefillGateRef.current = gateForSavedSettings({
      hasSavedSettings: true,
      savedKitId: (project as any)?.brand_kit?.id,
    });
    if (saved.subtitle) {
      setSubtitleData({ ...defaultSubtitleEditorData, ...saved.subtitle });
    }
    if (saved.logo) {
      setLogoData({ ...defaultSubtitleEditorLogoData, ...saved.logo });
    }
    if (saved.font_id) {
      setSelectedSubtitleFontId(saved.font_id);
      loadFont(saved.font_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(project as any)?.subtitle_config]);

  // Restore only kits the current plan may apply. This runs after subtitle
  // restoration, so the kit it restores (or rejects) never overwrites the
  // project's saved manual subtitle configuration.
  useEffect(() => {
    const projectBrandKitId = project?.brand_kit?.id ?? null;
    const accessKey = `${project?.id ?? ""}:${projectBrandKitId ?? ""}:${hasProFeatures}`;
    if (restoredBrandKitAccessKeyRef.current === accessKey) return;

    if (!projectBrandKitId) {
      restoredBrandKitAccessKeyRef.current = accessKey;
      setSelectedBrandKit(null);
      return;
    }
    const kits = brandKitsData?.myBrandKits;
    if (!kits) return;

    const restoredId = resolveRestoredBrandKitId({
      hasProFeatures,
      brandKitId: projectBrandKitId,
      kits,
    });
    restoredBrandKitAccessKeyRef.current = accessKey;
    // A rejected kit leaves the prefill gate alone: the plan often reads as
    // free until myData arrives, and the restored config must still win once
    // the kit is restored. An explicit pick (handleBrandKitChange) releases it.
    setSelectedBrandKit(restoredId);
    setSubtitlesEnabled(resolveDefaultSubtitlesEnabled(project));
  }, [project?.id, project?.brand_kit?.id, brandKitsData?.myBrandKits, hasProFeatures]);

  // Load brand kit into editor whenever the selected kit changes
  useEffect(() => {
    if (!selectedBrandKit || !brandKitsData?.myBrandKits) return;
    const kit = brandKitsData.myBrandKits.find(
      (k: any) => k.id === selectedBrandKit,
    );
    if (!kit) return;
    // Prefill only when the kit actually CHANGES. The effect re-runs on
    // loadBrandKitIntoEditor identity churn (fonts loading), and the restore
    // effect marks the project's own kit as already prefilled. After a config
    // restore the restored values win; the kit only registers as loaded.
    const settled = settleSelectedKit(kitPrefillGateRef.current, kit.id);
    kitPrefillGateRef.current = settled.gate;
    if (!settled.prefill) return;
    loadBrandKitIntoEditor(kit);
    const applied = applyTemplateToEditor(postLayoutRef.current, kit.data);
    setPostLayout(applied.postLayout);
    setLayoutFieldBinds(applied.fieldBinds);
    setSelectedSpeakerLayout(1);
    setSubtitleDriftsFromKit(false);
  }, [selectedBrandKit, brandKitsData, loadBrandKitIntoEditor]);

  // Persist every subtitle/logo tweak to project.subtitle_config (debounced,
  // flushed on unmount — see Editor Persistence in CLAUDE.md).
  const subtitleConfigBlob = useMemo(
    () => ({
      subtitle: subtitleData,
      logo: logoData,
      font_id: selectedSubtitleFontId || null,
    }),
    [subtitleData, logoData, selectedSubtitleFontId],
  );
  useAutoSaveSubtitleConfig({
    projectId: project?.id ?? null,
    config: subtitleConfigBlob,
    loadedConfig: (project as any)?.subtitle_config ?? null,
    enabled: !!project?.id,
  });

  // Free-plan watermark: shown in the preview (so the user can place it) and
  // persisted per project/library. Campaign watermarks are not draggable.
  const showWatermark = useShowWatermark();
  const { showDefaultWatermark } = useWatermarkVisibility({
    showWatermark,
    projectDocumentId: isLibrary ? undefined : project?.id,
    libraryDocumentId: isLibrary ? project?.id : undefined,
  });
  const {
    position: watermarkPosition,
    setPosition: setWatermarkPosition,
    resetPosition: resetWatermarkPosition,
  } = useWatermarkPosition({
    parentId: project?.id ?? null,
    loadedPosition: (project as any)?.watermark_position,
    enabled: !!project?.id,
  });

  const updateSubtitle = useCallback(
    (field: keyof SubtitleEditorData, value: any) => {
      setSubtitleData((prev) => ({ ...prev, [field]: value }));
      // Manual edit → the look no longer matches the applied kit.
      setSubtitleDriftsFromKit(true);
    },
    [],
  );

  const updateLogo = useCallback(
    (field: keyof SubtitleEditorLogoData, value: any) => {
      setLogoData((prev) => ({ ...prev, [field]: value }));
      setSubtitleDriftsFromKit(true);
    },
    [],
  );

  const handleLogoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: t("configureSubtitles.toast.error"),
          description: t("common.fileTooLarge", { size: "5MB" }),
          variant: "destructive",
        });
        event.target.value = "";
        return;
      }
      if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
        toast({
          title: t("configureSubtitles.toast.error"),
          description: t("common.invalidFileType", { types: "PNG, JPG" }),
          variant: "destructive",
        });
        event.target.value = "";
        return;
      }
      setIsUploadingLogo(true);
      try {
        const uploadUrl = await getUploadUrl(file);
        if (uploadUrl) {
          updateLogo("url", uploadUrl);
          registerUserMedia({ url: uploadUrl, kind: "image", name: file.name });
        }
      } catch (error) {
        console.error("Error uploading logo:", error);
      } finally {
        setIsUploadingLogo(false);
        event.target.value = "";
      }
    },
    [getUploadUrl, toast, t, updateLogo, registerUserMedia],
  );

  // Subtitle config derived from the editor state (fed to the live preview
  // and to the renderV2 payload).
  const subtitlePreviewConfig = useMemo(
    () => buildSubtitlePreviewConfig({
      subtitleData,
      resolvedFontFamily: getFontFamilyName(selectedSubtitleFontId),
    }),
    [subtitleData, selectedSubtitleFontId, getFontFamilyName],
  );

  const brandKitLogoConfig = useMemo(() => {
    if (!logoData.url) return null;
    return {
      url: logoData.url,
      position: logoData.position,
      size: logoData.size,
      opacity: logoData.opacity,
      horizontalMargin: logoData.horizontalMargin,
      verticalMargin: logoData.verticalMargin,
    };
  }, [logoData]);

  // Build subtitle_config / logo_config payload for renderV2 requests
  const buildRenderExtras = useCallback(
    () => buildSubtitleLogoRenderConfig({
      subtitlesEnabled,
      subtitleData,
      logoData,
      selectedSubtitleFontId,
      fonts: fontsData?.myFonts,
    }),
    [subtitlesEnabled, subtitleData, logoData, selectedSubtitleFontId, fontsData?.myFonts],
  );

  // Handle brand kit changes and save to project
  const handleBrandKitChange = useCallback(
    (
      brandKitId: string | null,
      { preserveRestoredConfig = false }: { preserveRestoredConfig?: boolean } = {},
    ) => {
      // Prevent removing brand kit when subtitles are enabled (pro model
      // couples them; free users edit manually and may have no kit at all)
      if (!brandKitId && subtitlesEnabled && hasProFeatures) {
        toast({
          title: t("projectEditor.brandKitRequired"),
          description: t("projectEditor.disableSubtitlesFirst"),
          variant: "destructive",
        });
        return;
      }

      if (brandKitId) {
        const kit = brandKitsData?.myBrandKits?.find(
          (candidate) => candidate.id === brandKitId,
        );
        if (!kit) return;
        if (!canApplyBrandKit({ hasProFeatures, kit })) {
          setKitApplyProDialogOpen(true);
          return;
        }
        if (!preserveRestoredConfig && kit.data) {
          loadBrandKitIntoEditor(kit);
          const applied = applyTemplateToEditor(postLayoutRef.current, kit.data);
          setPostLayout(applied.postLayout);
          setLayoutFieldBinds(applied.fieldBinds);
          setSelectedSpeakerLayout(1);
          kitPrefillGateRef.current = gateForAppliedKit(kit.id);
          setSubtitleDriftsFromKit(false);
        }
      }
      if (!preserveRestoredConfig) {
        kitPrefillGateRef.current = { ...kitPrefillGateRef.current, keepSavedSettings: false };
      }
      // Brand kit is set locally and associated with the clip on export/activate
      setSelectedBrandKit(brandKitId);
    },
    [subtitlesEnabled, toast, t, hasProFeatures, brandKitsData?.myBrandKits, loadBrandKitIntoEditor],
  );

  // Force brand kit selection when subtitles are enabled for PRO users. Free
  // users may choose a public kit explicitly or edit subtitle config manually.
  // The project's own kit (chosen when the video was submitted) comes first:
  // this effect can run in the same commit as the restore effect above, while
  // `selectedBrandKit` still reads null, and its pick is the one that sticks.
  useEffect(() => {
    if (!hasProFeatures) return;
    if (subtitlesEnabled && !selectedBrandKit && brandKitsData?.myBrandKits) {
      const kitId = resolveInitialBrandKitId({
        savedBrandKitId: project?.brand_kit?.id,
        kits: brandKitsData.myBrandKits,
        hasProFeatures,
      });
      if (kitId) handleBrandKitChange(kitId, { preserveRestoredConfig: true });
    }
  }, [subtitlesEnabled, selectedBrandKit, brandKitsData, handleBrandKitChange, hasProFeatures, project?.brand_kit?.id]);

  const { url: videoUrl, hasQualityChoice: hasPreviewQualityChoice } = useMemo(
    () => resolvePreviewVideoUrl({ quality: previewQuality, project }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      previewQuality,
      project.base_video_url,
      project.compressed_base_video_url,
      project.cut_video_url,
      project.compressed_cut_video_url,
    ],
  );

  // Small stills never need the full-quality playback file, even in HD mode.
  const thumbnailVideoUrl = resolvePreviewVideoUrl({ quality: "compressed", project }).url;
  // The clip cover is a full-resolution first frame, so capture it from the
  // original even while the preview plays the compressed tier.
  const coverCaptureVideoUrl = resolvePreviewVideoUrl({ quality: "full", project }).url || undefined;
  // The cover "Prévia" candidate: the frame the preview shows, rendered from
  // the full-quality source (the Player may be playing the compressed tier).
  const captureCoverPreviewFrame = useCallback(async (): Promise<string | null> => {
    const preview = videoPlayerRef.current;
    if (!preview) return null;
    return preview.renderCurrentFrameStill({ videoUrl: coverCaptureVideoUrl });
  }, [coverCaptureVideoUrl]);
  const { extractFrame: extractClipThumbnail } = useVideoFrameExtractor(thumbnailVideoUrl, {
    canvasWidth: 320, canvasHeight: 180, quality: 0.6, maxCachedFrames: 80,
  });
  // The Effects panel renders its LUT thumbnails from the frame under the
  // playhead. Refresh only once playback is paused and the playhead settles,
  // through the same shared extractor the clip cards use.
  const [effectsThumbnailFrame, setEffectsThumbnailFrame] = useState<string | null>(null);
  useEffect(() => {
    if (isPlaying || !thumbnailVideoUrl) return undefined;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      extractClipThumbnail(currentTime)
        .then((frame) => { if (!cancelled && frame) setEffectsThumbnailFrame(frame); })
        .catch(() => undefined);
    }, 400);
    return () => { cancelled = true; window.clearTimeout(handle); };
  }, [currentTime, isPlaying, thumbnailVideoUrl, extractClipThumbnail]);

  const downloadVideoUrl = useMemo(() => {
    return resolveOriginalVideoDownloadUrl({
      cutVideoUrl: project.cut_video_url,
      baseVideoUrl: project.base_video_url,
    }) || "";
  }, [project.cut_video_url, project.base_video_url]);

  // Metadata is identical across quality tiers; share the lighter source probe.
  useEffect(() => {
    setPreviewReady(false);
    const fallback = window.setTimeout(() => setPreviewReady(true), 3000);
    return () => window.clearTimeout(fallback);
  }, [videoUrl]);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      void getAvailableExportFormats(thumbnailVideoUrl).then(formats => {
        if (!cancelled) setAvailableFormats(formats);
      });
    };
    const idle = window.requestIdleCallback?.(run, { timeout: 2000 });
    const timer = idle === undefined ? window.setTimeout(run, 1500) : null;
    return () => {
      cancelled = true;
      if (idle !== undefined) window.cancelIdleCallback(idle);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [thumbnailVideoUrl]);

  // Track which clips have been locally modified (dirty) before backend confirms
  const [dirtyClipIds, setDirtyClipIds] = useState<Set<string>>(new Set());

  // Fetch a hidden clip when initialClipId is not found in project clips
  const [fetchHiddenClip, { data: hiddenClipData }] = useLazyQuery(
    GET_MY_CLIP,
    {
      fetchPolicy: "network-only",
    },
  );
  const hiddenClip = hiddenClipData?.myClip ?? null;

  const clips = useMemo(() => {
    const curatedClips = project.curated_clips || [];
    const projectClips = (project as any).clips || [];

    // Prefer real clips which support rating.
    // Fall back to curated_clips JSON only for legacy projects with no real clips.
    const mappedProjectClips = projectClips.map((clip: any, i: number) => {
      const start =
        typeof clip.clip_start_time === "number"
          ? clip.clip_start_time
          : undefined;
      const end =
        typeof clip.clip_end_time === "number" ? clip.clip_end_time : undefined;
      const center =
        typeof start === "number" && typeof end === "number"
          ? (start + end) / 2
          : (start ?? 0);

      const title = clip.name || "Untitled";
      const summary =
        typeof clip.clip_summary === "string"
          ? clip.clip_summary
          : clip.clip_summary?.text || clip.clip_summary?.summary || "";

      const isSkipped = clip.clipping_status === "skipped";

      return {
        id: i + 1,
        skippedClipId: isSkipped ? clip.id || null : null,
        activeClipId: isSkipped ? null : clip.id || null,
        title,
        score: (clip.clip_score ?? 0) * 100,
        clip_order: clip.clip_order as number | null | undefined,
        clip_description: summary,
        clip_start_time: start,
        clip_end_time: end,
        original_clip_start_time:
          typeof clip.original_clip_start_time === "number"
            ? clip.original_clip_start_time
            : undefined,
        original_clip_end_time:
          typeof clip.original_clip_end_time === "number"
            ? clip.original_clip_end_time
            : undefined,
        time: center,
        clip_thumbnail:
          clip.clip_thumbnail ||
          clip.thumbnail_url ||
          clip.finished_video_thumbnail_url ||
          null,
        isFallback: false,
        dirty: clip.dirty || dirtyClipIds.has(clip.id) || false,
        data: clip.data || null,
      };
    });

    // Always merge curated_clips into the list, deduplicating by matching start/end times
    const mappedCuratedClips = curatedClips.map((clip: any) => {
      const start =
        typeof clip.clip_start_time === "number"
          ? clip.clip_start_time
          : undefined;
      const end =
        typeof clip.clip_end_time === "number" ? clip.clip_end_time : undefined;
      const center =
        typeof start === "number" && typeof end === "number"
          ? (start + end) / 2
          : (start ?? 0);

      const title =
        typeof clip.clip_title === "string"
          ? clip.clip_title
          : clip.clip_title?.text || clip.clip_title?.title || "Untitled";

      const summary =
        typeof clip.clip_summary === "string"
          ? clip.clip_summary
          : clip.clip_summary?.text || clip.clip_summary?.summary || "";

      return {
        id: 0,
        skippedClipId: null as string | null,
        activeClipId: null as string | null,
        title,
        score: (clip.clip_score ?? 0) * 100,
        clip_order: undefined as number | null | undefined,
        clip_description: summary,
        clip_start_time: start,
        clip_end_time: end,
        time: center,
        clip_thumbnail: clip.clip_thumbnail,
        isFallback: false,
        dirty: false,
        data: null as any,
      };
    });

    // Filter out curated clips that already exist as real clips (by matching start/end times)
    const uniqueCuratedClips = mappedCuratedClips.filter((curated: any) => {
      return !mappedProjectClips.some(
        (real: any) =>
          typeof curated.clip_start_time === "number" &&
          typeof real.clip_start_time === "number" &&
          Math.abs(curated.clip_start_time - real.clip_start_time) < 1 &&
          typeof curated.clip_end_time === "number" &&
          typeof real.clip_end_time === "number" &&
          Math.abs(curated.clip_end_time - real.clip_end_time) < 1,
      );
    });

    // If initialClipId is provided and a hidden clip was fetched, inject it if not already present
    const combinedProjectClips = [...mappedProjectClips, ...uniqueCuratedClips];
    if (
      initialClipId &&
      hiddenClip &&
      hiddenClip.id === initialClipId &&
      !combinedProjectClips.some(
        (c) =>
          c.skippedClipId === initialClipId || c.activeClipId === initialClipId,
      )
    ) {
      const hc = hiddenClip;
      const start =
        typeof hc.clip_start_time === "number" ? hc.clip_start_time : undefined;
      const end =
        typeof hc.clip_end_time === "number" ? hc.clip_end_time : undefined;
      const center =
        typeof start === "number" && typeof end === "number"
          ? (start + end) / 2
          : (start ?? 0);
      const isSkipped = hc.clipping_status === "skipped";
      const summary =
        typeof hc.clip_summary === "string"
          ? hc.clip_summary
          : hc.clip_summary?.text || hc.clip_summary?.summary || "";

      combinedProjectClips.push({
        id: 0,
        skippedClipId: isSkipped ? hc.id || null : null,
        activeClipId: isSkipped ? null : hc.id || null,
        title: hc.name || "Untitled",
        score: (hc.clip_score ?? 0) * 100,
        clip_order: hc.clip_order as number | null | undefined,
        clip_description: summary,
        clip_start_time: start,
        clip_end_time: end,
        original_clip_start_time:
          typeof hc.original_clip_start_time === "number"
            ? hc.original_clip_start_time
            : undefined,
        original_clip_end_time:
          typeof hc.original_clip_end_time === "number"
            ? hc.original_clip_end_time
            : undefined,
        time: center,
        clip_thumbnail:
          hc.clip_thumbnail ||
          hc.thumbnail_url ||
          hc.finished_video_thumbnail_url ||
          null,
        isFallback: false,
        dirty: hc.dirty || false,
        data: hc.data || null,
      });
    }

    const allClips = combinedProjectClips
      .sort((a, b) => {
        const orderA = a.clip_order ?? Infinity;
        const orderB = b.clip_order ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;
        return (b.score ?? 0) - (a.score ?? 0);
      })
      .map((clip, i) => ({ ...clip, id: i + 1 }));

    // If nothing at all, create a fallback clip
    if (allClips.length === 0) {
      const fallbackDuration = Math.min(60, project.duration_seconds || 60);
      return [
        {
          id: 1,
          skippedClipId: null as string | null,
          activeClipId: null as string | null,
          title: t("projectEditor.autoClip"),
          score: 100,
          clip_description: t("projectEditor.defaultClipDescription"),
          clip_start_time: 0,
          clip_end_time: fallbackDuration,
          time: fallbackDuration / 2,
          clip_thumbnail: null,
          isFallback: true,
          data: null as any,
        },
      ];
    }

    return allClips;
  }, [
    (project as any)?.clips,
    project?.curated_clips,
    project?.duration_seconds,
    dirtyClipIds,
    initialClipId,
    hiddenClip,
  ]);

  const selectedClipData = clips.find((c) => c.id === selectedClips[0]);
  const channelSource = useMemo(
    () => resolveLayoutChannelSource(project as any),
    [project],
  );

  useEffect(() => {
    clipsRef.current = clips;
  }, [clips]);

  // ----- Post-frame layout state (registry-driven) -----
  // Campaign-enforced post layout (null when no active campaign enforcement).
  // Both hooks are called unconditionally (Rules of Hooks); the id is set to
  // null for whichever branch is not active so the query is skipped.
  const projectEffectivePostLayout = useEffectivePostLayout(
    !isLibrary ? (project?.id ?? null) : null,
  );
  const libraryEffectivePostLayout = useLibraryEffectivePostLayout(
    isLibrary ? (project?.id ?? null) : null,
  );
  const effectivePostLayout = isLibrary ? libraryEffectivePostLayout : projectEffectivePostLayout;

  const selectedKit = useMemo(() => {
    if (!selectedBrandKit || !brandKitsData?.myBrandKits) return null;
    return brandKitsData.myBrandKits.find(
      (k: any) => k.id === selectedBrandKit,
    ) ?? null;
  }, [selectedBrandKit, brandKitsData]);

  const brandKitLayoutSource = useMemo<BrandKitLayoutSource | null>(
    () => (selectedKit ? harvestBrandKitLayoutSource(selectedKit.data) : null),
    [selectedKit],
  );

  const selectPostLayout = useCallback(
    (kind: keyof typeof POST_LAYOUTS, options?: { preset?: string }) => {
      const clip = clips.find((c) => c.id === selectedClips[0]);
      const built = buildPostLayoutSelection(
        kind,
        options,
        {
          clipTitle: clip?.title,
          channelName: channelSource?.name,
          channelAvatarUrl: channelSource?.thumbnail,
        },
        brandKitLayoutSource,
        postLayout,
      );
      const overlaid = overlayTemplateOnCurrentLayout(built, selectedKit?.data) ?? built;
      setPostLayout(overlaid);
      const parsed = parsePersistedPostLayout(
        selectedKit?.data && typeof selectedKit.data === 'object'
          ? (selectedKit.data as { post_layout?: unknown }).post_layout
          : undefined,
      );
      setLayoutFieldBinds((prev) => withMatchingClipTitleBinds(
        kind,
        overlaid,
        resolveClipTitleForBind(clip?.title),
        {
          ...defaultLayoutBinds(kind),
          ...sanitizeLayoutFieldBinds(kind, parsed?.fieldBinds ?? {}),
          ...sanitizeLayoutFieldBinds(kind, prev),
        },
      ));
      if (POST_LAYOUTS[kind].requiredAspect) {
        setPreviewAspectRatio(POST_LAYOUTS[kind].requiredAspect);
      }
      setSelectedSpeakerLayout(1); // mutual exclusion
      // Kits carry the layout, so picking one diverges from the applied kit.
      setSubtitleDriftsFromKit(true);
      if (shouldSeedCirculoSubtitle(postLayout?.kind ?? null, kind)) {
        setSubtitleData((prev) => ({ ...prev, ...CIRCULO_SUBTITLE_SEED }));
        setSelectedSubtitleFontId(CIRCULO_SUBTITLE_SEED.fontFamily);
      }
    },
    [clips, selectedClips, brandKitLayoutSource, selectedKit, channelSource, postLayout],
  );

  // Debounced persistence of the post-frame layout to the parent. Skips the
  // load echo and flushes a pending write (e.g. the last Satisfying-cell
  // drag) on unmount.
  const persistedPostLayoutBlob = useMemo(
    () => (postLayout
      ? buildPersistedPostLayout({ postLayout, fieldBinds: layoutFieldBinds })
      : null),
    [postLayout, layoutFieldBinds],
  );
  useAutoSavePostLayout({
    blob: persistedPostLayoutBlob,
    loadedBlob: (project as any)?.post_layout,
    onPersist: onPostLayoutChange,
  });

  // Restore persisted post-frame layout on load (a {kind, config} blob).
  // When a campaign enforces a layout, build the merged config instead.
  useEffect(() => {
    const parsed = parsePersistedPostLayout((project as any)?.post_layout);
    if (effectivePostLayout) {
      const merged = buildEnforcedInitialConfig(
        effectivePostLayout,
        parsed?.postLayout
          ? { kind: parsed.postLayout.kind, ...(parsed.postLayout as unknown as Record<string, unknown>) }
          : null,
      );
      setPostLayout(merged);
      // Re-sanitize binds against the ACTIVE layout kind so stale binds cannot apply under enforcement
      setLayoutFieldBinds(sanitizeLayoutFieldBinds(merged.kind, parsed?.fieldBinds ?? {}));
    } else if (parsed) {
      setPostLayout(parsed.postLayout);
      setLayoutFieldBinds(parsed.fieldBinds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(project as any)?.post_layout, effectivePostLayout]);

  const selectedClipTitle = resolveClipTitleForBind(
    clips.find((c) => c.id === selectedClips[0])?.title,
  );
  const clipTitleBindSnapshot = postLayout
    ? clipTitleKeysForKind(postLayout.kind)
        .map((key) => String((postLayout as unknown as Record<string, unknown>)[key] ?? ''))
        .join('\0')
    : '';
  useEffect(() => {
    const layout = postLayoutRef.current;
    if (!layout) return;
    setLayoutFieldBinds((prev) => {
      const next = withMatchingClipTitleBinds(
        layout.kind,
        layout,
        selectedClipTitle,
        prev,
      );
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, [postLayout?.kind, selectedClipTitle, clipTitleBindSnapshot]);

  useEffect(() => {
    selectedClipsRef.current = selectedClips;
  }, [selectedClips]);
  useEffect(() => {
    clipIntervalsRef.current = clipIntervals;
  }, [clipIntervals]);
  useEffect(() => {
    editedCropKeyframesRef.current = editedCropKeyframes;
  }, [editedCropKeyframes]);
  useEffect(() => {
    sceneTransitionsRef.current = sceneTransitions;
  }, [sceneTransitions]);
  useEffect(() => {
    keyframeTransitionsRef.current = keyframeTransitions;
  }, [keyframeTransitions]);
  useEffect(() => {
    coverImageRef.current = coverImage;
  }, [coverImage]);

  useEditorClipSelection({
    initialClipId,
    clips,
    selectedClips,
    setSelectedClips,
    fetchHiddenClip,
    swiperRef,
  });

  // Calculate total duration of all segments
  const totalSegmentDuration = useMemo(() => {
    let segments = clipIntervals;

    // If no intervals are set, use the selected clip's start/end times
    if (segments.length === 0) {
      const selectedClipData = clips.find(
        (clip) => clip.id === selectedClips[0],
      );
      if (
        selectedClipData &&
        typeof selectedClipData.clip_start_time === "number" &&
        typeof selectedClipData.clip_end_time === "number"
      ) {
        segments = [
          {
            id: selectedClipData.id,
            startTime: selectedClipData.clip_start_time,
            endTime: selectedClipData.clip_end_time,
          },
        ];
      }
    }

    return segments.reduce((total, segment) => {
      return total + Math.max(0, segment.endTime - segment.startTime);
    }, 0);
  }, [clipIntervals, clips, selectedClips]);

  const isInvalidDuration =
    totalSegmentDuration < 1 ||
    totalSegmentDuration > MAX_CLIP_DURATION_SECONDS;

  const durationErrorMessage =
    totalSegmentDuration < 1
      ? t("projectEditor.minDuration")
      : totalSegmentDuration > MAX_CLIP_DURATION_SECONDS
        ? t("projectEditor.maxDuration")
        : "";

  const words = useMemo(() => {
    const src = transcriptionDraftRef.current ?? transcription;
    const transcriptionWords: any[] =
      src?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
    return [...transcriptionWords];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcription, transcriptionDraftVersion]);

  // Debounce intervals for transcription calculation to improve performance during adjustments
  const debouncedIntervals = useDebouncedIntervals(clipIntervals, 300);

  // Auto-save clip data when segments change (for any real clip, not curated_clips fallback)
  const currentSaveableClipId = useMemo(() => {
    const sel = clips.find((clip) => clip.id === selectedClips[0]);
    return sel?.skippedClipId || sel?.activeClipId || null;
  }, [clips, selectedClips]);

  // Effective crop keyframes: user edits override the computed defaults.
  // Hoisted here (ahead of its other crop-keyframe sibling hooks further
  // down) so handleLayerFile and the preview render below can share the
  // portrait-fallback aspect derived from it in effectivePreviewAspect.
  const effectiveCropKeyframes = useMemo<CropCenterKeyframe[]>(
    () => editedCropKeyframes ?? cropCenterKeyframes,
    [editedCropKeyframes, cropCenterKeyframes],
  );

  // Boundaries between the effective crop keyframes, and the keyframe
  // transitions snapped to them. Sanitized here (not at load) so entries
  // survive while the computed-default keyframes are still resolving.
  const keyframeCutBoundaries = useMemo(
    () => keyframeBoundaries(effectiveCropKeyframes),
    [effectiveCropKeyframes],
  );
  const effectiveKeyframeTransitions = useMemo(
    () => sanitizeKeyframeTransitions(keyframeTransitions, keyframeCutBoundaries),
    [keyframeTransitions, keyframeCutBoundaries],
  );
  // What the preview plays. While the framing is still the detected one
  // (nothing saved), the export glides at detection camera moves, so the
  // preview does too; the user's own transitions come first and win at a
  // shared boundary. Preview-only: saves and the PRO count use
  // effectiveKeyframeTransitions, so the automatic glides are never persisted.
  const previewKeyframeTransitions = useMemo(
    () => (editedCropKeyframes
      ? effectiveKeyframeTransitions
      : sanitizeKeyframeTransitions(
        [...keyframeTransitions, ...autoCameraMoveTransitions(detectionCropKeyframes)],
        keyframeCutBoundaries,
      )),
    [
      editedCropKeyframes,
      effectiveKeyframeTransitions,
      keyframeTransitions,
      detectionCropKeyframes,
      keyframeCutBoundaries,
    ],
  );

  // Natural scenes are a presentation until the user edits them. This keeps
  // simply opening a production clip read-only, even when detection arrives late.
  const naturalTimeline = useMemo(() => splitNaturalScenes({
    intervals: clipIntervals,
    alreadySplit: sceneSequence,
    boundaries: [
      ...detectionScenes.flatMap((scene: { start_time: number; end_time: number }) => [scene.start_time, scene.end_time]),
      ...keyframeCutBoundaries,
    ],
    transitions: sceneTransitions,
    keyframeTransitions,
  }), [clipIntervals, sceneSequence, detectionScenes, keyframeCutBoundaries, sceneTransitions, keyframeTransitions]);

  // applyNaturalTimeline skips the undo entry (the assistant's batches have
  // their own undo); every user edit goes through commitNaturalTimeline.
  const applyNaturalTimeline = useCallback((
    next: TimelineInterval[],
    transitions: SceneTransition[],
    framingTransitions = naturalTimeline.keyframeTransitions,
  ) => {
    setClipIntervals(next);
    setSceneTransitions(transitions);
    setKeyframeTransitions(framingTransitions);
    setSceneSequence(true);
    if (effectiveCropKeyframes.length) {
      setEditedCropKeyframes(splitSceneCropKeyframes(effectiveCropKeyframes, next));
    }
  }, [naturalTimeline.keyframeTransitions, effectiveCropKeyframes]);
  const commitNaturalTimeline = useCallback((
    next: TimelineInterval[],
    transitions: SceneTransition[],
    framingTransitions = naturalTimeline.keyframeTransitions,
  ) => {
    pushUndoSnapshot("interval");
    applyNaturalTimeline(next, transitions, framingTransitions);
  }, [naturalTimeline.keyframeTransitions, pushUndoSnapshot, applyNaturalTimeline]);

  const setNaturalIntervalsWithUndo = useCallback((next: TimelineInterval[]) => {
    commitNaturalTimeline(next, reconcileTransitions(naturalTimeline.intervals, next, naturalTimeline.transitions));
  }, [naturalTimeline, commitNaturalTimeline]);

  /** Previewing 9:16 from a horizontal source before auto-crop has run. */
  const autoCropPending = useMemo(
    () => shouldShowAutoCropPendingNotice({
      effectiveAspect: effectivePreviewAspect,
      hasCropKeyframes: effectiveCropKeyframes.length > 0,
      availableFormats,
    }),
    [effectivePreviewAspect, effectiveCropKeyframes, availableFormats],
  );

  const updatePostLayoutField = useCallback((key: string, value: PostLayoutFieldValue) => {
    setPostLayout((prev) => {
      if (!prev) return prev;
      return applyPostLayoutField(prev, key, value, effectivePreviewAspect === "landscape");
    });
    if (typeof value === "string") {
      const clipTitle = resolveClipTitleForBind(
        clips.find((c) => c.id === selectedClips[0])?.title,
      );
      if (postLayout && clipTitleKeysForKind(postLayout.kind).includes(key)) {
        setLayoutFieldBinds((prev) => {
          if (value === clipTitle && clipTitle) {
            return sanitizeLayoutFieldBinds(postLayout.kind, { ...prev, [key]: "clipTitle" });
          }
          return prev;
        });
      }
    }
    setSubtitleDriftsFromKit(true);
  }, [effectivePreviewAspect, clips, selectedClips, postLayout]);

  // Media layers (image overlays) for the currently selected/edited clip.
  // CapCut-style gate: free to add + preview, PRO required to export a clip
  // that contains any layer (see canExportWithLayers usages below).
  const selectedLayerClipId = currentSaveableClipId;
  const initialLayers = useMemo(() => {
    const sel = clips.find((clip) => clip.id === selectedClips[0]);
    if (!sel) return [];
    return resolvePersistedClipPreviewForClip({
      clip: sel,
      sceneDetection,
      isPortrait: effectivePreviewAspect === 'portrait',
      projectDuration: project?.duration_seconds,
    }).mediaLayers;
  }, [
    clips,
    selectedClips,
    sceneDetection,
    project?.duration_seconds,
    effectivePreviewAspect,
  ]);
  const clipDuration = clipIntervals.reduce(
    (sum, iv) => sum + (iv.endTime - iv.startTime),
    0,
  );
  const fillerPool = useMemo(
    () => fillerCatalogUrls(fillerVideosData?.fillerVideos ?? [], i18n.language),
    [fillerVideosData?.fillerVideos, i18n.language],
  );
  const fillerRandomize = !!(postLayout && 'fillerRandomize' in postLayout && postLayout.fillerRandomize);
  const bottomFillerRandomize = !!(postLayout && 'bottomFillerRandomize' in postLayout && postLayout.bottomFillerRandomize);
  const fillerSpeed = postLayout && 'fillerSpeed' in postLayout ? postLayout.fillerSpeed : undefined;
  const previewFillerUrls = useMemo(() => {
    if (!fillerRandomize || fillerPool.length === 0) return null;
    return materializeFillerPlaylist(
      { fillerUrl: '', fillerRandomize: true, fillerSpeed },
      { pool: fillerPool, targetDurationSec: clipDuration || 8 },
    );
  }, [fillerRandomize, fillerSpeed, fillerPool, clipDuration]);
  const previewBottomFillerUrls = useMemo(() => {
    if (!bottomFillerRandomize || fillerPool.length === 0) return null;
    return materializeFillerPlaylist(
      { fillerUrl: '', fillerRandomize: true, fillerSpeed },
      { pool: fillerPool, targetDurationSec: clipDuration || 8 },
    );
  }, [bottomFillerRandomize, fillerSpeed, fillerPool, clipDuration]);
  const postLayoutForPreview = useMemo(
    () => {
      let withFillers = postLayout;
      if (withFillers && previewFillerUrls) {
        withFillers = { ...withFillers, fillerUrls: previewFillerUrls, fillerUrl: previewFillerUrls[0] ?? '' } as PostLayoutConfig;
      }
      if (withFillers && previewBottomFillerUrls) {
        withFillers = {
          ...withFillers,
          bottomFillerUrls: previewBottomFillerUrls,
          bottomFillerUrl: previewBottomFillerUrls[0] ?? '',
        } as PostLayoutConfig;
      }
      if (!withFillers) return withFillers;
      const selectedClipData = clips.find((clip) => clip.id === selectedClips[0]);
      return applyLayoutBinds(withFillers, layoutFieldBinds, {
        clipTitle: resolveClipTitleForBind(selectedClipData?.title),
        channelName: channelSource?.name,
        channelAvatarUrl: channelSource?.thumbnail,
        userName: myData?.myData?.name ?? undefined,
        userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
      });
    },
    [postLayout, previewFillerUrls, previewBottomFillerUrls, clips, selectedClips, layoutFieldBinds, channelSource, myData],
  );
  const {
    layers,
    activeLayerId,
    setActiveLayerId,
    addLayer,
    patchLayer,
    moveLayer,
    deleteLayer,
    commit: commitLayers,
  } = useMediaLayers(selectedLayerClipId, initialLayers);

  /** The layer whose contextual bar floats over the preview, if any. */
  const activeLayer = useMemo(
    () => layers.find((l) => l.id === activeLayerId) ?? null,
    [layers, activeLayerId],
  );

  /** The playhead on the output clip's timeline — the axis layer windows live
   *  on. `currentTime` is source-video time. */
  const currentClipTime = useMemo(
    () => videoTimeToClipTime(currentTime, clipIntervals),
    [currentTime, clipIntervals],
  );

  // Which timeline scene/interval is selected (for highlight + keyboard delete).
  const [selectedIntervalId, setSelectedIntervalId] = useState<number | null>(null);
  // Which timeline effect section is selected (controls row, delete, nudge).
  const [activeEffectSectionId, setActiveEffectSectionId] = useState<string | null>(null);

  // Single-focus selection: picking a layer, a scene or an effect section
  // clears the other two, so a Delete keypress has one unambiguous target.
  const selectLayer = useCallback((id: string | null) => {
    setActiveLayerId(id);
    if (id != null) { setSelectedIntervalId(null); setActiveEffectSectionId(null); }
  }, [setActiveLayerId]);

  const selectInterval = useCallback((id: number | null) => {
    setSelectedIntervalId(id);
    if (id != null) { setActiveLayerId(null); setActiveEffectSectionId(null); }
  }, [setActiveLayerId]);

  // Timeline effect sections live in videoEffects.sections and commit through
  // handleVideoEffectsChange, exactly like a whole-clip effects edit: an undo
  // entry, the per-clip draft, and the debounced autosave (with its page-hide
  // flush). Discrete edits (add, delete, ease, lane drag) are one entry each;
  // arrow nudges and slider keyboard steps pass an undo category so a run
  // folds into one entry. An undo can remove the selected section, so the
  // selection is resolved against the live list rather than trusted.
  const effectSections = videoEffects.sections ?? [];
  const activeEffectSection = effectSections.find((s) => s.id === activeEffectSectionId) ?? null;
  const commitEffectSections = useCallback((sections: EffectSection[], undoCategory?: string) => {
    handleVideoEffectsChange(normalizeVideoEffects({ ...videoEffectsRef.current, sections }), undoCategory);
  }, [handleVideoEffectsChange]);
  const selectEffectSection = useCallback((id: string | null) => {
    setActiveEffectSectionId(id);
    if (id == null) return;
    setActiveLayerId(null);
    setSelectedIntervalId(null);
    // The lane bars cancel pointerdown (no focus move), so a control focused
    // earlier — the playhead or a trim handle, both sliders — would keep
    // consuming ←/→ and the selected section could never be nudged. Hand the
    // keyboard to the selection, as a click elsewhere would. Text fields keep
    // focus: typing there is never hijacked.
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && isKeyOwningTarget(focused) && !isEditableTarget(focused)) focused.blur();
  }, [setActiveLayerId]);
  const addEffectSection = useCallback((effect: EffectId) => {
    const sections = videoEffectsRef.current.sections ?? [];
    if (sections.length >= MAX_EFFECT_SECTIONS) return;
    const section = createSectionAtPlayhead({ effect, playhead: currentClipTime, clipDuration: clipDuration ?? 0, id: `fx-${crypto.randomUUID()}` });
    if (!section) return;
    commitEffectSections([...sections, section]);
    selectEffectSection(section.id);
  }, [currentClipTime, clipDuration, commitEffectSections, selectEffectSection]);
  // A patch that changes nothing (a slider released on its value, a nudge
  // against a clip edge) commits nothing: no undo entry, no save.
  const patchEffectSection = useCallback((id: string, patch: Partial<EffectSection>, undoCategory?: string) => {
    const sections = videoEffectsRef.current.sections ?? [];
    const index = sections.findIndex((s) => s.id === id);
    const merged = index === -1 ? null : applyEffectSectionPatch(sections[index], patch);
    if (!merged) return;
    commitEffectSections(sections.map((s, i) => (i === index ? merged : s)), undoCategory);
  }, [commitEffectSections]);
  const deleteEffectSection = useCallback((id: string) => {
    commitEffectSections((videoEffectsRef.current.sections ?? []).filter((s) => s.id !== id));
    setActiveEffectSectionId(null);
  }, [commitEffectSections]);
  // addLayer (useMediaLayers) selects the new layer directly, bypassing
  // selectLayer — e.g. from the canvas context menu, which is layer UI and so
  // never triggers the outside-click deselect. Keep the selection exclusive.
  useEffect(() => {
    if (activeLayerId != null) setActiveEffectSectionId(null);
  }, [activeLayerId]);

  // Scene-transition editing: a "cut" is the boundary between clipIntervals[i]
  // and clipIntervals[i+1], so there are always intervals.length - 1 of them.
  const cutCount = Math.max(0, naturalTimeline.intervals.length - 1);

  const handleTransitionChange = useCallback(
    (cutIndex: number, transition: SceneTransition | null) => {
      const rest = naturalTimeline.transitions.filter((t) => t.afterSceneIndex !== cutIndex);
      const next = transition
        ? [...rest, { ...transition, afterSceneIndex: cutIndex }].sort(
            (a, b) => a.afterSceneIndex - b.afterSceneIndex,
          )
        : rest;
      commitNaturalTimeline(naturalTimeline.intervals, next);
    },
    [naturalTimeline, commitNaturalTimeline],
  );

  const handleApplyTransitionToAll = useCallback(
    (transition: SceneTransition) => {
      const next = Array.from({ length: cutCount }, (_, i) => ({
        ...transition,
        afterSceneIndex: i,
      }));
      commitNaturalTimeline(naturalTimeline.intervals, next);
    },
    [cutCount, naturalTimeline, commitNaturalTimeline],
  );

  const handleRandomizeTransitions = useCallback(
    (kinds: TransitionKind[]) => {
      commitNaturalTimeline(naturalTimeline.intervals,
        assignRandomTransitions({ cutCount, kinds, rng: Math.random }),
      );
    },
    [cutCount, naturalTimeline, commitNaturalTimeline],
  );

  /** Everything the Auto (signal-driven) generator needs, resolved once.
   *  `available` is false for every project without a video_signals_url — the
   *  pre-PR-A world — which keeps the affordance visibly "not available for
   *  this project" instead of broken. */
  const autoTransitions = useMemo(() => {
    const url = project?.video_signals_url ?? null;
    if (!url) {
      return { available: false, reason: t("transitions.autoUnavailable"), label: null as string | null };
    }
    if (videoSignalsStatus === "loading") {
      return { available: false, reason: t("transitions.autoLoading"), label: null as string | null };
    }
    const parsed = parseVideoSignals(videoSignals);
    if (!parsed) {
      return { available: false, reason: t("transitions.autoError"), label: null as string | null };
    }
    const { contentType } = resolveContentTypeProfile(
      project?.content_type ?? parsed.content_type,
      project?.content_type_confidence ?? parsed.content_type_confidence,
    );
    return {
      available: true,
      reason: null as string | null,
      label: t("transitions.autoContentType", {
        type: t(`transitions.contentTypes.${contentType}`),
      }),
    };
  }, [project?.video_signals_url, project?.content_type, project?.content_type_confidence, videoSignals, videoSignalsStatus, t]);

  /** Assign-at-click, signal-driven. Replaces both transition sets in ONE undo
   *  entry: commitNaturalTimeline pushes the snapshot (captured from the
   *  refs, i.e. the pre-change state of BOTH sets) and the keyframe set is then
   *  applied with the plain setter. Persistence rides the existing
   *  useAutoSaveTransitions / useAutoSaveKeyframeTransitions debounce, and the
   *  PRO gate is the same canExportWithTransitions check at export time. */
  const handleAutoTransitions = useCallback(() => {
    if (!autoTransitions.available) return;
    const result = assignSignalTransitions({
      signals: videoSignals,
      clipIntervals: naturalTimeline.intervals,
      contentType: project?.content_type ?? null,
      contentTypeConfidence: project?.content_type_confidence ?? null,
      keyframeBoundaries: keyframeCutBoundaries,
    });
    if (result.sceneTransitions.length === 0 && result.keyframeTransitions.length === 0) {
      toast({ title: t("transitions.autoNoSuggestions") });
      return;
    }
    commitNaturalTimeline(naturalTimeline.intervals, result.sceneTransitions, result.keyframeTransitions);
  }, [
    autoTransitions.available,
    videoSignals,
    project?.content_type,
    project?.content_type_confidence,
    keyframeCutBoundaries,
    naturalTimeline,
    commitNaturalTimeline,
    toast,
    t,
  ]);

  // Keyframe-boundary transition editing: time-keyed (atTime = boundary), so
  // there is no index remap across keyframe edits. round3 mirrors the write-
  // time rounding the sanitizer expects.
  const toKeyframeTransition = useCallback(
    (atTime: number, transition: SceneTransition): KeyframeTransition => ({
      atTime: round3(atTime),
      kind: transition.kind,
      durationSec: clampTransitionDuration(transition.durationSec),
      ...(isDirectionalKind(transition.kind) && transition.direction
        ? { direction: transition.direction }
        : {}),
    }),
    [],
  );

  const handleKeyframeTransitionChange = useCallback(
    (atTime: number, transition: SceneTransition | null) => {
      const rest = naturalTimeline.keyframeTransitions.filter(
        (kt) => Math.abs(kt.atTime - atTime) >= KEYFRAME_TRANSITION_EPS,
      );
      const next = transition
        ? [...rest, toKeyframeTransition(atTime, transition)].sort(
            (a, b) => a.atTime - b.atTime,
          )
        : rest;
      commitNaturalTimeline(naturalTimeline.intervals, naturalTimeline.transitions, next);
    },
    [naturalTimeline, commitNaturalTimeline, toKeyframeTransition],
  );

  const handleApplyKeyframeTransitionToAll = useCallback(
    (transition: SceneTransition) => {
      const next = keyframeCutBoundaries.map((b) => toKeyframeTransition(b, transition));
      commitNaturalTimeline(naturalTimeline.intervals, naturalTimeline.transitions, next);
    },
    [keyframeCutBoundaries, naturalTimeline, commitNaturalTimeline, toKeyframeTransition],
  );

  const handleRandomizeKeyframeTransitions = useCallback(
    (kinds: TransitionKind[]) => {
      if (kinds.length === 0) {
        commitNaturalTimeline(naturalTimeline.intervals, naturalTimeline.transitions, []);
        return;
      }
      const next = keyframeCutBoundaries.map<KeyframeTransition>((b) => {
        const pick = Math.min(kinds.length - 1, Math.floor(Math.random() * kinds.length));
        return { atTime: round3(b), kind: kinds[pick], durationSec: DEFAULT_TRANSITION_DURATION_SEC };
      });
      commitNaturalTimeline(naturalTimeline.intervals, naturalTimeline.transitions, next);
    },
    [keyframeCutBoundaries, naturalTimeline, commitNaturalTimeline],
  );

  // Keyboard editing: Delete/Backspace removes the selected layer, effect
  // section or scene, Escape clears the selection, and ←/→ nudge the selected
  // effect section by one export frame. Ignored while typing in a text field so
  // editing transcript/text-layer content never nukes a layer.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveLayerId(null);
        setSelectedIntervalId(null);
        setActiveEffectSectionId(null);
        return;
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Only for a selected section, and only when nothing else owns the
        // key: a focused slider/trim handle/playhead handles (and consumes)
        // its own arrows, Alt+arrow reorders scenes, and dialogs keep theirs.
        if (!activeEffectSection) return;
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
        if (e.defaultPrevented || e.isComposing) return;
        if (isKeyOwningTarget(e.target) || isDialogOpen()) return;
        e.preventDefault();
        const next = moveSectionWindow(
          activeEffectSection,
          (e.key === "ArrowLeft" ? -1 : 1) * FRAME_DURATION,
          clipDuration ?? 0,
        );
        // A held key is one undo entry; against a clip edge it is a no-op.
        patchEffectSection(activeEffectSection.id, next, effectNudgeUndoCategory(activeEffectSection.id));
        return;
      }
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (isEditableTarget(e.target)) return;
      const target = resolveDeleteTarget({
        activeLayerId,
        selectedIntervalId,
        activeEffectSectionId: activeEffectSection?.id ?? null,
      });
      if (!target) return;
      e.preventDefault();
      if (target.kind === "layer") {
        deleteLayer(target.id);
      } else if (target.kind === "effect") {
        deleteEffectSection(target.id);
      } else {
        const next = removeInterval(naturalTimeline.intervals, target.id);
        if (next !== naturalTimeline.intervals) {
          setSelectedIntervalId(null);
          setNaturalIntervalsWithUndo(next);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    activeLayerId,
    selectedIntervalId,
    activeEffectSection,
    clipDuration,
    deleteLayer,
    deleteEffectSection,
    patchEffectSection,
    naturalTimeline.intervals,
    setNaturalIntervalsWithUndo,
    setActiveLayerId,
  ]);

  // Clicking anywhere that is not layer UI clears the selection, which hides the
  // LayerExpansionRow and the canvas handles. Capture phase, because
  // LayerCanvasEditor and LayerLanes call stopPropagation on their own
  // pointerdowns — a bubble-phase listener would never see them, and worse,
  // would deselect a layer whenever a handler stopped doing so. isLayerUiTarget
  // asks the DOM instead of trusting propagation.
  // Timeline effect sections follow the same rule: their lane bars, controls
  // row and library popover are layer UI.
  useEffect(() => {
    if (!activeLayerId && !activeEffectSectionId) return;
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return; // right-click opens a menu; it never deselects
      if (isLayerUiTarget(e.target)) return;
      setActiveLayerId(null);
      setActiveEffectSectionId(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [activeLayerId, activeEffectSectionId, setActiveLayerId]);

  const handleLayerFile = useCallback(
    async (file: File, kind: "image" | "video" | "audio" = "image") => {
      const id = `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const blobUrl = createBlobUrl(file);
      // The selection box on the canvas must hug the media exactly, so the
      // default rect is shrunk to the media's natural aspect at insert time.
      const compAspect =
        effectivePreviewAspect === "portrait" ? 1080 / 1920 : 1920 / 1080;
      let draft: MediaLayer;
      // Captured from the probe below so the registerUserMedia call after
      // upload (outside these branches) can pass real dimensions/duration.
      let probedWidth: number | undefined;
      let probedHeight: number | undefined;
      let probedDuration: number | undefined;
      if (kind === "image") {
        draft = makeImageLayer({ id, name: file.name, url: blobUrl, clipDuration });
        const size = await probeImageSize(blobUrl);
        if (size.width > 0 && size.height > 0) {
          probedWidth = size.width;
          probedHeight = size.height;
          draft = {
            ...draft,
            rect: fitRectToAspect({
              rect: draft.rect,
              mediaAspect: size.width / size.height,
              compAspect,
            }),
          };
        }
      } else {
        const meta = await probeMediaMetadata(blobUrl, kind);
        const mediaDuration = meta.duration || 5;
        if (meta.duration) probedDuration = meta.duration;
        const playhead = videoTimeToClipTime(currentTime, clipIntervals);
        const { start, end } = computeLayerWindow({
          playhead,
          duration: mediaDuration,
          clipDuration,
        });
        if (kind === "video") {
          let video = makeVideoLayer({ id, name: file.name, url: blobUrl, start, end, mediaDuration });
          if (meta.width > 0 && meta.height > 0) {
            probedWidth = meta.width;
            probedHeight = meta.height;
            video = {
              ...video,
              rect: fitRectToAspect({
                rect: video.rect,
                mediaAspect: meta.width / meta.height,
                compAspect,
              }),
            };
          }
          draft = video;
        } else {
          draft = makeAudioLayer({ id, name: file.name, url: blobUrl, start, end, mediaDuration });
        }
      }
      addLayer(draft);
      const uploadedUrl = await getUploadUrl(file, undefined, {
        acceptedMimeTypes: layerTypeConfig[kind].acceptMimeTypes,
      });
      if (uploadedUrl) {
        patchLayer(id, { url: uploadedUrl, uploading: false });
        // Editor uploads feed the user's media library for reuse.
        registerUserMedia({
          url: uploadedUrl,
          kind,
          name: file.name,
          ...(probedWidth ? { width: probedWidth } : {}),
          ...(probedHeight ? { height: probedHeight } : {}),
          ...(probedDuration ? { duration: probedDuration } : {}),
        });
      } else deleteLayer(id);
    },
    [addLayer, patchLayer, deleteLayer, getUploadUrl, createBlobUrl, clipDuration, currentTime, clipIntervals, effectivePreviewAspect, registerUserMedia],
  );

  // Stock picks (Pexels/Unsplash) skip the blob/upload phase entirely: the
  // layer hotlinks the provider's CDN file, and dimensions/duration always
  // come from the search API, so this stays synchronous for them — byte
  // identical to before. Personal-library picks (My library tab) can be
  // legacy rows with no stored width/height, which stockItemToLayerInput
  // surfaces as a null mediaAspect; those are probed the same way
  // handleLayerFile probes a freshly-uploaded file, before computing the
  // fitted rect / window, so a dimensionless library item still lands
  // correctly. No registerUserMedia — nothing was uploaded here.
  const handleAddStockLayer = useCallback(
    async (item: StockMediaItem) => {
      const input = stockItemToLayerInput(item);
      const id = `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const compAspect =
        effectivePreviewAspect === "portrait" ? 1080 / 1920 : 1920 / 1080;
      let mediaAspect = input.mediaAspect;
      let mediaDuration = input.mediaDuration;
      if (mediaAspect === null) {
        if (input.kind === "image") {
          const size = await probeImageSize(input.url);
          if (size.width > 0 && size.height > 0) {
            mediaAspect = size.width / size.height;
          }
        } else {
          const meta = await probeMediaMetadata(input.url, "video");
          if (meta.width > 0 && meta.height > 0) {
            mediaAspect = meta.width / meta.height;
          }
          if (meta.duration) {
            // Recompute the window below with the real probed duration
            // instead of the input's already-defaulted fallback.
            mediaDuration = meta.duration;
          }
        }
      }
      const fitted = (rect: LayerRect) =>
        mediaAspect
          ? fitRectToAspect({ rect, mediaAspect, compAspect })
          : rect;
      if (input.kind === "image") {
        const draft = makeImageLayer({ id, name: input.name, url: input.url, clipDuration });
        addLayer({ ...draft, uploading: false, rect: fitted(draft.rect) });
        return;
      }
      const duration = mediaDuration ?? 5;
      const playhead = videoTimeToClipTime(currentTime, clipIntervals);
      const { start, end } = computeLayerWindow({
        playhead,
        duration,
        clipDuration,
      });
      const draft = makeVideoLayer({
        id, name: input.name, url: input.url, start, end, mediaDuration: duration,
      });
      addLayer({ ...draft, uploading: false, rect: fitted(draft.rect) });
    },
    [addLayer, clipDuration, currentTime, clipIntervals, effectivePreviewAspect],
  );

  // Draft/curated clips can have no persistent id yet. Retain their manual
  // audio flow, but never open/commit against the previous clip's intervals.
  const currentAudioClipKey = selectedClips[0] != null
    && intervalsLoadedForClip === selectedClips[0]
    && (!currentSaveableClipId || clipDataLoadedFor === currentSaveableClipId)
    ? `${selectedClips[0]}:${currentSaveableClipId ?? "draft"}` : null;
  useEffect(() => {
    if (audioPickerClipKey && audioPickerClipKey !== currentAudioClipKey) setAudioPickerClipKey(null);
  }, [audioPickerClipKey, currentAudioClipKey]);

  const handleAddSoundEffect = useCallback((sound: SoundEffectRow) => {
    if (!currentAudioClipKey || audioPickerClipKey !== currentAudioClipKey) return;
    const layer = createSoundEffectLayer({
      id: `layer-${crypto.randomUUID()}`, sound, layers,
      at: videoTimeToClipTime(currentTime, clipIntervals), clipDuration,
    });
    addLayer(layer);
  }, [currentAudioClipKey, audioPickerClipKey, layers, currentTime, clipIntervals, clipDuration, addLayer]);

  const handleAddLayer = useCallback(
    (type: AddableLayerType) => {
      if (type === "text") {
        const playhead = videoTimeToClipTime(currentTime, clipIntervals);
        const { start, end } = computeLayerWindow({
          playhead,
          duration: TEXT_LAYER_DEFAULT_DURATION,
          clipDuration,
        });
        addLayer(
          makeTextLayer({
            id: `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: t("projectEditor.layers.textDefaultName"),
            start,
            end,
          }),
        );
        return;
      }
      if (type === "clip") {
        const playhead = videoTimeToClipTime(currentTime, clipIntervals);
        const { start, end } = computeLayerWindow({
          playhead,
          duration: 5,
          clipDuration,
        });
        addLayer(
          makeClipMirrorLayer({
            id: `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: t("projectEditor.layers.clipMirrorName"),
            start,
            end,
            clipDuration,
          }),
        );
        return;
      }
      if (type === "image") {
        setImageLibraryOpen(true);
        return;
      }
      if (type === "video") {
        setStockDialogKind("video");
        return;
      }
      if (currentAudioClipKey) setAudioPickerClipKey(currentAudioClipKey);
    },
    [addLayer, clipDuration, currentTime, clipIntervals, t, currentAudioClipKey],
  );

  // Which layer (video only) currently has canvas-pan/zoom reframing active.
  // Cleared automatically when the selection moves away or the layer is
  // deleted (deleteLayer clears activeLayerId, so this effect covers both).
  const [reframeLayerId, setReframeLayerId] = useState<string | null>(null);
  useEffect(() => {
    setReframeLayerId((cur) => (cur && cur !== activeLayerId ? null : cur));
  }, [activeLayerId]);
  const handleReframeToggle = useCallback((id: string) => {
    setReframeLayerId((cur) => (cur === id ? null : id));
  }, []);

  // Track latest saved data per clip so switching clips loads the freshest segments
  const clipDataOverridesRef = useRef<Record<string, any>>({});
  // Tracks which clip has already been loaded into local state, so auto-save
  // cache updates (which change `clips` identity) don't re-run the init
  // effect and reset intervals / playback position.
  const loadedClipIdRef = useRef<number | null>(null);
  const handleClipDataSaved = useCallback(
    (savedClipId: string, data: any) => {
      clipDataOverridesRef.current[savedClipId] = {
        ...(clipDataOverridesRef.current[savedClipId] || {}),
        ...data,
      };
      setDirtyClipIds((prev) => {
        if (prev.has(savedClipId)) return prev;
        const next = new Set(prev);
        next.add(savedClipId);
        return next;
      });
      toast({ title: t("projectEditor.clipSaved") });
    },
    [toast, t],
  );

  const { saving: autoSaving, flush: flushSegmentSave } = useAutoSaveClipData({
    clipId: currentSaveableClipId,
    intervals: clipIntervals,
    sceneSequence,
    loadedClipId: clipDataLoadedFor,
    enabled: !!currentSaveableClipId,
    delay: 500,
    onSaved: handleClipDataSaved,
  });

  useAutoSaveCropKeyframes({
    clipId: currentSaveableClipId,
    keyframes: editedCropKeyframes,
    loadedClipId: clipDataLoadedFor,
    enabled: !!currentSaveableClipId,
    delay: 500,
    onSaved: handleClipDataSaved,
  });

  useAutoSaveTransitions({
    clipId: currentSaveableClipId,
    transitions: sceneTransitions,
    loadedClipId: clipDataLoadedFor,
    enabled: !!currentSaveableClipId,
    delay: 500,
    onSaved: handleClipDataSaved,
  });

  const { flush: flushVideoEffects, error: videoEffectsSaveError } = useAutoSaveVideoEffects({
    clipId: currentSaveableClipId, loadedClipId: clipDataLoadedFor,
    loadedRevision: currentSaveableClipId ? clipDataOverridesRef.current[currentSaveableClipId]?.videoEffectsRevision ?? selectedClipData?.data?.videoEffectsRevision : undefined,
    videoEffects, enabled: !!currentSaveableClipId, onSaved: handleClipDataSaved,
  });

  useAutoSaveCoverImage({
    clipId: currentSaveableClipId,
    coverImage,
    loadedClipId: clipDataLoadedFor,
    enabled: !!currentSaveableClipId,
    delay: 500,
    onSaved: handleClipDataSaved,
  });

  useAutoSaveKeyframeTransitions({
    clipId: currentSaveableClipId,
    keyframeTransitions,
    loadedClipId: clipDataLoadedFor,
    enabled: !!currentSaveableClipId,
    delay: 500,
    onSaved: handleClipDataSaved,
  });

  // Also flush pending crop edits synchronously right before each renderV2
  // call, so a user who exports within the auto-save debounce window still
  // gets their latest zoom/crop in the render.
  const [updateClipDataMutation] = useMutation(UPDATE_CLIP_DATA);
  const persistPendingCropEdits = useCallback(
    async (clipId: string | null | undefined) => {
      if (!clipId || !editedCropKeyframes) return;
      const payload = { cropCenterKeyframes: editedCropKeyframes };
      try {
        await updateClipDataMutation({
          variables: { id: clipId, data: payload },
        });
        clipDataOverridesRef.current[clipId] = {
          ...(clipDataOverridesRef.current[clipId] || {}),
          ...payload,
        };
      } catch (err) {
        console.error("Failed to persist crop keyframes before export:", err);
      }
    },
    [editedCropKeyframes, updateClipDataMutation],
  );

  // Same shape as persistPendingCropEdits, for scene transitions.
  const persistPendingTransitions = useCallback(
    async (clipId: string | null | undefined) => {
      if (!clipId) return;
      const payload = { transitions: sceneTransitions };
      try {
        await updateClipDataMutation({ variables: { id: clipId, data: payload } });
        clipDataOverridesRef.current[clipId] = {
          ...(clipDataOverridesRef.current[clipId] || {}),
          ...payload,
        };
      } catch (err) {
        console.error("Failed to persist transitions before export:", err);
      }
    },
    [sceneTransitions, updateClipDataMutation],
  );

  // Same shape, for the cover image. `null` IS written (removal): the backend
  // shallow-merges clip.data, so omitting the key would keep the old cover.
  // Guarded by the load tag so a stale-window cover never lands on another clip.
  const persistPendingCoverImage = useCallback(
    async (clipId: string | null | undefined) => {
      if (!clipId || clipDataLoadedFor !== clipId) return;
      const payload = { coverImage };
      try {
        await updateClipDataMutation({ variables: { id: clipId, data: payload } });
        clipDataOverridesRef.current[clipId] = {
          ...(clipDataOverridesRef.current[clipId] || {}),
          ...payload,
        };
      } catch (err) {
        console.error("Failed to persist cover image before export:", err);
      }
    },
    [coverImage, clipDataLoadedFor, updateClipDataMutation],
  );

  // "Usar frame atual" crops the captured frame around the same reframe
  // center the preview applies at that source time. Same gate as the
  // preview's `cropCenterKeyframes` prop: portrait only, and a speaker layout
  // (without a post layout) replaces the reframe crop, so it stays centered.
  const previewAppliesCropKeyframes =
    effectivePreviewAspect === "portrait" &&
    (postLayoutForPreview ? true : !previewSpeakerLayout);
  const resolveCoverCropFraming = useCallback(
    (sourceTime: number) =>
      previewAppliesCropKeyframes
        ? resolveCropFramingAtTime(effectiveCropKeyframes, sourceTime)
        : null,
    [previewAppliesCropKeyframes, effectiveCropKeyframes],
  );

  const handleCoverImageChange = useCallback(
    (next: ClipCoverImage | null) => {
      pushUndoSnapshot();
      setCoverImage(next);
    },
    [pushUndoSnapshot],
  );

  // Same shape, for keyframe-boundary transitions. Persists the SANITIZED set
  // (effectiveKeyframeTransitions) rather than the raw working state: at
  // export-click time the crop keyframes are stable, so the load-window wipe
  // risk that justifies raw continuous autosave doesn't apply — and the backend
  // has no boundary check, so a stale raw entry (from a deleted/moved keyframe)
  // would render a transform pulse the preview never shows (WYSIWYG break).
  const persistPendingKeyframeTransitions = useCallback(
    async (clipId: string | null | undefined) => {
      if (!clipId) return;
      const payload = { keyframeTransitions: effectiveKeyframeTransitions };
      try {
        await updateClipDataMutation({ variables: { id: clipId, data: payload } });
        clipDataOverridesRef.current[clipId] = {
          ...(clipDataOverridesRef.current[clipId] || {}),
          ...payload,
        };
      } catch (err) {
        console.error("Failed to persist keyframe transitions before export:", err);
      }
    },
    [effectiveKeyframeTransitions, updateClipDataMutation],
  );

  // Segment transcription with the same canonical grouping used by clip cards.
  const segmentGroups = useMemo(
    () => buildClipTranscriptionSegments({
      words,
      intervals: debouncedIntervals,
      projectDuration: project.duration_seconds || Infinity,
    }),
    [words, debouncedIntervals, project.duration_seconds],
  );

  // Memoize the intervals prop for ProjectPreview to avoid new array references on every render
  const previewIntervals = useMemo(() => {
    if (!clipIntervals || clipIntervals.length === 0) return undefined;
    return clipIntervals.map(({ startTime, endTime }) => ({
      startTime,
      endTime,
    }));
  }, [clipIntervals]);

  // Memoize callbacks and style for ProjectPreview to avoid new references on every render
  const handlePreviewPlayStateChange = useCallback((playing: boolean) => {
    setIsPlaying(playing);
    isSeekingRef.current = false;
  }, []);

  const handlePreviewMuteStateChange = useCallback((muted: boolean) => {
    setIsMuted(muted);
  }, []);

  const handlePreviewVolumeChange = useCallback((vol: number) => {
    setVolume(vol);
    if (vol === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  }, []);

  // Recomputed only when layers/clip change — inline computation created
  // a fresh array on every currentTime tick and churned the Player props.
  const layersRenderInput = useMemo(
    () => buildLayersRenderInput(layers, clipDuration, { invalidMotionLabel: t('projectEditor.layers.motionUnavailable') }),
    [layers, clipDuration, t],
  );
  const timelineSceneMarkers = useMemo(
    () =>
      detectionScenes.map((s: any) => ({
        startTime: s.start_time,
        endTime: s.end_time,
      })),
    [detectionScenes],
  );

  const handlePreviewMediaReady = useCallback(() => {
    setPreviewReady(true);
  }, []);

  const handlePreviewSeek = useCallback(
    (time: number) => {
      updateCurrentTime(time);
    },
    [updateCurrentTime],
  );

  const previewStyle = useMemo(
    () => ({
      width: "100%" as const,
      maxHeight: "100%" as const,
    }),
    [],
  );

  // Helper function to format time
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Helper function to check if a segment is currently active
  const isSegmentActive = useCallback(
    (segment: { startTime: number; endTime: number }) => {
      return currentTime >= segment.startTime && currentTime <= segment.endTime;
    },
    [currentTime],
  );

  // Helper function to check if a word is currently active
  const isWordActive = useCallback(
    (word: any) => {
      const wordStartTime =
        word.start || word.start_time || word.startTime || 0;
      const wordEndTime =
        word.end || word.end_time || word.endTime || wordStartTime;
      return currentTime >= wordStartTime && currentTime <= wordEndTime;
    },
    [currentTime],
  );

  const toggleClip = useCallback((clipId: number) => {
    setSelectedClips([clipId]);
  }, []);

  const toggleCheckedClip = useCallback((clipId: number) => {
    setCheckedClips((prev) =>
      prev.includes(clipId)
        ? prev.filter((id) => id !== clipId)
        : [...prev, clipId],
    );
  }, []);

  const selectAllClips = useCallback(() => {
    setCheckedClips(clips.map((c) => c.id));
  }, [clips]);

  const deselectAllClips = useCallback(() => {
    setCheckedClips([]);
  }, []);

  const handleDownloadSRT = () => {
    if (!words || words.length === 0) {
      toast({
        title: t("projectClips.toast.downloadError"),
        description: t("projectClips.toast.noTranscription"),
        variant: "destructive",
      });
      return;
    }

    const srt = generateSRT(words);
    const blob = new Blob([srt], { type: "text/srt;charset=utf-8" });
    const fileName = `${project?.name || "subtitles"}.srt`;
    saveAs(blob, fileName);

    toast({
      title: t("projectClips.toast.srtDownloaded"),
    });
  };

  // Handle export format selection
  const handleExportFormat = useCallback(
    async (format: "vertical" | "horizontal") => {
      try {
        if (!hasClipQuota) {
          showUpgradeToast();
          return;
        }
        // CapCut-style: block export when the clip has layers and user isn't PRO.
        if (!canExportWithLayers({ isPro: hasProFeatures, layerCount: layers.length })) {
          showLayersProRequiredDialog();
          return;
        }
        // Same gate shape for scene + keyframe transitions: free to add/preview, PRO to export.
        if (!canExportWithTransitions({
          isPro: hasProFeatures,
          transitionCount: sceneTransitions.length + effectiveKeyframeTransitions.length,
        })) {
          setTransitionsProDialogOpen(true);
          return;
        }
        // And for timeline effect sections (the whole-clip look stays free).
        if (!canExportWithEffectSections({ isPro: hasProFeatures, effects: videoEffects, clipDuration })) {
          setEffectsProDialogOpen(true);
          return;
        }
        setIsExporting(true);

        // Prepare segments from clipIntervals
        const segments = clipIntervals.map((interval) => ({
          start: interval.startTime,
          end: interval.endTime,
        }));

        // If no intervals are set, use the selected clip's start/end times
        const selectedClipData = clips.find(
          (clip) => clip.id === selectedClips[0],
        );

        if (segments.length === 0) {
          throw new Error("No segments to export");
        }

        // Generate a name for the clip
        const clipName = selectedClipData?.title || project.name;

        // V2 render pipeline: call renderV2 directly for clips that already exist
        const v2ClipId =
          selectedClipData?.skippedClipId || selectedClipData?.activeClipId;
        if (renderV2 && v2ClipId) {
          const input = buildClipRenderInput({
            format,
            subtitlesEnabled,
            renderExtras: buildRenderExtras(),
            brandKitId: selectedBrandKit,
            layout: {
              postLayout,
              fieldBinds: layoutFieldBinds,
              clipTitle: resolveClipTitleForBind(selectedClipData?.title),
              channelName: channelSource?.name,
              channelAvatarUrl: channelSource?.thumbnail,
              userName: myData?.myData?.name ?? undefined,
              userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
              speakerLayout: selectedSpeakerLayout,
              uniqueSpeakerCount: uniqueSpeakers.length,
              fillerPool,
              clipDurationSec: clipDuration,
            },
            layers,
            layerClipDurationSec: clipDuration,
            watermarkPosition,
          });
          // Flush pending edits before render so the worker reads the
          // latest transcription, segments, and crop keyframes — otherwise
          // the rendered subtitles can desync from the user's last edits.
          await Promise.all([
            handleSaveTranscriptionRef.current(),
            flushSegmentSave(),
            persistPendingCropEdits(v2ClipId),
            persistPendingTransitions(v2ClipId),
            persistPendingCoverImage(v2ClipId),
            flushVideoEffects(),
            persistPendingKeyframeTransitions(v2ClipId),
          ]);
          await renderV2({ id: v2ClipId, input });

          toast({
            title: t("projectEditor.exportStarted"),
            description: t("projectEditor.clipProcessing", { clipName }),
            variant: "default",
          });

          navigate(clipsPagePath);

          onExport();
          return;
        }

        let action = "trim";

        if (subtitlesEnabled || format === "vertical") {
          action = "render";
        }

        if (selectedClipData?.skippedClipId) {
          // Clip was skipped — activate it
          await activateClipMutation({
            variables: {
              id: selectedClipData.skippedClipId,
              input: {
                action,
                aspect_ratio_format: format,
                preset: subtitlesEnabled ? null : "no-subtitle",
                brand_kit_id: selectedBrandKit,
              },
            },
            refetchQueries: [
              { query: GET_MY_DATA },
              { query: GET_MY_PROJECT, variables: { id: project.id } },
            ],
          });
        } else if (selectedClipData?.activeClipId) {
          // Clip was already exported — create a new hidden clip for re-export
          await createClip({
            variables: {
              input: {
                action,
                name: clipName,
                aspect_ratio_format: format,
                preset: subtitlesEnabled ? null : "no-subtitle",
                project_id: project.id,
                brand_kit_id: selectedBrandKit,
                segments,
                hide_in_project: true,
              },
            },
            refetchQueries: [{ query: GET_MY_DATA }],
          });
        } else {
          // No clip exists yet — create a new one
          await createClip({
            variables: {
              input: {
                action,
                name: clipName,
                aspect_ratio_format: format,
                preset: subtitlesEnabled ? null : "no-subtitle",
                project_id: project.id,
                brand_kit_id: selectedBrandKit,
                segments,
              },
            },
            refetchQueries: [{ query: GET_MY_DATA }],
          });
        }

        // Show success toast
        toast({
          title: t("projectEditor.exportStarted"),
          description: t("projectEditor.clipProcessing", { clipName }),
          variant: "default",
        });

        navigate(clipsPagePath);

        // Call the original onExport function for any additional logic
        onExport();
      } catch (error) {
        console.error("Error creating clip:", error);

        // Show error toast
        toast({
          title: t("projectEditor.exportError"),
          description:
            error instanceof Error
              ? error.message
              : t("projectEditor.unexpectedError"),
          variant: "destructive",
        });
      } finally {
        setIsExporting(false);
      }
    },
    [
      watermarkPosition,
      buildRenderExtras,
      myData?.myData?.name,
      myData?.myData?.profile_picture,
      createClip,
      activateClipMutation,
      project.id,
      project.name,
      clipIntervals,
      clips,
      selectedClips,
      selectedBrandKit,
      onExport,
      toast,
      hasClipQuota,
      navigate,
      showUpgradeToast,
      subtitlesEnabled,
      renderV2,
      clipsPagePath,
      t,
      persistPendingCropEdits,
      flushSegmentSave,
      selectedSpeakerLayout,
      uniqueSpeakers,
      postLayout,
      hasProFeatures,
      layers,
      clipDuration,
      showLayersProRequiredDialog,
      sceneTransitions,
      persistPendingTransitions,
      persistPendingCoverImage,
      flushVideoEffects,
      videoEffects,
      effectiveKeyframeTransitions,
      persistPendingKeyframeTransitions,
      layoutFieldBinds,
      channelSource,
      fillerPool,
    ],
  );


  // Flush pending edits before a mass-export preview or the batch itself so
  // workers (and the modal) read the latest data. Transcription is
  // project-wide, so flush regardless. Segment and crop edits live on the
  // active clip, so flush only when that clip is part of the batch.
  const flushMassExportPendingEdits = useCallback(async () => {
    const activeClipInBatch =
      !!currentSaveableClipId &&
      checkedClips.some((cid) => {
        const c = clips.find((cc) => cc.id === cid);
        return (
          c &&
          (c.skippedClipId === currentSaveableClipId ||
            c.activeClipId === currentSaveableClipId)
        );
      });

    await handleSaveTranscriptionRef.current();
    await flushVideoEffects();
    if (activeClipInBatch && currentSaveableClipId) {
      await flushSegmentSave();
      if (editedCropKeyframes) {
        await persistPendingCropEdits(currentSaveableClipId);
      }
      await persistPendingTransitions(currentSaveableClipId);
      await persistPendingCoverImage(currentSaveableClipId);
      await persistPendingKeyframeTransitions(currentSaveableClipId);
    }
  }, [
    currentSaveableClipId,
    checkedClips,
    clips,
    flushSegmentSave,
    editedCropKeyframes,
    persistPendingCropEdits,
    persistPendingTransitions,
    persistPendingCoverImage,
    flushVideoEffects,
    persistPendingKeyframeTransitions,
  ]);

  useEffect(() => {
    if (!massExportModalOpen) return;
    void flushMassExportPendingEdits();
  }, [massExportModalOpen, flushMassExportPendingEdits]);

  // Handle mass export of checked clips
  const handleMassExport = useCallback(
    async (format: "vertical" | "horizontal") => {
      try {
        // Bulk export is PRO-only (free users export one clip at a time).
        if (!hasProFeatures) {
          setBulkProDialogOpen(true);
          return;
        }
        if (!hasClipQuota) {
          showUpgradeToast();
          return;
        }
        setIsMassExporting(true);

        let action = "trim";
        if (subtitlesEnabled || format === "vertical") {
          action = "render";
        }

        let successCount = 0;
        let errorCount = 0;

        await flushMassExportPendingEdits();

        for (const clipId of checkedClips) {
          const clipData = clips.find((c) => c.id === clipId);
          if (!clipData) continue;

          // Media layers are stored per-clip (clip.data.editorLayers). A
          // mass-export batch can contain clips other than the one being
          // edited, and layer start/end times are only meaningful relative
          // to that clip's own duration, so each clip uses its own saved
          // layers here — except the currently-open clip, which uses the
          // live in-editor `layers` state (freshest; persistence to
          // clip.data.editorLayers is debounced and may lag a fresh edit).
          const clipLayerList = (
            clipId === selectedClips[0]
              ? layers
              : (((clipData as any).data?.editorLayers ?? []) as MediaLayer[]).map(normalizeLayer)
          );
          if (
            !canExportWithLayers({
              isPro: hasProFeatures,
              layerCount: clipLayerList.length,
            })
          ) {
            errorCount++;
            continue;
          }

          // Same per-clip pattern for scene transitions: the currently-open
          // clip uses the live `sceneTransitions` state (freshest); other
          // clips use their own persisted clip.data.transitions. Keyframe
          // transitions add to the same PRO gate count (other clips: count the
          // persisted array, since their crop boundaries aren't loaded here).
          const clipTransitionCount =
            clipId === selectedClips[0]
              ? sceneTransitions.length + effectiveKeyframeTransitions.length
              : sanitizeTransitions(
                  (clipData as any).data?.transitions,
                  (clipData as any).data?.segments?.length ?? 1,
                ).length +
                (Array.isArray((clipData as any).data?.keyframeTransitions)
                  ? (clipData as any).data.keyframeTransitions.length
                  : 0);
          if (
            !canExportWithTransitions({
              isPro: hasProFeatures,
              transitionCount: clipTransitionCount,
            })
          ) {
            errorCount++;
            continue;
          }

          // Timeline effect sections, same per-clip pattern: the open clip uses
          // the live state; other clips use this session's draft (keyed by
          // saveable id, like the mass-export preview) or their persisted data.
          const clipEffects =
            clipId === selectedClips[0]
              ? videoEffects
              : videoEffectsDraftsRef.current.get(clipData.skippedClipId || clipData.activeClipId || '') ??
                normalizeVideoEffects((clipData as any).data?.videoEffects);
          // This clip's own duration (also what its layers are clamped to);
          // Infinity when unknown, which skips the gate's past-the-end filter.
          const clipOwnDuration =
            typeof clipData.clip_start_time === "number" &&
            typeof clipData.clip_end_time === "number"
              ? clipData.clip_end_time - clipData.clip_start_time
              : Infinity;
          if (!canExportWithEffectSections({
            isPro: hasProFeatures,
            effects: clipEffects,
            // The open clip uses the live interval sum, like its live effects.
            clipDuration: clipId === selectedClips[0] ? clipDuration : clipOwnDuration,
          })) {
            errorCount++;
            continue;
          }

          try {
            // V2 render pipeline
            const v2ClipId = clipData.skippedClipId || clipData.activeClipId;
            if (renderV2 && v2ClipId) {
              const input = buildClipRenderInput({
                format,
                subtitlesEnabled,
                renderExtras: buildRenderExtras(),
                brandKitId: selectedBrandKit,
                layout: {
                  postLayout,
                  fieldBinds: layoutFieldBinds,
                  clipTitle: resolveClipTitleForBind(clipData.title),
                  channelName: channelSource?.name,
                  channelAvatarUrl: channelSource?.thumbnail,
                  userName: myData?.myData?.name ?? undefined,
                  userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
                  speakerLayout: selectedSpeakerLayout,
                  uniqueSpeakerCount: uniqueSpeakers.length,
                  fillerPool,
                  clipDurationSec: Number.isFinite(clipOwnDuration) ? clipOwnDuration : clipDuration,
                },
                layers: clipLayerList,
                layerClipDurationSec: clipOwnDuration,
                watermarkPosition,
              });
              await renderV2({ id: v2ClipId, input });
              successCount++;
              continue;
            }

            if (clipData.skippedClipId) {
              // Clip was skipped — activate it
              await activateClipMutation({
                variables: {
                  id: clipData.skippedClipId,
                  input: {
                    action,
                    aspect_ratio_format: format,
                    preset: subtitlesEnabled ? null : "no-subtitle",
                    brand_kit_id: selectedBrandKit,
                  },
                },
              });
            } else if (clipData.activeClipId) {
              // Clip was already exported — create a new hidden clip for re-export
              const segments = clipTimeSegments(clipData);
              if (segments.length === 0) continue;

              await createClip({
                variables: {
                  input: {
                    action,
                    name: clipData.title || project.name,
                    aspect_ratio_format: format,
                    preset: subtitlesEnabled ? null : "no-subtitle",
                    project_id: project.id,
                    brand_kit_id: selectedBrandKit,
                    segments,
                    hide_in_project: true,
                  },
                },
              });
            } else {
              // No clip record yet (curated-only or fallback clip) — create
              // it, like the single export does. Counting it as a success
              // without a request told the user a render started that never
              // did, and nothing showed up on the clips page.
              const segments = clipTimeSegments(clipData);
              if (segments.length === 0) {
                errorCount++;
                continue;
              }

              await createClip({
                variables: {
                  input: {
                    action,
                    name: clipData.title || project.name,
                    aspect_ratio_format: format,
                    preset: subtitlesEnabled ? null : "no-subtitle",
                    project_id: project.id,
                    brand_kit_id: selectedBrandKit,
                    segments,
                  },
                },
              });
            }
            successCount++;
          } catch (err) {
            console.error(`Error exporting clip ${clipId}:`, err);
            errorCount++;
          }
        }

        if (errorCount > 0 && successCount > 0) {
          toast({
            title: t("projectEditor.massExportStarted"),
            description: t("projectEditor.massExportPartial", {
              success: successCount,
              errors: errorCount,
            }),
            variant: "default",
          });
        } else if (errorCount > 0) {
          toast({
            title: t("projectEditor.massExportError"),
            description: t("projectEditor.massExportPartial", {
              success: successCount,
              errors: errorCount,
            }),
            variant: "destructive",
          });
        } else {
          toast({
            title: t("projectEditor.massExportStarted"),
            description: t("projectEditor.massExportProcessing", {
              success: successCount,
            }),
            variant: "default",
          });
        }

        setCheckedClips([]);
        navigate(clipsPagePath);
      } catch (error) {
        console.error("Error in mass export:", error);
        toast({
          title: t("projectEditor.massExportError"),
          description:
            error instanceof Error
              ? error.message
              : t("projectEditor.unexpectedError"),
          variant: "destructive",
        });
      } finally {
        setIsMassExporting(false);
      }
    },
    [
      watermarkPosition,
      buildRenderExtras,
      myData?.myData?.name,
      myData?.myData?.profile_picture,
      checkedClips,
      clips,
      createClip,
      activateClipMutation,
      project.id,
      project.name,
      selectedBrandKit,
      subtitlesEnabled,
      hasClipQuota,
      showUpgradeToast,
      renderV2,
      clipsPagePath,
      toast,
      navigate,
      t,
      editedCropKeyframes,
      currentSaveableClipId,
      persistPendingCropEdits,
      flushSegmentSave,
      flushMassExportPendingEdits,
      hasProFeatures,
      selectedClips,
      layers,
      sceneTransitions,
      videoEffects,
      persistPendingTransitions,
      persistPendingCoverImage,
      effectiveKeyframeTransitions,
      persistPendingKeyframeTransitions,
      layoutFieldBinds,
      channelSource,
      selectedSpeakerLayout,
      uniqueSpeakers,
      postLayout,
      fillerPool,
      clipDuration,
    ],
  );

  // Shared seeking function to ensure consistent behavior across Timeline and transcription
  const handleSeekToTime = useCallback(
    (time: number, opts?: { scrub?: boolean }) => {
      const selectedClipData = clips.find(
        (clip) => clip.id === selectedClips[0],
      );
      if (!selectedClipData) return;

      // Jump seeks serialize so pause/resume cannot overlap. Scrub already
      // arrives at most once per frame from the timeline and must not drop
      // or pause/play the Remotion player on every tick.
      if (!opts?.scrub && isVideoLocked && isSeekingRef.current) return;

      const fps = videoPlayerRef.current?.getFps() ?? 30;

      // Paused unlocked navigation uses source frames. During playback even
      // an unlocked padlock uses the edited clip's output axis.
      if (!isPreviewVideoLocked) {
        const clamped = Math.max(
          0,
          Math.min(time, project.duration_seconds || 0),
        );
        const foundFrame = Math.round(clamped * fps);
        lastSeekTimeRef.current = Date.now();
        updateCurrentTime(clamped);
        videoPlayerRef.current?.seekTo(foundFrame);
        return;
      }

      // When video is locked, use interval-based seeking (original logic)
      const intervals =
        clipIntervals && clipIntervals.length > 0
          ? clipIntervals
          : typeof selectedClipData.clip_start_time === "number" &&
              typeof selectedClipData.clip_end_time === "number"
            ? [
                {
                  id: selectedClipData.id,
                  startTime: selectedClipData.clip_start_time,
                  endTime: selectedClipData.clip_end_time,
                },
              ]
            : [];

      if (intervals.length === 0) return;

      // Clamp target time to overall covered range
      const minStart = Math.min(...intervals.map((i) => i.startTime));
      const maxEnd = Math.max(...intervals.map((i) => i.endTime));
      const clamped = Math.max(minStart, Math.min(time, maxEnd));

      // Map absolute time -> comp frame (overlap-aware; matches the preview's
      // sequence placement exactly)
      const { blocks } = buildSceneLayout({
        scenes: intervals,
        transitions: sceneTransitions,
        fps,
      });
      const foundFrame = Math.round(
        sourceTimeToOutputTime(intervals, blocks, clamped, fps) * fps,
      );

      lastSeekTimeRef.current = Date.now();
      updateCurrentTime(clamped);
      videoPlayerRef.current?.seekTo(foundFrame);
      if (opts?.scrub || !isVideoLocked) return;

      isSeekingRef.current = true;
      wasPlayingBeforeSeekRef.current = isPlaying;
      if (isPlaying) {
        videoPlayerRef.current?.pause();
        setIsPlaying(false);
      }
      setTimeout(() => {
        isSeekingRef.current = false;
        if (wasPlayingBeforeSeekRef.current) {
          videoPlayerRef.current?.play();
          setIsPlaying(true);
        }
      }, 100);
    },
    [
      clips,
      selectedClips,
      clipIntervals,
      isPlaying,
      isVideoLocked,
      isPreviewVideoLocked,
      project.duration_seconds,
      sceneTransitions,
    ],
  );

  const timelineSelectedClip = useMemo(() => {
    const selectedClipData = clips.find((clip) => clip.id === selectedClips[0]);
    if (
      selectedClipData &&
      selectedClipData.clip_start_time !== undefined &&
      selectedClipData.clip_end_time !== undefined
    ) {
      return {
        id: selectedClipData.id,
        startTime: selectedClipData.clip_start_time,
        endTime: selectedClipData.clip_end_time,
        title: selectedClipData.title,
      };
    }
    return null;
  }, [clips, selectedClips]);

  // The interval state still holds the previous clip's intervals until the
  // clip-init effect loads the new one.
  const clipIntervalsLoaded = intervalsLoadedForClip != null && intervalsLoadedForClip === selectedClips[0];

  // The editor's Timeline moves the playhead to a newly selected clip; AI
  // Studio has no timeline, so it runs the same rule here.
  usePlayheadInit({
    intervals: naturalTimeline.intervals,
    selectedClip: timelineSelectedClip,
    onTimeChange: updateCurrentTime,
    onSeekRequest: handleSeekToTime,
    enabled: variant === "studio",
    intervalsLoaded: clipIntervalsLoaded,
  });

  const { enabled: hasAiAssistant } = useAiAssistantAccess();
  const handleOpenAiStudio = useCallback(() => {
    navigate(aiStudioPathFor({
      kind: isLibrary ? "library" : "project",
      id: project.id,
      clipId: currentSaveableClipId,
    }));
  }, [navigate, isLibrary, project.id, currentSaveableClipId]);

  // Time-based skip amount in seconds
  const SKIP_AMOUNT_SECONDS = 10;

  const playbackNavigation = useMemo(() => {
    if (!isPreviewVideoLocked || !clipIntervals.length) {
      return { position: currentTime, duration: project.duration_seconds || 0, sourceAt: (time: number) => time };
    }
    const fps = videoPlayerRef.current?.getFps() ?? 30;
    const layout = buildSceneLayout({ scenes: clipIntervals, transitions: sceneTransitions, fps });
    return {
      position: sourceTimeToOutputTime(clipIntervals, layout.blocks, currentTime, fps),
      duration: layout.totalDurationInFrames / fps,
      sourceAt: (time: number) => outputFrameToSourceFrame(layout.blocks, time * fps) / fps,
    };
  }, [isPreviewVideoLocked, clipIntervals, sceneTransitions, currentTime, project.duration_seconds]);

  const skipBackward = useCallback(() => {
    handleSeekToTime(playbackNavigation.sourceAt(Math.max(0, playbackNavigation.position - SKIP_AMOUNT_SECONDS)));
  }, [playbackNavigation, handleSeekToTime]);
  const skipForward = useCallback(() => {
    handleSeekToTime(playbackNavigation.sourceAt(Math.min(playbackNavigation.duration, playbackNavigation.position + SKIP_AMOUNT_SECONDS)));
  }, [playbackNavigation, handleSeekToTime]);
  const canSkipBackward = playbackNavigation.position > 0;
  const canSkipForward = playbackNavigation.position < playbackNavigation.duration;

  // Initialize/reset intervals when selected clip changes. Intentionally
  // gated by loadedClipIdRef so cache updates from auto-save (which change
  // `clips` identity) don't re-run the init and reset intervals/playback.
  useEffect(() => {
    const activeClipId = selectedClips[0];
    const selectedClipData = clips.find((c) => c.id === activeClipId);
    if (!activeClipId || !selectedClipData) return;
    if (loadedClipIdRef.current === activeClipId) return;
    loadedClipIdRef.current = activeClipId;

    const title =
      typeof selectedClipData.title === "string"
        ? selectedClipData.title
        : selectedClipData.title?.text || "Intervalo";

    // Use locally saved data first (freshest), then fall back to query data
    const saveableId =
      selectedClipData.skippedClipId || selectedClipData.activeClipId;
    const overrideData = saveableId
      ? clipDataOverridesRef.current[saveableId]
      : null;
    const resolvedPreview = resolvePersistedClipPreviewForClip({
      clip: selectedClipData,
      clipDataOverride: overrideData,
      sceneDetection,
      isPortrait: effectivePreviewAspect === "portrait",
      projectDuration: project?.duration_seconds,
    });

    // Everything set below belongs to this clip — tag it so the auto-save
    // hooks know when the loaded data has actually arrived.
    setClipDataLoadedFor(saveableId ?? null);
    setIntervalsLoadedForClip(activeClipId);

    setSceneSequence((overrideData?.timelineSceneSequence ?? selectedClipData.data?.timelineSceneSequence) === true);
    setSceneTransitions(resolvedPreview.transitions);
    videoEffectsLoadedIdRef.current = saveableId ?? null;
    setVideoEffects(videoEffectsDraftsRef.current.get(saveableId ?? '') ?? normalizeVideoEffects(overrideData?.videoEffects ?? selectedClipData.data?.videoEffects));
    setEffectsPreview(null);
    setActiveEffectSectionId(null);
    setCoverImage(
      resolveClipCoverImage({ clipData: selectedClipData.data, override: overrideData }),
    );
    // Keep the resolver's raw persisted values until async detection-derived
    // crop boundaries are available; effectiveKeyframeTransitions performs
    // the same final reconciliation used by route-level previews.
    setKeyframeTransitions(resolvedPreview.rawKeyframeTransitions);
    setEditedCropKeyframes(
      resolvedPreview.hasSavedCropOverride
        ? resolvedPreview.cropCenterKeyframes
        : null,
    );
    setClipIntervals(
      resolvedPreview.intervals.map((interval, idx) => ({
        id: Date.now() + idx,
        startTime: interval.startTime,
        endTime: interval.endTime,
        title,
      })),
    );
  }, [
    selectedClips,
    clips,
    sceneDetection,
    effectivePreviewAspect,
    project?.duration_seconds,
  ]);

  // Whether any checked clip (for mass export) would be blocked by the
  // layers PRO gate. The currently-open clip uses the live in-editor
  // `layers` state (freshest); other clips use their own persisted
  // clip.data.editorLayers.
  const checkedClipsHaveLayers = useMemo(() => {
    return checkedClips.some((cid) => {
      if (cid === selectedClips[0]) return layers.length > 0;
      const c = clips.find((cc) => cc.id === cid);
      return (((c as any)?.data?.editorLayers ?? []) as MediaLayer[]).length > 0;
    });
  }, [checkedClips, clips, selectedClips, layers]);
  const canExportSelectedClipLayers = canExportWithLayers({
    isPro: hasProFeatures,
    layerCount: layers.length,
  });
  const canExportCheckedClipsLayers = canExportWithLayers({
    isPro: hasProFeatures,
    layerCount: checkedClipsHaveLayers ? 1 : 0,
  });

  const massExportClips = useMemo(
    () =>
      checkedClips
        .map((id) => clips.find((clip) => clip.id === id))
        .filter((clip): clip is (typeof clips)[number] => !!clip),
    [checkedClips, clips],
  );
  // The modal marks clips that have a cover. Overlay each clip's freshest
  // cover — the live editor state for the open clip, this session's saved
  // override for the others — onto the fetched data.
  const massExportModalClips = useMemo(
    () =>
      massExportClips.map((clip) => {
        const saveableId = clip.skippedClipId || clip.activeClipId;
        const cover =
          saveableId && saveableId === clipDataLoadedFor
            ? coverImage
            : resolveClipCoverImage({
                clipData: clip.data,
                override: saveableId ? clipDataOverridesRef.current[saveableId] : undefined,
              });
        return { ...clip, data: { ...(clip.data || {}), coverImage: cover } };
      }),
    [massExportClips, clipDataLoadedFor, coverImage],
  );
  const massExportPreviewClip =
    massExportClips.find((clip) => clip.id === massExportFocusedClipId) ??
    massExportClips[0] ??
    null;
  const resolvedMassExportFormat = layoutExportFormats.includes(massExportFormat)
    ? massExportFormat
    : (layoutExportFormats[0] ?? "vertical");
  const massExportPreviewAspect =
    resolvedMassExportFormat === "vertical" ? "portrait" : "landscape";
  const massExportPreviewInputs = useMemo(() => {
    if (!massExportPreviewClip) return null;
    const boundLayout = postLayoutForPreview
      ? applyLayoutBinds(postLayoutForPreview, layoutFieldBinds, {
          clipTitle: resolveClipTitleForBind(massExportPreviewClip.title),
          channelName: channelSource?.name,
          channelAvatarUrl: channelSource?.thumbnail,
          userName: myData?.myData?.name ?? undefined,
          userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
        })
      : null;
    const previewPostLayout = resolveEditorPreviewPostLayout({
      postLayout: boundLayout,
      effectiveAspectRatio: massExportPreviewAspect,
    });
    const isEditorClip = massExportPreviewClip.id === selectedClips[0];
    if (isEditorClip) {
      return {
        startTime: massExportPreviewClip.clip_start_time || 0,
        endTime: massExportPreviewClip.clip_end_time || 30,
        intervals: previewIntervals,
        transitions: sceneTransitions,
        videoEffects,
        keyframeTransitions: previewKeyframeTransitions,
        cropCenterKeyframes:
          massExportPreviewAspect === "portrait" &&
          (previewPostLayout ? true : !previewSpeakerLayout)
            ? effectiveCropKeyframes
            : undefined,
        speakerLayout:
          !previewPostLayout && massExportPreviewAspect === "portrait"
            ? previewSpeakerLayout
            : undefined,
        postLayout: previewPostLayout,
        layers: layersRenderInput,
        transcriptionSegments: segmentGroups,
      };
    }
    const resolved = resolvePersistedClipPreviewForClip({
      clip: massExportPreviewClip,
      sceneDetection,
      isPortrait: massExportPreviewAspect === "portrait",
      projectDuration: project?.duration_seconds,
    });
    return {
      startTime:
        resolved.startTime ?? massExportPreviewClip.clip_start_time ?? 0,
      endTime: resolved.endTime ?? massExportPreviewClip.clip_end_time ?? 30,
      intervals: resolved.intervals.length > 0 ? resolved.intervals : undefined,
      transitions: resolved.transitions,
      videoEffects: videoEffectsDraftsRef.current.get(massExportPreviewClip.skippedClipId || massExportPreviewClip.activeClipId || '') ?? normalizeVideoEffects(massExportPreviewClip.data?.videoEffects),
      keyframeTransitions: resolved.keyframeTransitions,
      cropCenterKeyframes:
        massExportPreviewAspect === "portrait" &&
        (previewPostLayout ? true : !previewSpeakerLayout)
          ? resolved.cropCenterKeyframes
          : undefined,
      speakerLayout:
        !previewPostLayout && massExportPreviewAspect === "portrait"
          ? previewSpeakerLayout
          : undefined,
      postLayout: previewPostLayout,
      layers: resolved.layers,
      transcriptionSegments: buildClipTranscriptionSegments({
        words,
        intervals: resolved.intervals,
        projectDuration: project.duration_seconds || Infinity,
      }),
    };
  }, [
    myData?.myData?.name,
    myData?.myData?.profile_picture,
    videoEffects,
    massExportPreviewClip,
    postLayoutForPreview,
    layoutFieldBinds,
    channelSource,
    massExportPreviewAspect,
    selectedClips,
    previewIntervals,
    sceneTransitions,
    previewKeyframeTransitions,
    previewSpeakerLayout,
    effectiveCropKeyframes,
    layersRenderInput,
    segmentGroups,
    sceneDetection,
    project?.duration_seconds,
    words,
  ]);

  // The AI assistant operates this editor through the same commit paths as
  // the controls (see useEditorAssistantBridge).
  const selectedClipIndex = clips.findIndex((clip) => clip.id === selectedClips[0]);
  const { searchMedia, trackStockUse } = useAssistantMediaSearch();
  const { captureFrames, captureCurrentFrame, captureCover } = useEditorFrameCapture({
    previewRef: videoPlayerRef,
    videoUrl: coverCaptureVideoUrl,
    exportAspect: effectivePreviewAspect === "portrait" ? 9 / 16 : 16 / 9,
    resolveCropFraming: resolveCoverCropFraming,
    intervals: clipIntervals,
    transitions: sceneTransitions,
    locked: isPreviewVideoLocked,
    fullDuration: project.duration_seconds || 0,
    clipDuration,
    playhead: currentClipTime,
  });
  useEditorAssistantBridge({
    ready: !!currentSaveableClipId && clipDataLoadedFor === currentSaveableClipId,
    clipKey: currentSaveableClipId,
    clipTitle: clips[selectedClipIndex]?.title ?? null,
    clipIndex: selectedClipIndex + 1,
    clipTitles: clips.map((clip) => clip.title ?? null),
    clipDuration,
    playhead: currentClipTime,
    aspect: effectivePreviewAspect,
    words,
    intervals: clipIntervals,
    scenes: naturalTimeline.intervals,
    sourceDuration: project.duration_seconds || 0,
    cropKeyframes: effectiveCropKeyframes,
    keyframeTransitions: effectiveKeyframeTransitions,
    framingAvailable: previewAppliesCropKeyframes,
    cover: coverImage,
    layoutEnforced: !!effectivePostLayout,
    subtitle: subtitleData,
    subtitlesEnabled,
    fontId: selectedSubtitleFontId,
    fonts: fontsData?.myFonts ?? [],
    kits: brandKitsData?.myBrandKits ?? [],
    selectedKitId: selectedBrandKit,
    hasProFeatures,
    layers,
    videoEffects,
    transitions: naturalTimeline.transitions,
    postLayout,
    layoutFieldBinds,
    setSubtitleData,
    setFontId: (fontId) => {
      setSelectedSubtitleFontId(fontId);
      loadFont(fontId);
    },
    setSubtitlesEnabled,
    commitLayers,
    // Assistant batches stay out of the editor history: they are undone as
    // one batch through the assistant (the chat bar and AI Studio's Undo).
    commitVideoEffects: applyVideoEffects,
    commitTransitions: (next) => applyNaturalTimeline(naturalTimeline.intervals, next),
    applyTemplate: (kitId) => handleBrandKitChange(kitId),
    restoreTemplate: ({ kitId, postLayout: previousLayout, fieldBinds }) => {
      kitPrefillGateRef.current = { ...kitPrefillGateRef.current, prefilledKitId: kitId };
      setSelectedBrandKit(kitId);
      setPostLayout(previousLayout);
      setLayoutFieldBinds(fieldBinds as LayoutFieldBinds);
    },
    seekClipTime: (clipTime, play) => {
      handleSeekToTime(clipTimeToVideoTime(clipTime, clipIntervals));
      if (play) {
        setTimeout(() => {
          videoPlayerRef.current?.play();
          setIsPlaying(true);
        }, 200);
      }
    },
    selectClip: (index) => {
      const clip = clips[index - 1];
      if (clip) setSelectedClips([clip.id]);
    },
    captureFrames,
    captureCurrentFrame,
    captureCover,
    // The assistant's own commits: no editor-history entries (its batches
    // are undone as one through the assistant).
    timelineSnapshot: () => ({
      clipIntervals: clipIntervalsRef.current,
      sceneTransitions: sceneTransitionsRef.current,
      keyframeTransitions: keyframeTransitionsRef.current,
      sceneSequence: sceneSequenceRef.current,
      editedCropKeyframes: editedCropKeyframesRef.current,
    }),
    restoreTimeline: (snapshot) => {
      const saved = snapshot as {
        clipIntervals: TimelineInterval[];
        sceneTransitions: SceneTransition[];
        keyframeTransitions: KeyframeTransition[];
        sceneSequence: boolean;
        editedCropKeyframes: CropCenterKeyframe[] | null;
      };
      setClipIntervals(saved.clipIntervals);
      setSceneTransitions(saved.sceneTransitions);
      setKeyframeTransitions(saved.keyframeTransitions);
      setSceneSequence(saved.sceneSequence);
      setEditedCropKeyframes(saved.editedCropKeyframes);
    },
    commitTimeline: ({ scenes, transitions, cropKeyframes }) => {
      applyNaturalTimeline(scenes, transitions);
      if (cropKeyframes.length) setEditedCropKeyframes(cropKeyframes);
    },
    setEditedCropKeyframes,
    setKeyframeTransitions,
    layoutSnapshot: () => ({
      postLayout,
      fieldBinds: layoutFieldBinds,
      speakerLayout: selectedSpeakerLayout,
      previewAspect: previewAspectRatio,
      subtitle: subtitleData,
      fontId: selectedSubtitleFontId,
      drift: subtitleDriftsFromKit,
    }),
    restoreLayout: (snapshot) => {
      const saved = snapshot as {
        postLayout: PostLayoutConfig | null;
        fieldBinds: LayoutFieldBinds;
        speakerLayout: number;
        previewAspect: typeof previewAspectRatio;
        subtitle: typeof subtitleData;
        fontId: string;
        drift: boolean;
      };
      setPostLayout(saved.postLayout);
      setLayoutFieldBinds(saved.fieldBinds);
      if (saved.speakerLayout !== selectedSpeakerLayout) persistSpeakerLayout(saved.speakerLayout);
      setPreviewAspectRatio(saved.previewAspect);
      setSubtitleData(saved.subtitle);
      setSelectedSubtitleFontId(saved.fontId);
      setSubtitleDriftsFromKit(saved.drift);
    },
    selectLayout: (kind, preset) => {
      selectPostLayout(kind as keyof typeof POST_LAYOUTS, preset ? { preset } : undefined);
    },
    setLayoutField: (key, value) => {
      updatePostLayoutField(key, value);
      // An explicit value replaces a bind (e.g. headline ← clip title), as
      // turning the panel's bind toggle off does; otherwise it never shows.
      setLayoutFieldBinds((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    clearLayout: switchToSingleLayout,
    setCover: setCoverImage,
    searchMedia,
    trackStockUse,
  });

  // One preview element and one top bar, shared by the editor and AI Studio
  // layouts (only one renders, so the player ref attaches once).
  const selectedClipForPreview = clips.find((clip) => clip.id === selectedClips[0]);
  const previewPostLayoutForRender = resolveEditorPreviewPostLayout({
    postLayout: postLayoutForPreview,
    effectiveAspectRatio: effectivePreviewAspect,
  });
  const previewPlayer = selectedClipForPreview && videoUrl ? (
    <ProjectPreview
      fillerPreviewSources={fillerPreviewSources}
      ref={videoPlayerRef}
      videoUrl={videoUrl}
      startTime={selectedClipForPreview.clip_start_time || 0}
      endTime={selectedClipForPreview.clip_end_time || 30}
      intervals={previewIntervals}
      videoEffects={effectsPreview ?? videoEffects}
      transitions={sceneTransitions}
      keyframeTransitions={previewKeyframeTransitions}
      width={
        effectivePreviewAspect === "portrait" ? 1080 : 1920
      }
      height={
        effectivePreviewAspect === "portrait" ? 1920 : 1080
      }
      autoPlay={false}
      loop={true}
      cropCenterKeyframes={
        // Post layouts also receive the keyframes now:
        // the reframe crop flows into the layout's clip
        // cell via ClipVideoSequences. A post layout
        // overrides the speaker layout in the preview,
        // so the speaker gate only applies without one.
        effectivePreviewAspect === "portrait" &&
        (previewPostLayoutForRender ? true : !previewSpeakerLayout)
          ? effectiveCropKeyframes
          : undefined
      }
      speakerLayout={
        !previewPostLayoutForRender && effectivePreviewAspect === "portrait"
          ? previewSpeakerLayout
          : undefined
      }
      postLayout={previewPostLayoutForRender}
      posterUrl={project.thumbnail_url || undefined}
      onPlayStateChange={handlePreviewPlayStateChange}
      onMuteStateChange={handlePreviewMuteStateChange}
      onVolumeChange={handlePreviewVolumeChange}
      onSeek={handlePreviewSeek}
      onPreviewMediaReady={handlePreviewMediaReady}
      style={previewStyle}
      subtitlesEnabled={subtitlesEnabled}
      transcriptionSegments={segmentGroups}
      subtitleConfig={subtitlePreviewConfig}
      logoConfig={brandKitLogoConfig}
      isVideoLocked={isPreviewVideoLocked}
      fullVideoDuration={project.duration_seconds}
      showVolumeControls={false}
      // AI Studio draws its own controls under the player.
      showPlayerControls={variant !== "studio"}
      projectDocumentId={isLibrary ? undefined : project?.id}
      libraryDocumentId={isLibrary ? project?.id : undefined}
      layers={layersRenderInput}
      showWatermark={showWatermark}
      watermarkPosition={watermarkPosition}
    />
  ) : null;

  const topBar = (
    <EditorTopBar
      topBarRef={topBarRef}
      projectName={project.name}
      hasClipQuota={hasClipQuota}
      quotaPressure={quotaPressure}
      autoSaving={autoSaving}
      canClipVideos={permissions.canClipVideos}
      onOpenAiStudio={variant !== "studio" && hasAiAssistant ? handleOpenAiStudio : undefined}
      checkedCount={checkedClips.length}
      hasProFeatures={hasProFeatures}
      canExportCheckedClipsLayers={canExportCheckedClipsLayers}
      canExportSelectedClipLayers={canExportSelectedClipLayers}
      onBulkProGate={() => setBulkProDialogOpen(true)}
      onQuotaBlocked={showUpgradeToast}
      onLayersProGate={showLayersProRequiredDialog}
      availableFormats={layoutExportFormats}
      effectivePreviewAspect={effectivePreviewAspect}
      onOpenMassExportModal={() => {
        setMassExportFocusedClipId(checkedClips[0] ?? null);
        setMassExportFormat(resolveExportFormat({
          previewAspect: effectivePreviewAspect,
          availableFormats: layoutExportFormats,
        }));
        setMassExportModalOpen(true);
      }}
      onSingleExport={(format) => handleExportFormat(format)}
      isMassExporting={isMassExporting}
      isExporting={isExporting}
      createClipLoading={createClipLoading}
      activateClipLoading={activateClipLoading}
      isInvalidDuration={isInvalidDuration}
      durationErrorMessage={durationErrorMessage}
    />
  );

  // State-driven audio, export, PRO and confirmation dialogs in both layouts.
  const editorDialogs = (
    <>
      <input
        ref={layerAudioInputRef}
        data-testid="layer-audio-input"
        type="file"
        accept={layerTypeConfig.audio.acceptMimeTypes.join(",")}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleLayerFile(f, "audio");
          e.target.value = "";
        }}
      />
      {audioPickerClipKey && audioPickerClipKey === currentAudioClipKey && (
        <SoundEffectsBrowseDialog
          key={audioPickerClipKey}
          open
          onOpenChange={(open) => { if (!open) setAudioPickerClipKey(null); }}
          onPick={handleAddSoundEffect}
          onUploadInstead={() => layerAudioInputRef.current?.click()}
          onPreviewStart={() => videoPlayerRef.current?.pause()}
        />
      )}

      {/* WYSIWYG mass-export preview. Single-clip export uses the format
          currently being previewed (resolveExportFormat) and never asks. */}
      <MassExportPreviewModal
        isOpen={massExportModalOpen}
        onClose={() => setMassExportModalOpen(false)}
        clips={massExportModalClips}
        focusedClipId={massExportPreviewClip?.id ?? massExportClips[0]?.id ?? 0}
        onFocusClip={(id) => setMassExportFocusedClipId(Number(id))}
        postLayout={postLayoutForPreview}
        fieldBinds={layoutFieldBinds}
        channelName={channelSource?.name}
        channelAvatarUrl={channelSource?.thumbnail}
        userName={myData?.myData?.name ?? undefined}
        userAvatarUrl={myData?.myData?.profile_picture ?? undefined}
        availableFormats={layoutExportFormats}
        selectedFormat={resolvedMassExportFormat}
        onFormatChange={(format) => {
          if (format === "horizontal") {
            requestLandscapePreview();
            return;
          }
          setMassExportFormat(format);
        }}
        livePreview={
          massExportModalOpen && videoUrl && massExportPreviewInputs ? (
            <ProjectPreview
                          fillerPreviewSources={fillerPreviewSources}
              videoUrl={videoUrl}
              startTime={massExportPreviewInputs.startTime}
              endTime={massExportPreviewInputs.endTime}
              intervals={massExportPreviewInputs.intervals}
              videoEffects={massExportPreviewInputs.videoEffects}
              transitions={massExportPreviewInputs.transitions}
              keyframeTransitions={massExportPreviewInputs.keyframeTransitions}
              width={massExportPreviewAspect === "portrait" ? 1080 : 1920}
              height={massExportPreviewAspect === "portrait" ? 1920 : 1080}
              autoPlay={false}
              loop
              cropCenterKeyframes={massExportPreviewInputs.cropCenterKeyframes}
              speakerLayout={massExportPreviewInputs.speakerLayout}
              postLayout={massExportPreviewInputs.postLayout}
              posterUrl={project.thumbnail_url || undefined}
              style={previewStyle}
              subtitlesEnabled={subtitlesEnabled}
              transcriptionSegments={massExportPreviewInputs.transcriptionSegments}
              subtitleConfig={subtitlePreviewConfig}
              logoConfig={brandKitLogoConfig}
              isVideoLocked
              fullVideoDuration={project.duration_seconds}
              showVolumeControls={false}
              projectDocumentId={isLibrary ? undefined : project?.id}
              libraryDocumentId={isLibrary ? project?.id : undefined}
              layers={massExportPreviewInputs.layers}
            />
          ) : null
        }
        onExport={(format) => {
          setMassExportModalOpen(false);
          handleMassExport(format);
        }}
      />

      {quotaUpsellDialog}

      {/* Free user clicked Export on a clip with media layers (PRO on export). */}
      <LayersProExportDialog
        open={layersProDialogOpen}
        onOpenChange={setLayersProDialogOpen}
        onUpgrade={() => navigate("/plans")}
      />

      {/* Save the current look as a brand kit (creation is free-tier too). */}
      <SaveBrandKitDialog
        open={saveBrandKitOpen}
        onOpenChange={setSaveBrandKitOpen}
        subtitle={subtitleData}
        logo={logoData}
        fontId={selectedSubtitleFontId || null}
        postLayout={postLayout}
        fieldBinds={layoutFieldBinds}
        onCreated={(kitId) => {
          setSubtitleDriftsFromKit(false);
          toast({ title: t("projectEditor.saveBrandKit.created") });
          // Pro users start using the new kit immediately; free users keep it
          // saved because applying personal kits is PRO.
          if (hasProFeatures) handleBrandKitChange(kitId);
        }}
      />

      {/* Free user tried to apply a personal kit (public kits stay free). */}
      <ProFeatureDialog
        open={kitApplyProDialogOpen}
        onOpenChange={setKitApplyProDialogOpen}
        onUpgrade={() => navigate("/plans")}
        title={t("projectEditor.brandKitApplyProTitle")}
        body={t("projectEditor.brandKitApplyProBody")}
        dismissLabel={t("projectEditor.layers.proExportDialogDismiss")}
        upgradeLabel={t("projectEditor.layers.proExportDialogUpgrade")}
        testIdPrefix="kit-apply-pro"
      />

      {/* Free user tried to export a clip that has scene transitions. */}
      <ProFeatureDialog
        open={transitionsProDialogOpen}
        onOpenChange={setTransitionsProDialogOpen}
        onUpgrade={() => navigate("/plans")}
        title={t("projectEditor.transitionsProTitle")}
        body={t("projectEditor.transitionsProBody")}
        dismissLabel={t("projectEditor.layers.proExportDialogDismiss")}
        upgradeLabel={t("projectEditor.layers.proExportDialogUpgrade")}
        testIdPrefix="transitions-pro"
      />

      {/* Free user tried to export a clip that has timeline effect sections. */}
      <ProFeatureDialog
        open={effectsProDialogOpen}
        onOpenChange={setEffectsProDialogOpen}
        onUpgrade={() => navigate("/plans")}
        title={t("videoEffects.sections.proTitle")}
        body={t("videoEffects.sections.proBody")}
        dismissLabel={t("projectEditor.layers.proExportDialogDismiss")}
        upgradeLabel={t("projectEditor.layers.proExportDialogUpgrade")}
        testIdPrefix="effects-pro"
      />

      {/* Free user tried select-all / bulk export (PRO-only). */}
      <ProFeatureDialog
        open={bulkProDialogOpen}
        onOpenChange={setBulkProDialogOpen}
        onUpgrade={() => navigate("/plans")}
        title={t("projectEditor.bulkExportProTitle")}
        body={t("projectEditor.bulkExportProBody")}
        dismissLabel={t("projectEditor.layers.proExportDialogDismiss")}
        upgradeLabel={t("projectEditor.layers.proExportDialogUpgrade")}
        testIdPrefix="bulk-pro"
      />

      {/* Reframe Modal */}
      <ReframeModal
        open={reframeModalOpen}
        onOpenChange={setReframeModalOpen}
        keyframes={effectiveCropKeyframes}
        scenes={naturalTimeline.intervals}
        onChange={setCropKeyframesWithUndo}
        videoUrl={videoUrl}
        currentTime={currentTime}
      />

      <AlertDialog
        open={landscapeSingleConfirmOpen}
        onOpenChange={setLandscapeSingleConfirmOpen}
      >
        <AlertDialogContent data-testid="landscape-single-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("projectEditor.landscapeSwitch.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("projectEditor.landscapeSwitch.description", {
                layout: t("projectClips.speakerLayout.single"),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("projectEditor.landscapeSwitch.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="landscape-single-confirm-action"
              onClick={confirmLandscapeSingle}
            >
              {t("projectEditor.landscapeSwitch.confirm", {
                layout: t("projectClips.speakerLayout.single"),
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  // The timeline and the controls for what is selected in it, shared by the
  // editor and AI Studio. In the studio, undo goes through the studio's undo
  // (the assistant's pending batch first), like its bar's button.
  const editorTimeline = (
    <Timeline
      fullVideoDuration={project.duration_seconds || 0}
      selectedClip={timelineSelectedClip}
      intervalsLoaded={clipIntervalsLoaded}
      intervals={naturalTimeline.intervals}
      sequenceMode={isTimelineClipView}
      onVideoLockedChange={setIsTimelineClipView}
      currentTime={currentTime}
      onTimeChange={updateCurrentTime}
      onSeekRequest={handleSeekToTime}
      onIntervalsChange={setNaturalIntervalsWithUndo}
      transitions={naturalTimeline.transitions}
      onTransitionChange={handleTransitionChange}
      onApplyTransitionToAll={handleApplyTransitionToAll}
      onRandomizeTransitions={handleRandomizeTransitions}
      onAutoTransitions={handleAutoTransitions}
      autoTransitionsDisabledReason={autoTransitions.reason}
      autoTransitionsContentTypeLabel={autoTransitions.label}
      keyframeTransitions={naturalTimeline.keyframeTransitions}
      onKeyframeTransitionChange={handleKeyframeTransitionChange}
      onApplyKeyframeTransitionToAll={handleApplyKeyframeTransitionToAll}
      onRandomizeKeyframeTransitions={handleRandomizeKeyframeTransitions}
      isVideoLocked={isTimelineClipView}
      sceneMarkers={timelineSceneMarkers}
      cropCenterKeyframes={effectiveCropKeyframes}
      cropUncoveredScenes={previewAppliesCropKeyframes}
      onCropKeyframesChange={setCropKeyframesWithUndo}
      videoUrl={videoUrl}
      thumbnailVideoUrl={thumbnailVideoUrl}
      canUndo={variant === "studio" ? studioUndo.canUndo : canUndoTranscription}
      canRedo={canRedoTranscription}
      onUndo={variant === "studio" ? handleStudioUndo : handleUndoTranscription}
      onRedo={handleRedoTranscription}
      layers={layers}
      activeLayerId={activeLayerId}
      onLayerSelect={selectLayer}
      onLayerPatch={patchLayer}
      onLayerReorder={moveLayer}
      clipDuration={clipDuration}
      onAddLayer={handleAddLayer}
      selectedIntervalId={selectedIntervalId}
      onSelectInterval={selectInterval}
      effectSections={effectSections}
      activeEffectSectionId={activeEffectSectionId}
      onEffectSectionSelect={selectEffectSection}
      onEffectSectionCommit={(id, w) => patchEffectSection(id, w)}
      onAddEffect={
        // Same readiness gate as the whole-clip effects panel: an add
        // before this clip's data loads would land on the old clip.
        currentSaveableClipId && clipDataLoadedFor === currentSaveableClipId
          ? addEffectSection
          : undefined
      }
      suspendFrameExtraction={isPlaying || !previewReady}
      toolbarStart={
        // Keyed by clip: an in-flight capture/upload is dropped on
        // switch instead of landing on the newly selected clip.
        <CoverImageControl
          key={currentSaveableClipId ?? "no-clip"}
          cover={coverImage}
          onChange={handleCoverImageChange}
          videoUrl={coverCaptureVideoUrl}
          currentTime={currentTime}
          exportAspect={effectivePreviewAspect === "portrait" ? 9 / 16 : 16 / 9}
          resolveCropFraming={resolveCoverCropFraming}
          capturePreviewFrame={captureCoverPreviewFrame}
          disabled={!currentSaveableClipId || clipDataLoadedFor !== currentSaveableClipId}
          hasProAccess={hasProFeatures}
          clipId={currentSaveableClipId}
          clipTitle={selectedClipTitle || null}
        />
      }
      className={variant === "studio" ? undefined : "mt-4"}
    />
  );

  // The selected layer, else the selected timeline effect section.
  let selectionControls: ReactNode = null;
  if (activeLayer) {
    selectionControls = (
      <LayerExpansionRow
        layer={activeLayer}
        clipDuration={clipDuration ?? 0}
        onPatch={patchLayer}
        onDelete={deleteLayer}
        reframeActive={activeLayer.id === reframeLayerId}
        onReframeToggle={() => handleReframeToggle(activeLayer.id)}
      />
    );
  } else if (activeEffectSection) {
    selectionControls = (
      <EffectSectionControls
        section={activeEffectSection}
        onChange={(patch) => patchEffectSection(
          activeEffectSection.id,
          patch,
          effectControlUndoCategory(activeEffectSection.id, patch),
        )}
        onDelete={() => deleteEffectSection(activeEffectSection.id)}
      />
    );
  }

  if (variant === "studio") {
    return (
      <PaletteProvider colors={selectedKit?.data?.colors?.filter(Boolean) ?? []}>
        <AiStudioLayout
          aspect={effectivePreviewAspect}
          fullscreenRef={studioFullscreenRef}
          header={(
            <>
              {topBar}
              <AiStudioBar
                clips={clips.map((clip, index) => ({
                  id: String(clip.id),
                  title: clip.title || t("verticalClipCard.clipNumber", { number: index + 1 }),
                }))}
                selectedClipId={selectedClips[0] != null ? String(selectedClips[0]) : null}
                onSelectClip={(id) => setSelectedClips([Number(id)])}
                canUndo={studioUndo.canUndo}
                canRedo={canRedoTranscription}
                onUndo={handleStudioUndo}
                onRedo={handleRedoTranscription}
                fullEditorPath={`/${isLibrary ? "library" : "project"}/${project.id}/edit${currentSaveableClipId ? `?clip=${currentSaveableClipId}` : ""}`}
              />
            </>
          )}
          player={previewPlayer ?? (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          transport={(
            <AiStudioTransport
              isPlaying={isPlaying}
              isMuted={isMuted}
              currentTime={playbackNavigation.position}
              duration={playbackNavigation.duration}
              onTogglePlay={() => videoPlayerRef.current?.togglePlay()}
              onToggleMute={() => videoPlayerRef.current?.toggleMute()}
              onSeek={(time) => handleSeekToTime(playbackNavigation.sourceAt(time), { scrub: true })}
              onFullscreen={() => {
                // Toggle; a refused request (iOS, iframes) is ignored.
                const request = document.fullscreenElement
                  ? document.exitFullscreen?.()
                  : studioFullscreenRef.current?.requestFullscreen?.();
                request?.catch(() => {});
              }}
            />
          )}
          timeline={editorTimeline}
          selection={selectionControls}
        />
        {editorDialogs}
      </PaletteProvider>
    );
  }

  return (
    <PaletteProvider colors={selectedKit?.data?.colors?.filter(Boolean) ?? []}>
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      {topBar}

      {/* Clips Selection - Above video */}
      <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 border-b border-border tour-clips-selection">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h2 className="text-sm sm:text-base font-semibold">
              {t("projectEditor.clipsByViralization")}
            </h2>
            {clips.length >= 2 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (!hasProFeatures) {
                    setBulkProDialogOpen(true);
                    return;
                  }
                  if (checkedClips.length === clips.length) deselectAllClips();
                  else selectAllClips();
                }}
                className="text-xs h-7"
              >
                <CheckSquare className="w-3 h-3 mr-1" />
                {checkedClips.length === clips.length
                  ? t("projectEditor.deselectAll")
                  : t("projectEditor.selectAll")}
              </Button>
            )}
          </div>
        </div>

        {/* Warning for no clips */}
        {clips.length > 0 && clips[0].isFallback && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 text-amber-600 mt-0.5">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              </div>
              <div className="text-sm text-amber-800">
                <p className="font-medium">{t("projectEditor.noClipsFound")}</p>
                <p className="text-xs text-amber-700 mt-1">
                  {t("projectEditor.noClipsExplanation")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Carousel of clips */}
        <Swiper
          modules={[Navigation, Mousewheel, Keyboard]}
          navigation
          mousewheel={{ forceToAxis: true }}
          keyboard
          spaceBetween={2}
          slidesPerView={2}
          breakpoints={{
            640: { slidesPerView: 4, spaceBetween: 8 },
            1024: { slidesPerView: 6, spaceBetween: 10 },
            1280: { slidesPerView: 7, spaceBetween: 10 },
            1536: { slidesPerView: 8, spaceBetween: 10 },
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          className="clips-swiper"
        >
          {clips.map((clip) => (
            <SwiperSlide key={clip.id} className="!h-auto">
              <div
                className={`relative m-1.5 p-1.5 rounded-lg border-2 transition-all cursor-pointer hover:scale-[1.02] ${
                  selectedClips.includes(clip.id)
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => toggleClip(clip.id)}
              >
                {/* Checkbox for batch selection */}
                <div
                  className="absolute top-1 left-1 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCheckedClip(clip.id);
                  }}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${checkedClips.includes(clip.id) ? "bg-primary border-primary text-primary-foreground" : "bg-background/80 border-muted-foreground/40 hover:border-primary"}`}
                  >
                    {checkedClips.includes(clip.id) && (
                      <Check className="w-3 h-3" />
                    )}
                  </div>
                </div>
                {/* Per-clip rating buttons */}
                {(clip.skippedClipId || clip.activeClipId) && (
                  <div className="absolute top-1 right-1 z-10 flex gap-1">
                    <button
                      className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${clipRatings[clip.skippedClipId || clip.activeClipId || ""] === "like" ? "bg-green-500 text-white" : "bg-background/80 text-muted-foreground hover:text-green-500"}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClipRating(
                          clip.skippedClipId || clip.activeClipId || "",
                          "like",
                        );
                      }}
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${clipRatings[clip.skippedClipId || clip.activeClipId || ""] === "dislike" ? "bg-red-500 text-white" : "bg-background/80 text-muted-foreground hover:text-red-500"}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClipRating(
                          clip.skippedClipId || clip.activeClipId || "",
                          "dislike",
                        );
                      }}
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="w-full aspect-video rounded mb-1.5 overflow-hidden relative bg-black">
                  {clip.clip_thumbnail &&
                  typeof clip.clip_thumbnail === "string" &&
                  clip.clip_thumbnail.startsWith("https://") ? (
                    <img
                      src={clip.clip_thumbnail}
                      loading="lazy"
                      decoding="async"
                      alt={`Thumbnail for ${clip.title}`}
                      className="inset-0 w-full h-full object-contain rounded"
                    />
                  ) : videoUrl ? (
                    <VideoFrameAtTime
                      videoUrl={thumbnailVideoUrl}
                      extractFrame={extractClipThumbnail}
                      time={clip.time}
                      width={320}
                      height={180}
                      rounded
                      suspend={isPlaying || !previewReady}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-muted flex items-center justify-center">
                      <Play className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                    </div>
                  )}
                </div>
                {/* One compact meta row (number + modified + score) and a
                    single-line title. The two-line centered stack cost every
                    card ~50px; hover `title` keeps the full text reachable. */}
                <div>
                  <div className="mb-0.5 flex items-center justify-between gap-1">
                    <div className="flex min-w-0 items-center gap-1">
                      <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                        {t("projectEditor.clipNumber", { number: clip.id })}
                      </Badge>
                      {clip.dirty && (
                        <Badge
                          variant="outline"
                          className="px-1.5 py-0 text-[10px] border-yellow-500 text-yellow-600"
                        >
                          {t("projectEditor.modified")}
                        </Badge>
                      )}
                    </div>
                    {!clip.isFallback && (
                      <div className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
                        <div className="h-1 w-8 overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full rounded-full bg-gradient-primary"
                            style={{ width: `${clip.score}%` }}
                          />
                        </div>
                        <span className="font-medium">
                          {Math.round(clip.score)}
                        </span>
                      </div>
                    )}
                  </div>
                  <p
                    className="truncate text-[11px] font-medium"
                    title={
                      typeof clip.title === "string"
                        ? clip.title
                        : clip.title?.text || clip.title?.title || "Untitled"
                    }
                  >
                    {typeof clip.title === "string"
                      ? clip.title
                      : clip.title?.text || clip.title?.title || "Untitled"}
                  </p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Main Content */}
      <div className="flex flex-col xl:flex-row xl:items-start mx-auto" style={{ ["--top-bar-height" as any]: `${topBarHeight}px` }}>
        {/* Video Player */}
        <div
          className="flex-1 p-3 sm:p-4 lg:p-6 min-w-0 xl:sticky xl:self-start xl:flex xl:flex-col xl:top-[var(--top-bar-height)] xl:h-[calc(100vh-var(--top-bar-height))]"
        >
          <div className="space-y-4 lg:space-y-6 xl:flex-1 xl:min-h-0 xl:flex xl:flex-col xl:space-y-0">
            {/* Video Player */}
            <Card className="p-3 sm:p-4 lg:p-6 tour-video-player xl:flex-1 xl:min-h-0 xl:flex xl:flex-col">
              <input
                ref={layerFileInputRef}
                data-testid="layer-file-input"
                type="file"
                accept={layerTypeConfig.image.acceptMimeTypes.join(",")}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLayerFile(f, "image");
                  e.target.value = "";
                }}
              />
              <input
                ref={layerVideoInputRef}
                data-testid="layer-video-input"
                type="file"
                accept={layerTypeConfig.video.acceptMimeTypes.join(",")}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLayerFile(f, "video");
                  e.target.value = "";
                }}
              />
              {stockDialogKind && (
                <StockMediaBrowseDialog
                  open
                  onOpenChange={(o) => {
                    if (!o) setStockDialogKind(null);
                  }}
                  kind={stockDialogKind}
                  onPick={handleAddStockLayer}
                  onUploadInstead={() => {
                    const ref =
                      stockDialogKind === "image" ? layerFileInputRef : layerVideoInputRef;
                    setStockDialogKind(null);
                    ref.current?.click();
                  }}
                />
              )}
              <MyMediaPickerDialog
                open={imageLibraryOpen}
                onOpenChange={setImageLibraryOpen}
                onSelect={(item) => {
                  const stockItem = userMediaToStockItem(item);
                  if (stockItem) void handleAddStockLayer(stockItem);
                }}
                onBrowseStock={() => {
                  setImageLibraryOpen(false);
                  setStockDialogKind("image");
                }}
                onUploadInstead={() => {
                  setImageLibraryOpen(false);
                  layerFileInputRef.current?.click();
                }}
              />
              {/* 9:16 is previewable before auto-crop has run, but the framing
                  is provisional (centred) until the crop keyframes arrive. */}
              {autoCropPending && (
                <div
                  data-testid="auto-crop-pending-notice"
                  className="mx-auto mb-4 flex w-fit max-w-full items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-foreground/90"
                >
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
                  <span>{t("projectEditor.autoCropPending")}</span>
                </div>
              )}

              <div className="mb-4 flex justify-center items-center xl:flex-1 xl:min-h-[280px] xl:[container-type:size]">
              <div data-testid="editor-preview" className="aspect-video bg-gradient-secondary rounded-lg relative overflow-hidden w-full min-h-[220px] xl:w-auto xl:h-[min(100cqh,56.25cqw)] xl:min-h-[280px]">
                {/* Preview quality pill (top-left; the aspect pill owns the
                    top-right). Only when both tiers exist. Titles carry the
                    explanation; the switch toast reminds that exports always
                    render in full quality. */}
                {hasPreviewQualityChoice && (
                  <div className="absolute left-2 top-2 z-30 flex gap-0.5 rounded-lg bg-black/60 p-0.5 backdrop-blur-sm">
                    <Button
                      data-testid="preview-quality-compressed"
                      variant={previewQuality === "compressed" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      title={t("projectEditor.previewQuality.compressedTitle")}
                      onClick={() => setPreviewQuality("compressed")}
                    >
                      {t("projectEditor.previewQuality.compressed")}
                    </Button>
                    <Button
                      data-testid="preview-quality-full"
                      variant={previewQuality === "full" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      title={t("projectEditor.previewQuality.fullTitle")}
                      onClick={() => {
                        setPreviewQuality("full");
                        toast({
                          title: t("projectEditor.previewQuality.fullToastTitle"),
                          description: t("projectEditor.previewQuality.exportsAlwaysFull"),
                        });
                      }}
                    >
                      {t("projectEditor.previewQuality.full")}
                    </Button>
                  </div>
                )}
                {/* Aspect toggle, folded into the preview corner as a compact
                    pill so it costs the preview no vertical space. z-30 keeps
                    it above the Remotion player but below the layer canvas
                    handles (z-20 container puts handles at the same plane —
                    the pill sits top-right where handles rarely are). It is
                    NOT layer UI: clicking it deselects the active layer,
                    which is fine — switching aspect re-fits the canvas. */}
                {/* Aspect toggle, folded into the preview corner as a compact
                    pill so it costs the preview no vertical space. z-30 keeps
                    it above the Remotion player but below the layer canvas
                    handles (z-20 container puts handles at the same plane —
                    the pill sits top-right where handles rarely are). It is
                    NOT layer UI: clicking it deselects the active layer,
                    which is fine — switching aspect re-fits the canvas.
                    16:9 is always offered; portrait-only layouts warn that
                    Single will be used. */}
                <div className="absolute right-2 top-2 z-30 flex gap-0.5 rounded-lg bg-black/60 p-0.5 backdrop-blur-sm">
                  <Button
                    data-testid="preview-aspect-landscape"
                    variant={effectivePreviewAspect === "landscape" ? "default" : "ghost"}
                    size="sm"
                    onClick={requestLandscapePreview}
                    className="h-7 px-2 text-xs"
                    title="Formato Paisagem (16:9)"
                  >
                    <Monitor className="mr-1 h-3.5 w-3.5" />
                    16:9
                  </Button>
                  <Button
                    data-testid="preview-aspect-portrait"
                    variant={effectivePreviewAspect === "portrait" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => !requiredPostLayoutAspect && setPreviewAspectRatio("portrait")}
                    disabled={!!requiredPostLayoutAspect}
                    className="h-7 px-2 text-xs disabled:opacity-100"
                    title={requiredPostLayoutAspect
                      ? t("projectClips.postLayouts.fofoca.portraitOnly")
                      : "Formato Retrato (9:16)"}
                  >
                    {requiredPostLayoutAspect ? (
                      <Lock className="mr-1 h-3.5 w-3.5" />
                    ) : previewAspectRatio === "portrait" &&
                    effectiveCropKeyframes.length === 0 ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Smartphone className="mr-1 h-3.5 w-3.5" />
                    )}
                    9:16
                  </Button>
                </div>
                {(() => {
                  const selectedClipData = clips.find(
                    (clip) => clip.id === selectedClips[0],
                  );
                  // For horizontal videos, portrait mode requires crop center data from scene detection.
                  // If detection is not yet available, fall back to landscape rendering.
                  // (effectivePreviewAspect is the shared single source of truth — see its
                  // declaration above handleLayerFile — so insert-time layer fitting and this
                  // preview render always agree on the composition aspect.)
                  const effectiveAspectRatio = effectivePreviewAspect;
                  if (selectedClipData && videoUrl) {
                    return (
                      <AddLayerContextMenu onAdd={handleAddLayer}>
                        <LayerDropzone onLayerFile={handleLayerFile}>
                        {previewPlayer}
                        <CompositionFitOverlay
                          compAspect={
                            effectiveAspectRatio === "portrait"
                              ? 1080 / 1920
                              : 1920 / 1080
                          }
                        >
                          <LayerCanvasEditor
                            layers={layers}
                            activeLayerId={activeLayerId}
                            currentClipTime={currentClipTime}
                            onSelect={selectLayer}
                            onChange={(id, rect, rotation) =>
                              patchLayer(id, { rect, rotation })
                            }
                            reframeLayerId={reframeLayerId}
                            onFrameChange={(id, frame: LayerFrame) =>
                              patchLayer(id, { frame })
                            }
                          />
                          <PreviewChromeEditors
                            subtitlesEnabled={subtitlesEnabled}
                            subtitlePosition={subtitleData.position}
                            subtitleMarginVertical={subtitleData.marginVertical}
                            onSubtitleChange={(next) => {
                              setSubtitleData((prev) => ({
                                ...prev,
                                position: next.position,
                                marginVertical: next.marginVertical,
                              }));
                            }}
                            postLayout={postLayout}
                            layoutLocked={!!effectivePostLayout}
                            isLandscape={effectiveAspectRatio === "landscape"}
                            watermark={showDefaultWatermark ? {
                              position: watermarkPosition,
                              frameAspect: effectiveAspectRatio === "portrait" ? 1080 / 1920 : 1920 / 1080,
                              showPlaceholder: currentClipTime < WATERMARK_DELAY_SECONDS,
                              onChange: setWatermarkPosition,
                              onReset: resetWatermarkPosition,
                            } : null}
                            onPostLayoutPatch={(patch) => {
                              setPostLayout((prev) => (
                                prev ? { ...prev, ...patch } as typeof prev : prev
                              ));
                              setSubtitleDriftsFromKit(true);
                            }}
                          />
                        </CompositionFitOverlay>

                        </LayerDropzone>
                      </AddLayerContextMenu>
                    );
                  }
                  return (
                    <div className="absolute inset-0 bg-muted flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Play className="w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-primary" />
                        </div>
                        <p className="text-xs sm:text-sm lg:text-base text-muted-foreground">
                          Video Preview
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
              </div>

              {/* Contextual control bar for the selected layer (or timeline
                  effect section), rendered BELOW the preview rather than
                  floating over it, so it never obstructs the frame being
                  edited. It is a sibling of the
                  preview container (not inside CompositionFitOverlay) so it can
                  use the full width — a portrait composition is far too narrow
                  to hold it — and it wraps to two rows there. */}
              {selectionControls && (
                <div className="mb-4 flex justify-center px-3">
                  {/* No overflow-* here: an overflow ancestor becomes a clipping
                      boundary for the Style popover's collision detection, which
                      placed it off-screen. The bar wraps (flex-wrap) instead of
                      scrolling horizontally. */}
                  <div className="max-w-full">{selectionControls}</div>
                </div>
              )}

              {/* Video Controls */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center justify-between gap-2 sm:gap-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="w-8 h-8 sm:w-10 sm:h-10"
                      data-testid="editor-toggle-play"
                      aria-pressed={isPlaying}
                      onClick={() => {
                        videoPlayerRef.current?.togglePlay();
                      }}
                    >
                      {isPlaying ? (
                        <Pause className="w-3 h-3 sm:w-4 sm:h-4" />
                      ) : (
                        <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                      )}
                    </Button>

                    <Button
                      variant="outline"
                      size="icon"
                      className="w-8 h-8 sm:w-10 sm:h-10"
                      onClick={() => {
                        videoPlayerRef.current?.toggleMute();
                      }}
                    >
                      {isMuted ? (
                        <VolumeX className="w-3 h-3 sm:w-4 sm:h-4" />
                      ) : (
                        <Volume2 className="w-3 sm:w-4 sm:h-4" />
                      )}
                    </Button>

                    <div className="flex items-center gap-2 w-[110px] sm:w-[140px]">
                      <Slider
                        value={[volume * 100]}
                        onValueChange={(value) => {
                          const newVolume = value[0] / 100;
                          setVolume(newVolume);
                          videoPlayerRef.current?.setVolume(newVolume);
                        }}
                        max={100}
                        min={0}
                        step={1}
                        className="w-full"
                      />
                      <span className="text-xs text-muted-foreground min-w-[2.25rem] text-right">
                        {Math.round(volume * 100)}%
                      </span>
                    </div>

                    <Button
                      variant="outline"
                      size="icon"
                      className="w-8 h-8 sm:w-10 sm:h-10"
                      data-testid="editor-playback-lock"
                      onClick={() => setIsVideoLocked(locked => !locked)}
                      aria-pressed={isVideoLocked}
                      title={
                        isVideoLocked
                          ? t("projectEditor.unlockVideoNavigation")
                          : t("projectEditor.lockVideoNavigation")
                      }
                    >
                      {isVideoLocked ? (
                        <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                      ) : (
                        <Unlock className="w-3 h-3 sm:w-4 sm:h-4" />
                      )}
                    </Button>

                    <OriginalVideoDownloadButton
                      sourceUrl={downloadVideoUrl}
                      hasProAccess={hasProFeatures}
                      sourceDuration={project.duration_seconds}
                      clipDuration={clipDuration}
                    />

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button
                            variant="outline"
                            size="icon"
                            className="w-8 h-8 sm:w-10 sm:h-10"
                            onClick={handleDownloadSRT}
                            disabled={!words?.length}
                          >
                            <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent align="center" side="top">
                        {t("projectClips.downloadSRT")}
                      </TooltipContent>
                    </Tooltip>

                    <div className="group relative">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Button
                              variant="outline"
                              size="icon"
                              className="w-8 h-8 sm:w-10 sm:h-10"
                              onClick={() => setReframeModalOpen(true)}
                              disabled={effectiveCropKeyframes.length === 0}
                            >
                              <Crop className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent align="center" side="top">
                          {t("reframeModal.trigger")}
                        </TooltipContent>
                      </Tooltip>
                      {effectiveCropKeyframes.length > 0 && (
                        <SceneZoomControl
                          className="absolute left-1/2 top-full z-20 mt-1 flex -translate-x-1/2 items-center gap-1 rounded-md border bg-popover px-2 py-1.5 shadow-md opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto"
                          keyframes={effectiveCropKeyframes}
                          scenes={naturalTimeline.intervals}
                          currentTime={currentTime}
                          onChange={setCropKeyframesWithUndo}
                        />
                      )}
                    </div>

                    <span data-testid="editor-source-time" data-source-time={currentTime} className="text-xs sm:text-sm text-muted-foreground">
                      {(() => {
                        return `${formatTime(currentTime)} / ${formatTime(project.duration_seconds || 0)}`;
                      })()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="w-8 h-8 sm:w-10 sm:h-10 relative"
                      onClick={skipBackward}
                      disabled={!canSkipBackward}
                      title={t("projectEditor.skipBackward", {
                        seconds: SKIP_AMOUNT_SECONDS,
                      })}
                    >
                      <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="absolute -bottom-1 -right-1 text-[8px] sm:text-[10px] font-bold bg-primary text-primary-foreground rounded-full px-1 min-w-[12px] h-3 sm:h-4 flex items-center justify-center">
                        {SKIP_AMOUNT_SECONDS}
                      </span>
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="w-8 h-8 sm:w-10 sm:h-10 relative"
                      onClick={skipForward}
                      disabled={!canSkipForward}
                      title={t("projectEditor.skipForward", {
                        seconds: SKIP_AMOUNT_SECONDS,
                      })}
                    >
                      <RotateCw className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="absolute -bottom-1 -right-1 text-[8px] sm:text-[10px] font-bold bg-primary text-primary-foreground rounded-full px-1 min-w-[12px] h-3 sm:h-4 flex items-center justify-center">
                        {SKIP_AMOUNT_SECONDS}
                      </span>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Timeline - Always visible */}
              {editorTimeline}
            </Card>
          </div>
        </div>

        {/* Right Panel - Controls */}
        <div className="w-full xl:w-80 p-3 sm:p-4 lg:p-6 xl:border-l border-border space-y-4 lg:space-y-6 sm:min-w-[35vw] xl:sticky xl:top-[var(--top-bar-height)] xl:self-start xl:h-[calc(100vh-var(--top-bar-height))] xl:overflow-y-auto">
          <StyleSidePanel
            uniqueSpeakerCount={uniqueSpeakers.length}
            selectedSpeakerLayout={selectedSpeakerLayout}
            onSelectSpeakerLayout={(num) => { setPostLayout(null); setLayoutFieldBinds({}); persistSpeakerLayout(num); }}
            postLayout={postLayout}
            onSelectPostLayout={selectPostLayout}
            speakerOptions={[1]}
            layoutFields={postLayout
              ? resolveFields(POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS], postLayout)
              : []}
            layoutConfig={postLayout}
            onLayoutFieldChange={updatePostLayoutField}
            videoTargetDurationSec={clipDuration}
            videoFrameSource={videoUrl ? { videoUrl, time: currentTime, captureLabel: 'playhead' } : undefined}
            disabledKeys={effectivePostLayout ? enforcedLockedKeys(effectivePostLayout) : undefined}
            fieldBinds={layoutFieldBinds}
            onFieldBindChange={(key, source) => {
              if (!postLayout) return;
              setLayoutFieldBinds((prev) => {
                const next = { ...prev };
                if (source) next[key] = source;
                else delete next[key];
                return sanitizeLayoutFieldBinds(postLayout.kind, next);
              });
              setSubtitleDriftsFromKit(true);
            }}
            bindContext={{
              clipTitle: resolveClipTitleForBind(selectedClipData?.title),
              channelName: channelSource?.name,
              channelAvatarUrl: channelSource?.thumbnail,
              userName: myData?.myData?.name ?? undefined,
              userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
              hasChannel: !!channelSource,
            }}
            enforcedNotice={effectivePreviewAspect === "portrait" && effectivePostLayout ? (
              <div
                data-testid="enforced-layout-notice"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground"
              >
                <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  {t('projectClips.enforcedLayout.notice')}
                  {postLayout && POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS] && (
                    <> &mdash; <span className="font-medium text-foreground">{t(POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS].labelKey)}</span></>
                  )}
                </span>
              </div>
            ) : undefined}
            palette={null}
            showLayoutControls
            showSubtitleEditor={subtitlesEnabled && (!!selectedBrandKit || !hasProFeatures)}
            subtitleData={subtitleData}
            onSubtitleChange={updateSubtitle}
            hidePathControls={postLayout?.kind === 'circulo'}
            logoData={logoData}
            onLogoChange={updateLogo}
            selectedSubtitleFontId={selectedSubtitleFontId}
            onFontSelect={(fontId, font) => {
              setSelectedSubtitleFontId(fontId);
              updateSubtitle("fontFamily", font.family);
              loadFont(fontId);
            }}
            onLogoUpload={handleLogoUpload}
            isUploadingLogo={isUploadingLogo}
            beforeLayout={(
          <Card className="p-3" data-testid="editor-templates-card">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Palette className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">
                  {t("projectEditor.templatesSection")}
                </span>
              </div>

              <EditorTemplatesCarousel
                kits={brandKitsData?.myBrandKits || []}
                selectedId={selectedBrandKit}
                onSelect={(id) => handleBrandKitChange(id)}
                disabled={updateProjectLoading}
              />
              <div className="flex items-center justify-between gap-2">
                    {subtitleDriftsFromKit && selectedBrandKit ? (
                      <p className="text-xs text-muted-foreground">
                        {t("projectEditor.saveBrandKit.driftHint")}
                      </p>
                    ) : <span />}
                    <Button
                      data-testid="save-brand-kit-button"
                      size="sm"
                      variant={subtitleDriftsFromKit ? "gradient" : "secondary"}
                      className="shrink-0"
                      onClick={() => setSaveBrandKitOpen(true)}
                    >
                      <Palette className="mr-1 h-3.5 w-3.5" />
                      {t("projectEditor.saveBrandKit.cta")}
                    </Button>
                  </div>

                {!hasProFeatures && (
                  <Card className="p-3 sm:p-4 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Palette className="w-4 h-4 text-primary" />
                        <p className="text-sm text-foreground">
                          {t("projectEditor.createCustomBrandKits")}
                        </p>
                      </div>
                      <Button
                        variant="gradient"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate("/plans")}
                      >
                        {t("projectEditor.upgradeToPro")}
                      </Button>
                    </div>
                  </Card>
                )}
                {updateProjectLoading && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="h-3 w-3 animate-spin rounded-full border border-primary border-t-transparent" />
                    {t("projectEditor.updatingProject")}
                  </div>
                )}
            </div>
          </Card>
            )}
            afterLayout={(subtitle) => (
          <Card className="p-3 sm:p-4 tour-subtitle-controls" data-testid="editor-subtitles-card">
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary" />
                  <span className="text-base font-semibold">
                    {t("projectEditor.subtitlesSection")}
                  </span>
                </div>
                <div
                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all cursor-pointer ${
                    subtitlesEnabled
                      ? "bg-primary/10 border-primary/30"
                      : "bg-muted/30 border-border hover:border-primary/20"
                  }`}
                  onClick={() => setSubtitlesEnabled(!subtitlesEnabled)}
                >
                  <Label
                    htmlFor="subtitles"
                    className="flex flex-col gap-0.5 cursor-pointer pointer-events-none"
                  >
                    <span className="text-sm font-semibold">
                      {t("projectEditor.subtitlesToggleLabel")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {subtitlesEnabled && selectedBrandKit
                        ? t("projectEditor.subtitleControlledByBrandKit")
                        : subtitlesEnabled
                          ? t("projectEditor.selectBrandKitForStyles")
                          : selectedBrandKit
                            ? t("projectEditor.enableSubtitlesToApply")
                            : t("projectEditor.subtitlesOffHint")}
                    </span>
                  </Label>
                  <Switch
                    id="subtitles"
                    checked={subtitlesEnabled}
                    onCheckedChange={setSubtitlesEnabled}
                    className="data-[state=checked]:bg-primary scale-125 pointer-events-none shrink-0"
                  />
                </div>
              </div>

              {subtitle}
            </div>
          </Card>
            )}
          />

          {/* Transcript */}
          <Card className="p-3 sm:p-4 tour-transcription relative">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-semibold text-sm sm:text-base">
                  {t("projectEditor.clipTranscription")}
                </h3>
                {(isSavingTranscription || isUploadingTranscription) && (
                  <span
                    className="flex items-center gap-1 text-xs text-muted-foreground"
                    aria-live="polite"
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("projectEditor.saving")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleUndoTranscription}
                  disabled={
                    !canUndoTranscription ||
                    isSavingTranscription ||
                    isUploadingTranscription
                  }
                  className="h-7 w-7 p-0"
                  title={t("projectEditor.undoTranscriptionEdit")}
                  aria-label={t("projectEditor.undoTranscriptionEdit")}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleRedoTranscription}
                  disabled={
                    !canRedoTranscription ||
                    isSavingTranscription ||
                    isUploadingTranscription
                  }
                  className="h-7 w-7 p-0"
                  title={t("projectEditor.redoTranscriptionEdit")}
                  aria-label={t("projectEditor.redoTranscriptionEdit")}
                >
                  <Redo2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleRevertToOriginal}
                  disabled={
                    !firstTranscriptionRef.current ||
                    isSavingTranscription ||
                    isUploadingTranscription
                  }
                  className="h-7 px-2 text-xs"
                  title={t("projectEditor.revertToOriginalTranscription")}
                >
                  <History className="h-3.5 w-3.5 mr-1" />
                  {t("projectEditor.revertToOriginalTranscription")}
                </Button>
                {fillerDetectionSupported && (
                  <Popover
                    open={fillerPopoverOpen}
                    onOpenChange={(open) => {
                      setFillerPopoverOpen(open);
                      if (open) handleDetectFillerWords();
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={
                          isSavingTranscription || isUploadingTranscription
                        }
                        className="h-7 px-2 text-xs"
                        title={t("projectEditor.detectFillerWords")}
                      >
                        <Wand2 className="h-3.5 w-3.5 mr-1" />
                        {t("projectEditor.detectFillerWords")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="end" side="top">
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-sm font-semibold">
                            {t("projectEditor.detectFillerWords")}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {fillerMatchCount > 0
                              ? t("projectEditor.fillerWordsFound", {
                                  count: fillerMatchCount,
                                })
                              : t("projectEditor.noFillerWordsFound")}
                          </p>
                        </div>
                        {fillerMatchCount > 0 && (
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                handleDeleteSelection();
                                setFillerPopoverOpen(false);
                              }}
                              disabled={
                                isSavingTranscription ||
                                isUploadingTranscription
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              {t("projectEditor.deleteSubtitleOnly", {
                                count: fillerMatchCount,
                              })}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                handleDeleteSelectionFromVideo();
                                setFillerPopoverOpen(false);
                              }}
                              disabled={
                                isSavingTranscription ||
                                isUploadingTranscription
                              }
                            >
                              <Scissors className="h-3.5 w-3.5 mr-1" />
                              {t("projectEditor.deleteFromVideo", {
                                count: fillerMatchCount,
                              })}
                            </Button>
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                <Popover
                  open={decoupPopoverOpen}
                  onOpenChange={setDecoupPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={
                        isSavingTranscription || isUploadingTranscription
                      }
                      className="h-7 px-2 text-xs"
                      title={t("projectEditor.decoupClipHint")}
                    >
                      <Zap className="h-3.5 w-3.5 mr-1" />
                      {t("projectEditor.decoupClip")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72" align="end" side="top">
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-semibold">
                          {t("projectEditor.decoupClip")}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {t("projectEditor.decoupClipHint")}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="decoup-silence-gap"
                          className="text-xs"
                        >
                          {t("projectEditor.decoupSilenceGapLabel")}
                        </Label>
                        <Input
                          id="decoup-silence-gap"
                          type="number"
                          inputMode="decimal"
                          step="0.1"
                          min="0"
                          value={decoupSilenceGap}
                          onChange={(e) =>
                            setDecoupSilenceGap(e.target.value)
                          }
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="decoup-padding"
                          className="text-xs"
                        >
                          {t("projectEditor.decoupPaddingLabel")}
                        </Label>
                        <Input
                          id="decoup-padding"
                          type="number"
                          inputMode="decimal"
                          step="0.05"
                          min="0"
                          value={decoupPadding}
                          onChange={(e) => setDecoupPadding(e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          const gap = Math.max(
                            0,
                            parseFloat(decoupSilenceGap) || 0,
                          );
                          const pad = Math.max(
                            0,
                            parseFloat(decoupPadding) || 0,
                          );
                          handleDecoupClip(gap, pad);
                          setDecoupPopoverOpen(false);
                        }}
                      >
                        <Zap className="h-3.5 w-3.5 mr-1" />
                        {t("projectEditor.decoupClipApply")}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="bg-primary text-primary-foreground px-2 py-1 rounded-md text-xs font-medium shadow-lg">
                  {formatTime(currentTime)}
                </div>
              </div>
            </div>
            <ContextMenu>
              <ContextMenuTrigger className="block">
                <div
                  ref={transcriptionRef}
                  className="text-xs sm:text-sm text-muted-foreground leading-relaxed space-y-2 select-none text-left"
                >
              {(() => {
                const selectedClipData = clips.find(
                  (clip) => clip.id === selectedClips[0],
                );

                if (!selectedClipData) {
                  return (
                    <p className="text-xs text-muted-foreground">
                      {t("projectEditor.selectClipForTranscription")}
                    </p>
                  );
                }

                const isEmpty = segmentGroups.every((segments) =>
                  segments.every((segment) => segment.words.length === 0),
                );
                if (isEmpty) {
                  return (
                    <p className="text-xs text-muted-foreground">
                      {t("projectEditor.noTranscription")}
                    </p>
                  );
                }

                return (
                  <>
                    {segmentGroups.map((segments) => {
                      return segments.map((segment, j) => {
                        const isActive = isSegmentActive(segment);
                        const isEditingDisabled =
                          isSavingTranscription || isUploadingTranscription;
                        return (
                          <div
                            key={j}
                            data-segment-time={segment.startTime}
                            className={`flex gap-2 sm:gap-3 p-2 rounded-lg transition-all duration-200 cursor-pointer group/segment ${
                              isActive
                                ? "bg-primary/10 border border-primary/30 shadow-sm"
                                : "hover:bg-muted/50"
                            }`}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSeekToTime(segment.startTime + 0.1);
                            }}
                          >
                            <div className="flex flex-col items-start gap-1 min-w-[40px] sm:min-w-[50px]">
                              <span
                                className={`font-medium text-md transition-colors duration-100 ${
                                  isActive ? "text-primary" : "text-primary/70"
                                }`}
                              >
                                {formatTime(segment.startTime)}
                              </span>
                              {isTranscriptionDirty && (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (isEditingDisabled) return;
                                    handleRevertSegment(segment.words);
                                  }}
                                  disabled={isEditingDisabled}
                                  title={t(
                                    "projectEditor.revertSegmentToOriginal",
                                  )}
                                  aria-label={t(
                                    "projectEditor.revertSegmentToOriginal",
                                  )}
                                  className="opacity-0 group-hover/segment:opacity-100 transition-opacity inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 disabled:opacity-0"
                                >
                                  <RotateCcw className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            <p className="text-sm leading-snug">
                              {segment.words.map((word, wi) => {
                                const editId: string | undefined = word._editId;
                                const isCurrentWordActive = isWordActive(word);
                                const isEditingThisWord =
                                  !!editId && editingWordId === editId;
                                const isWordSelected =
                                  !!editId && selectedWordIds.has(editId);

                                const showBeforeButton =
                                  hoveredWordId === editId;
                                const showAfterButton =
                                  hoveredWordId === editId;

                                const insertButton = (
                                  direction: "before" | "after",
                                ) => {
                                  if (!editId) return null;
                                  const label =
                                    direction === "before"
                                      ? t("projectEditor.insertWordBefore")
                                      : t("projectEditor.insertWordAfter");
                                  const visible =
                                    direction === "before"
                                      ? showBeforeButton
                                      : showAfterButton;
                                  const buttonStyle: React.CSSProperties =
                                    direction === "before"
                                      ? {
                                          top: "50%",
                                          left: "-16px",
                                          transform: "translateY(-50%)",
                                        }
                                      : {
                                          top: "50%",
                                          right: "-16px",
                                          transform: "translateY(-50%)",
                                        };
                                  return (
                                    <button
                                      type="button"
                                      style={buttonStyle}
                                      data-direction={direction}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (isEditingDisabled) return;
                                        if (direction === "before") {
                                          handleInsertWordBefore(editId);
                                        } else {
                                          handleInsertWordAfter(editId);
                                        }
                                      }}
                                      onMouseEnter={() =>
                                        setHoveredWordId(editId)
                                      }
                                      onMouseLeave={() =>
                                        setHoveredWordId(null)
                                      }
                                      aria-label={label}
                                      title={label}
                                      tabIndex={-1}
                                      className={`absolute z-20 inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary shadow-md ring-2 ring-background transition-opacity duration-100 ${visible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
                                    >
                                      <Plus
                                        className="h-3 w-3 text-primary-foreground"
                                        strokeWidth={3}
                                      />
                                    </button>
                                  );
                                };

                                if (isEditingThisWord && editId) {
                                  const initialText = word.word ?? "";
                                  return (
                                    <span
                                      key={`editing-${editId}`}
                                      className="relative inline-block whitespace-nowrap align-middle mr-[0.25em] last:mr-0"
                                    >
                                      {insertButton("before")}
                                      <input
                                        type="text"
                                        autoFocus
                                        defaultValue={initialText}
                                        onMouseEnter={() =>
                                          setHoveredWordId(editId)
                                        }
                                        onMouseLeave={() =>
                                          setHoveredWordId(null)
                                        }
                                        onFocus={(event) => {
                                          const input = event.currentTarget;
                                          if (
                                            nextFocusCursorRef.current ===
                                            "start"
                                          ) {
                                            input.setSelectionRange(0, 0);
                                          } else if (
                                            nextFocusCursorRef.current === "end"
                                          ) {
                                            const len = input.value.length;
                                            input.setSelectionRange(len, len);
                                          } else {
                                            input.select();
                                          }
                                          nextFocusCursorRef.current = null;
                                        }}
                                        onChange={(event) => {
                                          pendingWordEditRef.current = {
                                            editId,
                                            value: event.target.value,
                                          };
                                          const target =
                                            event.target as HTMLInputElement;
                                          target.style.width = `${Math.max(target.value.length + 1, 3)}ch`;
                                        }}
                                        onBlur={handleCommitEditWord}
                                        onKeyDown={(event) => {
                                          const target =
                                            event.target as HTMLInputElement;
                                          if (event.key === "Enter") {
                                            event.preventDefault();
                                            handleCommitEditWord();
                                          } else if (event.key === "Escape") {
                                            event.preventDefault();
                                            handleCancelEditWord();
                                          } else if (event.key === "Tab") {
                                            event.preventDefault();
                                            handleNavigateToSibling(
                                              editId,
                                              event.shiftKey ? -1 : 1,
                                            );
                                          } else if (
                                            event.key === "ArrowLeft" &&
                                            target.selectionStart === 0 &&
                                            target.selectionEnd === 0
                                          ) {
                                            event.preventDefault();
                                            handleNavigateToSibling(editId, -1);
                                          } else if (
                                            event.key === "ArrowRight" &&
                                            target.selectionStart ===
                                              target.value.length &&
                                            target.selectionEnd ===
                                              target.value.length
                                          ) {
                                            event.preventDefault();
                                            handleNavigateToSibling(editId, 1);
                                          }
                                        }}
                                        onClick={(event) =>
                                          event.stopPropagation()
                                        }
                                        disabled={isEditingDisabled}
                                        className="mx-0 px-1 py-0 rounded border border-primary bg-background text-foreground text-sm leading-snug align-middle focus:outline-none focus:ring-1 focus:ring-primary"
                                        style={{
                                          width: `${Math.max(initialText.length + 1, 3)}ch`,
                                        }}
                                      />
                                      {insertButton("after")}
                                    </span>
                                  );
                                }

                                return (
                                  <span
                                    key={
                                      editId ??
                                      `${word.start}-${word.end}-${wi}`
                                    }
                                    className="relative inline-block whitespace-nowrap align-middle mr-[0.25em] last:mr-0"
                                  >
                                    {insertButton("before")}
                                    <span
                                      onMouseDown={(event) => {
                                        if (editId)
                                          handleWordMouseDown(event, editId);
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (wasDraggingRef.current) {
                                          wasDraggingRef.current = false;
                                          return;
                                        }
                                        if (event.shiftKey) {
                                          if (editId)
                                            handleWordShiftClick(editId);
                                          return;
                                        }
                                        if (selectedWordIds.size > 0) {
                                          handleClearSelection();
                                        }
                                        if (typeof word.start === "number") {
                                          handleSeekToTime(word.start + 0.05);
                                        }
                                        if (editId) handleStartEditWord(editId);
                                      }}
                                      onContextMenu={() => {
                                        if (editId)
                                          handleWordContextMenu(editId);
                                      }}
                                      onMouseEnter={() => {
                                        if (editId)
                                          handleWordMouseEnter(editId);
                                      }}
                                      onMouseLeave={() =>
                                        setHoveredWordId(null)
                                      }
                                      className={`inline-block align-middle px-[2px] rounded cursor-text transition-[background-color,color] duration-150 ${
                                        isWordSelected
                                          ? "text-primary-foreground bg-primary"
                                          : isCurrentWordActive
                                            ? "text-primary bg-primary/20 shadow-sm"
                                            : "text-foreground hover:bg-muted"
                                      }`}
                                      style={{
                                        opacity:
                                          isWordSelected || isCurrentWordActive
                                            ? 1
                                            : word.gradientOpacity || 1,
                                      }}
                                      title={t("projectEditor.clickToEdit")}
                                    >
                                      {word.word || "\u00A0\u00A0"}
                                    </span>
                                    {insertButton("after")}
                                  </span>
                                );
                              })}
                            </p>
                          </div>
                        );
                      });
                    })}
                  </>
                );
              })()}
                </div>
              </ContextMenuTrigger>
              <ContextMenuContent onCloseAutoFocus={(event) => {
                const insertion = transcriptionMenuInsertionRef.current;
                if (!insertion) return;
                transcriptionMenuInsertionRef.current = null;
                // The newly inserted word owns focus; restoring the menu
                // trigger would blur and commit its still-empty input.
                event.preventDefault();
                clearAllSelections();
                if (insertion.direction === "before") handleInsertWordBefore(insertion.editId);
                else handleInsertWordAfter(insertion.editId);
              }}>
                <ContextMenuItem
                  onSelect={() => handleInsertAtWordSelection("before")}
                  disabled={selectedWordIds.size === 0 || isSavingTranscription || isUploadingTranscription}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("projectEditor.insertWordBefore")}
                </ContextMenuItem>
                <ContextMenuItem
                  onSelect={() => handleInsertAtWordSelection("after")}
                  disabled={selectedWordIds.size === 0 || isSavingTranscription || isUploadingTranscription}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("projectEditor.insertWordAfter")}
                </ContextMenuItem>
                <ContextMenuItem
                  onSelect={handleDeleteSelection}
                  disabled={
                    selectedWordIds.size === 0 ||
                    isSavingTranscription ||
                    isUploadingTranscription
                  }
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("projectEditor.deleteSubtitleOnly", {
                    count: selectedWordIds.size,
                  })}
                </ContextMenuItem>
                <ContextMenuItem
                  onSelect={handleDeleteSelectionFromVideo}
                  disabled={
                    selectedWordIds.size === 0 ||
                    isSavingTranscription ||
                    isUploadingTranscription
                  }
                >
                  <Scissors className="mr-2 h-4 w-4" />
                  {t("projectEditor.deleteFromVideo", {
                    count: selectedWordIds.size,
                  })}
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          </Card>
          <VideoEffectsPanel
            key={currentSaveableClipId ?? 'none'}
            value={videoEffects}
            onChange={handleVideoEffectsChange}
            onPreview={setEffectsPreview}
            disabled={!currentSaveableClipId || clipDataLoadedFor !== currentSaveableClipId}
            saveError={videoEffectsSaveError}
            thumbnailFrame={effectsThumbnailFrame}
            onRetry={() => { void flushVideoEffects().catch(() => {}); }}
          />
        </div>
      </div>

      {editorDialogs}
    </div>
    </PaletteProvider>
  );
};

export default ProjectEditor;
